/**
 * GlobalChatSystem Component
 * 
 * A comprehensive, reusable chat system component that provides:
 * - Full chat interface with message history
 * - Prompt bar with rich text editing
 * - File attachments with drag-and-drop
 * - Dock right functionality
 * - Mobile responsive design
 * - Construction waitlist integration
 * - LocalStorage persistence
 * 
 * This component can be used on any page with configurable behavior.
 */

import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  ChatContainer,
  ChatHeader,
  HeaderTitleGroup,
  HeaderTitleColumn,
  HeaderActions,
  ChatHeaderLogoImage,
  ChatTitle,
  HeaderSubtitle,
  HeaderSubtitleRow,
  HeaderSummonPill,
  ChatMessages,
  TranscriptContent,
  ArchiveToastPill,
  ChatFooter,
  MessageContainer,
  Message,
  MessageTime,
  ProcessingTime,
  PromptBarContainer,
  PromptInputWrapper,
  PromptInput,
  ActionButton,
  MentionTabChip,
  MentionTabKeycap,
  SendButton,
  HeaderIconButton,
  DragOverlay,
  fadeInCentered,
  ScrollToBottomButton,
  FloatingButtonBar,
  FloatingPillButton,
  ThreadNavBubble,
  FloatingNavPair,
  ThreadNavCluster,
  StepPositionBadge,
  AnimatedRevealContainer,
  RevealDivider,
  LoadEarlierMessagesButton,
  ThreadSidebarSkeleton,
  SidebarSkeletonLabel,
  SidebarSkeletonRow,
  TranscriptSkeleton,
  TranscriptSkeletonBubble,
  TranscriptSkeletonNotice,
  TranscriptRetryRow,
  TranscriptRetryButton,
  STREAMING_PULSE_CSS,
  PROCESSING_TOOLTIP_CSS,
  PendingWidgetGlobalStyles,
  PendingOverlayContainer,
  PendingOverlayRow,
  ThreadSwitcherContainer,
  ThreadSwitcherActionRail,
  ThreadSheetTrigger,
  ThreadChipButton,
  ThreadChipLabel,
  ThreadNewButton,
  MinimizedThreadLabel,
  CopyIconButton,
  TokenInfoButton,
  TokenInfoPopup,
  TokenInfoHeader,
  TokenInfoCostSection,
  TokenInfoCostTotal,
  TokenInfoCostTotalLabel,
  TokenInfoCostTotalValue,
} from './styles';
import {
  AttachmentSvgIcon,
  SendSvgIcon,
  HideSvgIcon,
  ExpandSvgIcon,
  ArrowRightSvgIcon,
  ArrowDownSvgIcon,
  ChevronDownSvgIcon,
  ChevronUpSvgIcon,
  ProcessingTooltip,
  TrashSvgIcon,
  AddPeopleSvgIcon,
  MentionOffSvgIcon,
  MentionSvgIcon,
} from './components';
import { ConfirmationModal } from './ConfirmationModal';
import { ThreadListSheet } from './ThreadListSheet';
import { AttachmentsSection } from './ChatAttachment';
import { SpecialMessageTile, getMessageTypeFromSourceTag } from './SpecialMessageTile';
import { isWorkflowCtaLive, isWorkflowCtaExemptId, CTA_RETIRED_ATTR } from './workflowCtaLifecycle';
import { TestModeBanner } from './TestModeBanner';
import { CriticalBalancePopup } from './CriticalBalancePopup';
import SpendChip from '../common/SpendChip';
import { usePaymentMethodStatus } from './usePaymentMethodStatus';
import { useMaybeTopUp } from '../../context/TopUpModalContext';
import { useMaybeAuth } from '../../context/AuthContext';
import { setChatPanelOpen, setChatPanelFullScreen } from '../wallet/chatPanelStore';
import { useChatModeDocumentSignal } from './chatModeSignal';
import { CHAT_OPEN_THREAD_EVENT, type ChatOpenThreadDetail } from './openChatForFile';
import { CONNECT_SIGNED_IN_EVENT, type ConnectSignedInDetail } from '../../services/composio/connectSignedInEvent';
import { DESKTOP_SIGNIN_EVENT } from '../DesktopSignInModalHost';
import { resolveConnectResumeOrigin } from './connectResume';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useGlobalChat } from './useGlobalChat';
import { normalizeChatHtml, formatProcessingTime, ensureLinksOpenInNewTab, autoLinkText, htmlToPlainText, isRetiredDevNoticeMessage, shouldNavigateInApp } from './utils';
import { plainTextToComposerHtml } from './pasteSanitizer';
import { markdownToHtml } from '../../utils/markdownProcessor';
import type { GlobalChatSystemProps, ChatMessage, TokenUsageMetadata, TokenUsageSnapshot, SpecialMessageActionButton } from './types';
import { CHAT_LAYOUT } from './types';
import { SEND_FAILURE_RETRY_SOURCE_TAG } from './sendFailureRetry';
import { failureNeedsExplainTurn } from '../../services/globalTools/tools/sendOutcomePolicy';
import { handleWriteVideoScript, handleWriteVideoScriptFromStage1, handleVideoScriptProceed, extractLatestWorkflowOutput } from '../../services/agenticWorkflows';
import { refreshRuntimeTestingFromStorage, shouldTagAsTest } from '../../config/agenticWorkflowConfig'; // isDevPreviewOrigin now folded into shouldTagAsTest()
import { getWorkflowOutput } from '../../services/globalTools/workflowOutputStore';
import { getFromLocalStorage } from './utils';
// MODEL_CODE_NAMES + getModelCodeName moved to the leaf config module
// (src/config/providerCodeNames.ts) to break a circular import
// (helpers.ts -> GlobalChatSystem.tsx -> useGlobalChat.ts/registerAllTools).
// Re-exported below for backward compatibility with existing importers.
import {
  MODEL_CODE_NAMES,
  getModelCodeName,
  getPublicSystemNameForUsage,
  PROVIDER_COLORS,
  getProviderColor,
  getSystemDotColor,
  resolveEffortCodeWordForDisplay,
} from '../../config/providerCodeNames';
import { auxSidecarStepLabel } from '../../config/usageVocabulary';
import { safelyPersistThreadMessages } from './threadPersist';
import { upsertThreadMessages } from '../../repositories/chatThreadMessagesRepository';
import {
  createRenderStormState,
  trackRenderStorm,
  buildRenderStormDiagnostics,
  type RenderStormState,
} from './renderStormTripwire';
import { shouldRenderPendingOverlay, messageCanHidePendingOverlay } from './pendingOverlayGate';
import { isActivePendingShapedMessage, resolveSyntheticTurnIndicator } from './turnIndicator';
import { shouldAnimateMessageEntry, stableMessageRowKey } from './messageRowIdentity';
import { decorateCopyableChunks } from './chunkCopy';
// Round O (O2, 2026-07-15): shared body-portal mousedown predicate — the same
// rule useGlobalChat's click-outside handler consults, so the two document-
// level mousedown paths can never drift apart again.
import { isInsidePrest0nPortal } from './portalClickGuard';
import { getThreadStorageKeys } from './types';
import { GLOBAL_CHAT_HISTORY_KEY } from './types';
import ConstructionWaitlistBubble from '../ConstructionWaitlistBubble';
import { calculateCost, hasKnownPricing } from '../../config/aiModelPricing';
import { OlvCostCell } from './OlvCostCell';
import { useOnboardingWelcome } from './useOnboardingWelcome';
import {
  findOriginalUserQueryFromHistory,
  detectSpanishFromQuery,
  buildSkipDispatchMessage,
  pickOriginalQueryForSkip,
} from './skipDirectDispatch';
import {
  useChatThreads,
  detectThreadLanguage,
  localizedDefaultTitle,
  isDefaultTitle,
  isThreadTombstonedInStorage,
  shouldShowGlobalClearButton,
} from './useChatThreads';
import { composePersonalizedGreeting } from '../../services/threadMetadata/composeGreeting';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { applyChatClipboardPayload, selectionToHtml } from './chatClipboard';
import ThreadArchiveNoticeOverlay from './ThreadArchiveNoticeOverlay';
import {
  cycleThreadNotifyMode,
  ensurePlatformNotificationPermission,
  loadThreadNotificationDefaults,
  modeFromDefaults,
  playThreadChime,
  resolveThreadNotifyMode,
  setStoredThreadNotifyMode,
  showThreadPlatformNotification,
  type ThreadNotifyMode,
} from '../../services/threadNotifications';
import {
  clearThreadArchiveNotice,
  subscribeThreadArchiveNotice,
  THREAD_ARCHIVE_PASSING_NOTICE_MS,
} from '../../services/threadArchiveNotice';
// Summon-gate suppressed delivery (2026-08-26): files shared without
// summoning Prest0n are uploaded to Storage first, because the other
// participants read them by URL.
import { getCurrentUserUid, serializeAttachmentsWithStorage } from '../../utils/attachmentStorage';
import { archiveGroupThreadForEveryone, type GroupPostAttachment } from '../../repositories/threadMembershipWriteClient';
import { isAssistantMentionOnly } from '../../services/threadMentions/summonGate';
// L4 (2026-07-15): guest usage privacy — signed-out viewers never see
// token-usage/cost details; the ⓘ popup body becomes a generic sign-in notice.
import { useViewerIsSignedOut, GuestUsageNotice } from './guestUsagePrivacy';
import { OrgUsageNotice, useUsageDetailRestricted } from './orgUsagePrivacy';
import type { ChatThread } from './types';
import { ThreadSwitcherBar, shouldRouteCloseToEmptyDelete } from './ThreadSwitcherBar';
import { WelcomeStarterChips, shouldShowStarterChips } from './WelcomeStarterChips';
import { computeGreetingRank } from '../../services/threadMetadata/greetingRank';
import { ThreadHeaderLabel } from './ThreadHeaderLabel';
import { ThreadHeaderActionButtons } from './ThreadHeaderActionButtons';
import { ThreadHeaderOverflowMenu } from './ThreadHeaderOverflowMenu';
// Docked density round (owner feedback, 2026-08-01): the pure mode/width
// decisions the docked header/footer/pill consult — see dockedDensity.ts.
import { isDockedPanelMode, shouldShowParticipantsLine, shouldShowThreadStrip, formatMessageMetaTimestamp } from './dockedDensity';
import { ThreadSidebar, type SidebarAccountIdentity } from './ThreadSidebar';
import { RoutineGlyph, routinePastTimeLabel, routineThreadDisplayName } from './RoutineGlyph';
import { RoutineRunBannerChip, RoutineRunBannerText } from './Routines.styles';
import { RoutineHeaderControls } from './RoutineHeaderControls';
import ThreadAddPeoplePanel from './ThreadAddPeoplePanel';
import InviteModal from '../TeamRoles/InviteModal';
import { useThreadAddPeople } from './useThreadAddPeople';
import type { AddPeopleRosterEntry } from '../../services/threadAddPeople';
import { normalizeRosterEntries } from '../../services/threadAddPeople/workspaceRosterModel';
import { useThreadSummonSetting } from './useThreadSummonSetting';
import { resolveSuggestionChips, suggestionsNeedMention, applyAssistantMentionToAll } from '../../services/threadMentions/suggestionMention';
import { useThreadMentionSummon } from './useThreadMentionSummon';
import { useThreadMentionAutocomplete } from './useThreadMentionAutocomplete';
import ThreadMentionAutocompleteDropdown from './ThreadMentionAutocompleteDropdown';
// Thread recommendations (owner directive 2026-08-11): chips in EVERY thread,
// grounded against the account's LIVE connected apps so a suggestion is never
// something Prest0n can't actually do.
import {
  groundSuggestionChips,
  getConnectedAppSlugs,
  invalidateConnectedAppsCache,
  resolveFollowupRecsEnabled,
  resolveRetryFollowup,
  chipNeedsAttachment,
} from '../../services/threadRecommendations';
import { useRbac } from '../../hooks/useRbac';
import { useLiveProfileName } from '../../hooks/useLiveProfileName';
import { useAutoTopUp } from '../../hooks/useAutoTopUp';
import { ThreadFollowupChips } from './ThreadFollowupChips';
import { useThreadFollowupSuggestions } from './useThreadFollowupSuggestions';
import { useStarterSuggestions } from './useStarterSuggestions';
import { resolveMentionAffordance } from '../../services/threadMentions/mentionAffordance';
import type { MentionRosterEntry } from '../../services/threadMentions';
import {
  buildSenderProfiles,
  participantCountLabel,
  resolveMessageSender,
  type SenderProfile,
} from '../../services/groupThreads/senderIdentity';
import { messageTurnKey } from './messageTurns';
import { isSpecialTileMessage, cardAttributionOwnedByTurnAnswer } from './turnAttribution';
import { NAV_CLUSTER_SLEEP_MS } from './constants';
import { MessageSenderChip } from './MessageSenderChip';
import { ASSISTANT_SENDER_ID } from '../../types/groupThreadMessageDoc';
import { resolveGroupThreadReadEnabled, setGroupThreadReadAccount } from '../../services/groupThreads/featureFlags';
import {
  getActiveWorkspaceAccount,
  onActiveWorkspaceResolved,
  setActiveWorkspaceAccount,
} from '../../services/activeWorkspace';
import { readConfirmedWorkspace, writeConfirmedWorkspace } from '../../services/prest0nRuntime/confirmedWorkspaceCache';
import { beginWorkspaceTransition } from '../../services/workspaceTransition';
import { consumePendingThreadOpen } from '../../lib/pendingThreadOpen';
// Persisted per-thread view markers (returning-tab reliability, 2026-09-01):
// the writer + restore sequencing live in THIS component — the scroll
// controller stays storage-free (threadScrollingUx pins). Anchors are keyed
// by perDocMessageId, the canonical merge key that survives the
// UUID↔prest0n_task_ id convergence across reloads (raw message.id does not).
import {
  buildThreadViewKey,
  peekThreadViewMarker,
  saveThreadViewMarker,
  clearThreadViewMarker,
  type ThreadViewMarker,
} from '../../lib/threadScrollRestore';
import { perDocMessageId } from '../../types/chatThreadMessageDocFirestore';
import {
  SHARED_THREAD_TITLE_FALLBACK,
  loadSurfacedThreadIds,
  materializeSurfacedThreads,
  selectStubsToRelabel,
  selectThreadsToSurface,
} from '../../services/groupThreads/sharedThreadSurfacing';
import { createClientMembershipSource } from '../../services/threadChoicePanel';
import { fetchThreadMembershipStrict } from '../../repositories/threadMembershipReadClient';
import {
  loadSharedParticipantsCache,
  parseSharedParticipantsCache,
  saveSharedParticipantsCache,
  sharedParticipantsCacheKey,
} from '../../services/groupThreads/participantsCache';
import { createThreadMembership } from '../../repositories/threadMembershipWriteClient';
import {
  isAutoTitleSuppressed,
  resolveThreadTitleAuthority,
  shouldPublishCanonicalTitle,
} from '../../services/groupThreads/threadTitleAuthority';
import ThreadTagChips from './ThreadTagChips';
import ThreadTagFilterBar from './ThreadTagFilterBar';
import { setGroupThreadTagsAccount, resolveGroupThreadTagsEnabled } from '../../services/threadTags';
import { useThreadTagsController } from './useThreadTagsController';
import { chatThreadIndexDocExists, upsertChatThread } from '../../repositories/chatThreadIndexRepository';
import { getAccountMembers } from '../../services/prest0nRuntime/accountMembership';
import { loadAccountProfile } from '../../services/prest0nRuntime/accountResolver';
import { buildMemberLabelMap } from '../../services/prest0nRuntime/memberDisplayName';
import { INTERNAL_PREST0N_SOURCES, INTERNAL_PREST0N_INSTANCE_IDS } from './types';
import { useAvatarBridge } from '../../hooks/useAvatarBridge';
import { TRIAL_ENDED_CHAT_MESSAGE } from '../../hooks/trialStatus';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import type {
  BridgeThreadStore,
  BridgeChatSystem,
} from '../../hooks/useAvatarBridge';
import { captureActiveThreadId } from '../../services/agenticWorkflows/broadcastBridgeEvent';
import {
  AttachmentPreviewOverlay,
  type AttachmentPreviewPlaceholder,
} from './AttachmentPreviewOverlay';
import {
  loadDraft,
  saveDraft,
  clearDraft,
  loadAttachmentDraft,
  loadAttachmentDraftBlobs,
  saveAttachmentDraft,
  clearAttachmentDraft,
  draftEntryToFile,
  sweepStaleDraftKeys,
  type AttachmentDraftEntry,
} from './draftPersistence';
import {
  markSentNow as draftMarkSentNow,
  markSentText as draftMarkSentText,
  isSentTextMatch as draftIsSentTextMatch,
  markProcessingStarted as draftMarkProcessingStarted,
  markProcessingEnded as draftMarkProcessingEnded,
  shouldSuppressDraftRehydrate,
} from './draftSuppressionState';

// I4 ("Load earlier messages", 2026-07-15) — affordance copy lives in the
// leaf module loadEarlierCopy.ts (pinned by tests without loading this tree).
import { loadEarlierMessagesCopy } from './loadEarlierCopy';
import {
  VISIBLE_MESSAGE_COUNT,
  collapsedRegionHoldsRow,
  loadEarlierControlVisible,
} from './collapsedHistory';
// P0 thread-durability (2026-09-02): this surface owns two halves of the
// connect tile's durability. The browser-lane card's click writes its `opened`
// lifecycle stamp onto the card message from here (the delegated button
// handler below), and a standing tile must never be hidden by the collapsed
// window (plan §3.3) — the collapse check uses the SAME predicate the payload
// rebuild and the hydrate refresh use, so "standing tile" has one definition.
import {
  isConnectBrowserOpenButtonId,
  isRebuildableConnectCard,
  stampOpenedFromClick,
} from '../../services/globalTools/tools/connectTileRehydrate';

/** How long a started sign-in stays resumable (2026-09-03). A browser
 *  sign-in with a one-time code, a password manager and an SSO redirect is
 *  minutes of work; an hour later the user has moved on, and answering the
 *  question they abandoned would be its own kind of wrong.
 *
 *  Declared below the imports on purpose: CRA enforces `import/first`, so a
 *  constant wedged between two imports fails the BUILD while passing every
 *  test and gate.
 */
const CONNECT_RESUME_WINDOW_MS = 30 * 60 * 1000;

// ============================================================================
// Token Info Popup Component
// ============================================================================

export interface TokenInfoPopupContentProps {
  /** Single token usage (backward compatible) */
  tokenUsage?: TokenUsageMetadata | TokenUsageSnapshot | null;
  /** Multiple token usages for multi-model responses */
  tokenUsages?: (TokenUsageMetadata | TokenUsageSnapshot)[];
  position: 'above' | 'below';
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  isUser?: boolean;
  /** Tags from the message for displaying test indicator */
  tags?: string[];
}

// ============================================================================
// Internal Model Code Names & Provider Colors
// ============================================================================

// Provider colors + model code names (consistent across all time for internal
// use). Re-exported from the leaf config module to preserve existing import
// paths (e.g. CopyableSystem.tsx imports these from GlobalChatSystem). The
// real definitions live in src/config/providerCodeNames.ts to break the
// circular import that was aborting tool registration — and so tool tiles
// share the same provider color map (2026-07-06 consolidation).
export { MODEL_CODE_NAMES, getModelCodeName, PROVIDER_COLORS, getProviderColor };

// Backoff schedule for re-running surfaceSharedThreads after a FAILED
// threadMembershipRead (2026-08-08 tab-parity fix). Exhausted attempts fall
// back to the existing focus/visibility refresh triggers.
const MEMBERSHIP_READ_RETRY_DELAYS_MS = [5_000, 15_000, 45_000];

// ────────────────────────────────────────────────────────────────────────────
// Rendered-body memo (smooth streaming, 2026-08-23)
// ────────────────────────────────────────────────────────────────────────────
// Every message body without stored html re-runs the full markdown → HTML →
// link-normalize pipeline on EVERY render of the transcript. That was already
// wasteful; with the paced reveal repainting the live bubble up to once per
// animation frame it would re-parse the ENTIRE thread's history at 60Hz. The
// pipeline is pure in its inputs, so a bounded text-keyed cache makes every
// settled message a lookup and leaves only the one growing bubble doing real
// work. Bounded to keep a long session from retaining every body ever seen.
const RENDERED_BODY_CACHE = new Map<string, string>();
const RENDERED_BODY_CACHE_MAX = 300;

function renderMessageBodyHtml(
  text: string,
  displayAsUser: boolean,
  convert: (raw: string) => string,
): string {
  const key = `${displayAsUser ? 'u' : 'a'}:${text}`;
  const hit = RENDERED_BODY_CACHE.get(key);
  if (hit !== undefined) return hit;
  const html = ensureLinksOpenInNewTab(normalizeChatHtml(convert(text)));
  if (RENDERED_BODY_CACHE.size >= RENDERED_BODY_CACHE_MAX) {
    const oldest = RENDERED_BODY_CACHE.keys().next().value;
    if (oldest !== undefined) RENDERED_BODY_CACHE.delete(oldest);
  }
  RENDERED_BODY_CACHE.set(key, html);
  return html;
}

// Summary info icon with hover tooltip and click-to-copy (matches wallet page design)
const SummaryInfoIcon: React.FC<{ summary: string }> = ({ summary }) => {
  const [copied, setCopied] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '6px',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); }}
    >
      {/* Clickable icon button */}
      <span
        onClick={handleCopy}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '22px',
          height: '22px',
          borderRadius: '6px',
          background: copied
            ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
            : isHovered
              ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
              : '#f1f5f9',
          border: `1px solid ${copied ? '#86efac' : 'rgba(0,0,0,0.06)'}`,
          color: copied ? '#16a34a' : '#64748b',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          transform: isHovered && !copied ? 'scale(1.08)' : 'scale(1)',
          boxShadow: isHovered && !copied ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
        }}
      >
        {copied ? (
          // Checkmark icon when copied
          <svg viewBox="0 0 24 24" style={{
            width: '13px',
            height: '13px',
            stroke: 'currentColor',
            strokeWidth: 2.5,
            fill: 'none',
          }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          // Info icon (default)
          <svg viewBox="0 0 24 24" style={{
            width: '13px',
            height: '13px',
            stroke: 'currentColor',
            strokeWidth: 2.5,
            fill: 'none',
          }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        )}
      </span>

      {/* Hover tooltip popup showing summary */}
      {(isHovered || copied) && (
        <span
          onClick={handleCopy}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: copied
              ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
              : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: copied
              ? '0 4px 16px rgba(22, 163, 74, 0.3)'
              : '0 4px 20px rgba(0, 0, 0, 0.25)',
            zIndex: 100,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {copied ? (
            <>
              <svg viewBox="0 0 24 24" style={{
                width: '12px',
                height: '12px',
                stroke: 'currentColor',
                strokeWidth: 2.5,
                fill: 'none',
              }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>copied!</span>
            </>
          ) : (
            <>
              <span>{summary}</span>
              <span style={{
                padding: '2px 6px',
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '4px',
                fontSize: '0.65rem',
              }}>
                click to copy
              </span>
            </>
          )}
          {/* Tooltip arrow */}
          <span
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: copied ? '6px solid #15803d' : '6px solid #0f172a',
            }}
          />
        </span>
      )}
    </span>
  );
};

export const TokenInfoPopupContent: React.FC<TokenInfoPopupContentProps> = ({
  tokenUsage,
  tokenUsages,
  position,
  onClose,
  anchorRef,
  isUser = false,
  tags,
}) => {
  const popupRef = React.useRef<HTMLDivElement>(null);

  // L4 (2026-07-15): view-time usage-privacy gate. A signed-out viewer NEVER
  // sees token counts, model identities, or cost figures — the popup body is
  // replaced below with the generic sign-in notice. Signed-in rendering is
  // bit-identical to before.
  const viewerSignedOut = useViewerIsSignedOut();

  // ORG-USAGE-2026: organizational usage visibility. Runs AFTER the guest gate
  // (which fails closed) and fails OPEN — a signed-in member only loses usage
  // detail when their org positively restricts it. See orgUsagePrivacy.tsx.
  const orgUsageRestricted = useUsageDetailRestricted();

  // Build array of usages from either array prop or single prop (backward compatible)
  const allUsages: TokenUsageSnapshot[] = React.useMemo(() => {
    if (tokenUsages && tokenUsages.length > 0) {
      return tokenUsages.map((u) => (u as TokenUsageSnapshot) || { provider: 'unknown' });
    }
    if (tokenUsage) {
      return [(tokenUsage as TokenUsageSnapshot) || { provider: 'unknown' }];
    }
    return [{ provider: 'unknown' } as TokenUsageSnapshot];
  }, [tokenUsage, tokenUsages]);

  // For backward compatibility, use first usage for single-model logic
  const usage: TokenUsageSnapshot = allUsages[0];
  const isMultiModel = allUsages.length > 1;

  // R15 unit-lockstep (2026-08-09): ONE popup-level unit toggle. The old
  // per-cell hover state let the hovered row show USD while every other row
  // kept the display unit — mixed units inside a single breakdown. Hovering
  // any WU cell now flips the WHOLE popup to USD together via the shared
  // OlvCostCell (module-scope component — popup re-renders no longer remount
  // each cell, which also fixes the hover transition resetting).
  // 2026-08-13 WU migration: the cell renders PRO Work Units derived from the
  // same user-cost USD at the fixed Standard Rate (src/config/workUnits.ts).
  const [showUsd, setShowUsd] = React.useState(false);
  const renderWuCost = (usdCost: number | undefined) => (
    <OlvCostCell usdCost={usdCost} showUsd={showUsd} onHoverChange={setShowUsd} />
  );
  const hasTokenData =
    usage.inputTokens !== undefined ||
    usage.outputTokens !== undefined ||
    usage.reasoningTokens !== undefined ||
    usage.citationTokens !== undefined ||
    usage.searchQueries !== undefined ||
    usage.totalTokens !== undefined ||
    usage.responseTimeMs !== undefined;

  // Determine if this message required AI processing
  // Must have actual token data OR a known provider (not undefined/unknown)
  const hasKnownProvider = usage.provider && usage.provider !== 'unknown';
  const requiredAiProcessing = !isUser && (hasTokenData || hasKnownProvider);

  const [style, setStyle] = React.useState<React.CSSProperties>({ opacity: 0 });

  // Position the popup relative to the button using a portal to avoid clipping
  // Ensures the popup is always fully visible within the viewport
  React.useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const gap = 12;
    const viewportPadding = 12; // Minimum distance from viewport edges

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate available space above and below the anchor
    const spaceAbove = anchorRect.top - viewportPadding;
    const spaceBelow = viewportHeight - anchorRect.bottom - viewportPadding;

    // Determine best vertical position - choose direction with most space
    let top: number;

    const fitsAbove = popupRect.height <= spaceAbove;
    const fitsBelow = popupRect.height <= spaceBelow;

    if (fitsAbove && (position === 'above' || !fitsBelow)) {
      // Position above - popup fits or user prefers above
      top = anchorRect.top - gap - popupRect.height;
    } else if (fitsBelow) {
      // Position below - popup fits below
      top = anchorRect.bottom + gap;
    } else if (spaceBelow >= spaceAbove) {
      // More space below - position there even if tight fit
      top = anchorRect.bottom + gap;
    } else {
      // More space above - position there even if tight fit
      top = Math.max(viewportPadding, anchorRect.top - gap - popupRect.height);
    }

    // Ensure top is never negative or too close to edge
    top = Math.max(viewportPadding, top);

    // Ensure popup doesn't go below viewport
    if (top + popupRect.height > viewportHeight - viewportPadding) {
      top = viewportHeight - popupRect.height - viewportPadding;
      top = Math.max(viewportPadding, top);
    }

    // Calculate horizontal position - align to right edge of anchor, constrain to viewport
    let left = anchorRect.right - popupRect.width;
    const minLeft = viewportPadding;
    const maxLeft = viewportWidth - popupRect.width - viewportPadding;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    // If popup is wider than viewport, align to left edge
    if (popupRect.width > viewportWidth - (viewportPadding * 2)) {
      left = viewportPadding;
    }

    setStyle({
      top,
      left,
      opacity: 1,
    });
  }, [anchorRef, position, tokenUsage]);

  // Close on click outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Format response time
  const formatResponseTime = (ms?: number): string => {
    if (!ms) return 'n/a';
    if (ms < 1000) return `${ms}ms`;

    const totalSeconds = Math.floor(ms / 1000);
    if (totalSeconds < 60) {
      // Show one decimal for short durations, whole seconds otherwise
      return ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${totalSeconds}s`;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) {
      return `${minutes}m ${seconds}s`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m ${seconds}s`;
  };

  // Calculate combined totals for multi-model display
  const combinedTotalCost = React.useMemo(() => {
    if (!isMultiModel) return 0;
    return allUsages.reduce((sum, u) => {
      const cost = (u as TokenUsageSnapshot).cost || calculateCost(u);
      return sum + (cost?.totalCost || 0);
    }, 0);
  }, [allUsages, isMultiModel]);

  // Render a single system-call section (compact for multi-call, full for single).
  // 2026-08-13 WU migration: the title is the versionless STATION name
  // (getPublicSystemNameForUsage) — never a versioned codename or raw token
  // counts; the only figure shown per call is its PRO Work Units.
  const renderModelSection = (u: TokenUsageSnapshot, index: number, isCompact: boolean) => {
    // R17-P1 WS-A2 keeps the zero-cost `<model>+workflow-intent` dispatch
    // row visible, but it records a DETERMINISTIC route decision — no model
    // was called, so the vendor-family color of the model id it rode on
    // (x-ai red for the orchestrator primary) misreads as a Grok call that
    // never happened. Neutral dot + explicit label, scoped HERE only —
    // providerCodeNames' shared maps also feed the activity page's colors.
    const uIsWorkflowIntentDispatch =
      typeof u.model === 'string' && u.model.endsWith('+workflow-intent');
    // 2026-08-15: color by the MODEL's family (true vendor) — breakdown rows
    // often have no provider field (or the transport 'openrouter'), which
    // turned every dot neutral slate.
    const uProviderColor = uIsWorkflowIntentDispatch
      ? '#94a3b8'
      : getSystemDotColor(u.model, u.provider);
    const uStationName = getPublicSystemNameForUsage(u.model, u.provider);
    const uHasTokenData =
      u.inputTokens !== undefined ||
      u.outputTokens !== undefined ||
      u.reasoningTokens !== undefined ||
      u.citationTokens !== undefined ||
      u.searchQueries !== undefined;
    const uHasKnownProvider = u.provider && u.provider !== 'unknown';
    const uRequiredAiProcessing = !isUser && (uHasTokenData || uHasKnownProvider);
    const uCostBreakdown = (u as TokenUsageSnapshot).cost || calculateCost(u);

    return (
      <div key={index} style={{ marginBottom: isCompact && index < allUsages.length - 1 ? '12px' : 0 }}>
        {/* Separator line for multi-model (except first) */}
        {isCompact && index > 0 && (
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', marginBottom: isCompact ? '8px' : '12px' }} />
        )}

        {/* Call header with provider dot, STATION name, and optional summary info icon */}
        {uRequiredAiProcessing && (
          <TokenInfoHeader $compact={isCompact}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isCompact ? '6px' : '8px' }}>
              <span
                style={{
                  width: isCompact ? '8px' : '10px',
                  height: isCompact ? '8px' : '10px',
                  borderRadius: '50%',
                  background: uProviderColor,
                  flexShrink: 0,
                }}
                title="AI Provider"
              />
              <span
                style={{
                  fontSize: isCompact ? '0.72rem' : '0.8rem',
                  fontWeight: 600,
                  color: uProviderColor,
                  letterSpacing: '0.05em',
                }}
              >
                {uStationName}
                {/* 2026-08-15 effort convention: the CODED effort designator
                    (EFFORT_CODE_WORDS — never the raw level, which is a
                    vendor API enum), rendered dimmed. 2026-08-16 (Miles): the
                    word must ALWAYS display — when the stored row carries no
                    effort, the step's model resolves it from the SSOT
                    (resolveEffortCodeWordForDisplay; stored effort wins). */}
                {resolveEffortCodeWordForDisplay(u.model, u.effort) && (
                  <span style={{ opacity: 0.55, fontWeight: 400 }}>
                    {` · ${resolveEffortCodeWordForDisplay(u.model, u.effort)}`}
                  </span>
                )}
              </span>
              {/* R6: per-call ordinal — the sections are the turn's model
                  calls IN ORDER, and the numbering makes that legible when
                  the same model appears more than once. */}
              {isCompact && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 500,
                    color: '#94a3b8',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  step {index + 1}
                </span>
              )}
              {uIsWorkflowIntentDispatch && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 500,
                    color: '#94a3b8',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  deterministic dispatch — no model call
                </span>
              )}
              {/* 2026-08-16 (Miles): background steps say WHAT they were —
                  the memory update / follow-up suggestions sidecars read as
                  part of the task, not anonymous extra steps. */}
              {auxSidecarStepLabel(u as { billedSeparately?: boolean; sidecarKey?: string; stage?: string }) && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 500,
                    color: '#b8860b',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {auxSidecarStepLabel(u as { billedSeparately?: boolean; sidecarKey?: string; stage?: string })}
                </span>
              )}
              {/* Show test tag badge if message is tagged */}
              {tags?.includes('test example') && (
                <span
                  style={{
                    marginLeft: '6px',
                    padding: '2px 5px',
                    fontSize: isCompact ? '0.6rem' : '0.65rem',
                    fontWeight: 500,
                    color: '#f59e0b',
                    background: 'rgba(245, 158, 11, 0.15)',
                    borderRadius: '3px',
                    border: '1px solid rgba(245, 158, 11, 0.25)',
                  }}
                >
                  test example
                </span>
              )}
              {/* Info icon with summary hover tooltip - click to copy */}
              {u.summary && (
                <SummaryInfoIcon summary={u.summary} />
              )}
            </div>
          </TokenInfoHeader>
        )}

        {/* Compute time */}
        {!isUser && u.responseTimeMs && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isCompact ? '4px' : '6px',
              padding: isCompact ? '1px 0 4px 0' : '2px 0 10px 0',
              fontSize: isCompact ? '0.7rem' : '0.76rem',
              color: '#64748b',
              fontWeight: 500,
            }}
            title="compute time"
          >
            <svg
              viewBox="0 0 24 24"
              style={{
                width: isCompact ? '12px' : '14px',
                height: isCompact ? '12px' : '14px',
                stroke: 'currentColor',
                strokeWidth: 2,
                fill: 'none',
                opacity: 0.7,
              }}
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatResponseTime(u.responseTimeMs)}</span>
          </div>
        )}

        {/* ONE line per call: the call's PRO Work Units (2026-08-13 mandate —
            no token grid, no per-component cost rows). The WU derives from
            the SAME per-call user-cost USD the popup computed before
            (persisted cost snapshot or calculateCost/upstreamCostUsd), and is
            also shown when the usage carries an authoritative upstream cost
            (or the unknown-model floor produced a nonzero estimate) — the
            wallet debits these turns, so the popup must not hide the figure
            just because the model has no pricing-table row. */}
        {uCostBreakdown && (hasKnownPricing(u.model) || (u as TokenUsageSnapshot).cost || (uCostBreakdown.totalCost ?? 0) > 0) && (
          <TokenInfoCostSection $compact={isCompact}>
            <TokenInfoCostTotal $compact={isCompact}>
              <TokenInfoCostTotalLabel $compact={isCompact}>work units</TokenInfoCostTotalLabel>
              <TokenInfoCostTotalValue $compact={isCompact}>{renderWuCost(uCostBreakdown.totalCost)}</TokenInfoCostTotalValue>
            </TokenInfoCostTotal>
          </TokenInfoCostSection>
        )}
      </div>
    );
  };

  const content = (
    <TokenInfoPopup
      ref={popupRef}
      $position={position}
      style={style}
      // Round O (O2.1): this popup is body-portaled (see createPortal below).
      // Without the portal marker, useGlobalChat's handleClickOutside treats a
      // mousedown on it as outside-chat and closes the whole chat pane — which
      // killed the guest "Sign in" link before its click could complete.
      // SpecialMessageTile's popup already carries the same marker.
      data-prest0n-portal="true"
      onClick={(e) => e.stopPropagation()}
    >
      {/* L4: signed-out viewers get ONE uniform generic message — never token
          counts, model identities, cost figures, or even the no-compute
          fallback (the popup must reveal nothing about compute to guests). */}
      {viewerSignedOut ? (
        <GuestUsageNotice />
      ) : orgUsageRestricted ? (
        /* Signed in, but this organization does not grant this member usage
           visibility. One uniform notice — same shape as the guest gate. */
        <OrgUsageNotice />
      ) : (
        <>
          {/* Message for user messages or messages without AI compute */}
          {(isUser || !requiredAiProcessing) && (
            <div
              style={{
                fontSize: '0.76rem',
                color: '#94a3b8',
                padding: '10px 12px',
                lineHeight: 1.45,
                background: 'rgba(0, 0, 0, 0.02)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.03)',
              }}
            >
              no extensive compute was necessary for this message
            </div>
          )}

          {/* Render model sections */}
          {requiredAiProcessing && (
            <>
              {/* R6: multi-call turns announce the list so the scroll is
                  discoverable and the ordering contract is explicit. */}
              {isMultiModel && (
                <div
                  style={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    letterSpacing: '0.06em',
                    paddingBottom: '8px',
                    marginBottom: '10px',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    position: 'sticky',
                    top: '-18px',
                    background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
                    zIndex: 1,
                  }}
                >
                  {allUsages.length} steps · in step order
                </div>
              )}
              {allUsages.map((u, i) => renderModelSection(u, i, isMultiModel))}

              {/* Turn-total WU for multi-call turns */}
              {isMultiModel && combinedTotalCost > 0 && (
                <>
                  <div style={{ borderTop: '2px solid rgba(0,0,0,0.1)', margin: '12px 0' }} />
                  <TokenInfoCostTotal>
                    <TokenInfoCostTotalLabel>combined total</TokenInfoCostTotalLabel>
                    <TokenInfoCostTotalValue>{renderWuCost(combinedTotalCost)}</TokenInfoCostTotalValue>
                  </TokenInfoCostTotal>
                </>
              )}
            </>
          )}
        </>
      )}
    </TokenInfoPopup>
  );

  return ReactDOM.createPortal(content, document.body);
};

// ============================================================================
// Token Info Action (button + popup)
// ============================================================================

interface TokenInfoActionProps {
  message: ChatMessage;
  index: number;
  isActive: boolean;
  onToggle: () => void;
}

const TokenInfoAction: React.FC<TokenInfoActionProps> = ({ message, index, isActive, onToggle }) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const fallbackUsage: TokenUsageMetadata = {
    provider: 'unknown', // Always 'unknown' for messages without real token data
    model: undefined, // Don't use sourceTag as model - it's not a real AI model identifier
    timestamp:
      message.timestamp instanceof Date
        ? message.timestamp
        : new Date(message.timestamp as unknown as string),
  };

  // Check for multi-model usages array first
  const hasMultiUsages = (message.tokenUsages && message.tokenUsages.length > 0) ||
    (message.tokenUsageSnapshots && message.tokenUsageSnapshots.length > 0);

  const usagesForPopup = hasMultiUsages
    ? (message.tokenUsages || message.tokenUsageSnapshots || [])
    : undefined;

  const usageForPopup = !hasMultiUsages
    ? ((message.tokenUsage as TokenUsageMetadata | undefined) ||
       (message.tokenUsageSnapshot as TokenUsageSnapshot | undefined) ||
       fallbackUsage)
    : undefined;

  return (
    <>
      <TokenInfoButton
        ref={buttonRef}
        aria-label="view info"
        title="view info"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </TokenInfoButton>
      {isActive && (
        <TokenInfoPopupContent
          tokenUsage={usageForPopup}
          tokenUsages={usagesForPopup}
          position="above"
          anchorRef={buttonRef}
          onClose={onToggle}
          isUser={message.isUser}
          tags={message.tags}
        />
      )}
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const GlobalChatSystem: React.FC<GlobalChatSystemProps> = ({
  config: userConfig,
  className,
  children,
}) => {
  const {
    state,
    dispatch,
    config,
    handlers,
    refs,
    waitlistController,
    showScrollToBottom,
    isPinnedNow,
    isNearTop,
    newMessageCount,
    hasUnseenActivity,
    scrollToBottom,
    scrollToTopAnimated,
    isAtThreadTop,
    isAtThreadBottom,
    stepPosition,
    stepToPreviousMessage,
    stepToNextMessage,
    agenticWorkflows,
    globalTools,
    isSystemProcessing,
    lowBalanceState,
    trialExpiredBlock,
    sendProgrammaticMessage,
    clearComposerForSuppressedDelivery,
    isPrest0nSessionReady,
    cancelActiveThread,
    cancelThreadSendWatchdog,
    pokeActiveTaskDiscovery,
    prest0nSessionAccountId,
    workspaceUnresolved,
    isMigrationGateChecking,
  } = useGlobalChat(userConfig);

  // Navigation for wallet redirect
  const navigate = useNavigate();

  // ============================================================================
  // Thread Switcher gating
  // ============================================================================
  // The Slack-style thread switcher is for account-specific Prest0ns only.
  // It MUST be gated off for internal Prest0n usage (citadel command centre)
  // and prest0-vm (infrastructure Prest0n) instances regardless of whether
  // `enableThreadSwitcher` is set — internal chats are single-purpose and
  // should never expose multi-thread project switching.
  const isInternalPrest0n = useMemo(() => {
    const source = (config.source || '').toLowerCase();
    const iid = (config.instanceId || '').toLowerCase();
    if (INTERNAL_PREST0N_SOURCES.has(source)) return true;
    if (INTERNAL_PREST0N_INSTANCE_IDS.has(iid)) return true;
    // Defensive: anything that self-identifies as vm / internal. Keeps us
    // safe if a new internal instance is added without updating the set.
    if (/(^|[_-])vm([_-]|$)/.test(iid) || /(^|[_-])vm([_-]|$)/.test(source)) return true;
    if (iid.startsWith('citadel') || source.startsWith('citadel')) return true;
    return false;
  }, [config.source, config.instanceId]);

  const threadSwitcherEnabled = !!config.enableThreadSwitcher && !isInternalPrest0n;

  // ── Fullscreen sidebar footer identity (exit + account menu, Miles
  // 2026-07-13; live-name round 2026-08-12) ─────────────────────────────
  // Same AuthContext the app Header reads (initials/profileImage + logout).
  // useMaybeAuth (non-throwing) because isolated tests / embedded hosts mount
  // the widget without an AuthProvider — they simply get no account button.
  const maybeAuth = useMaybeAuth();

  const [currentUid, setCurrentUid] = useState<string | null>(() => getAuth().currentUser?.uid || null);
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (user) => {
      setCurrentUid(user?.uid || null);
    });
  }, []);

  // Chat balance chrome (2026-08-14 owner directive): the chat NEVER mentions
  // the balance unless it is low or out. "Low" is measured against the user's
  // own auto top-up threshold (default $25), same as the global banner.
  const { settings: chatAutoTopUpSettings } = useAutoTopUp(currentUid);
  // OUT: wallet actually empty — derived in useGlobalChat (<= $0, real user,
  // summary settled; ignores the entitlement bypass, matching the server's
  // hard balance stop). Bypassed lanes report balanceSettled:false and
  // isCriticalBalance:false, so they get no balance chrome at all.
  const chatOutOfWorkUnits = lowBalanceState.isCriticalBalance;
  // LOW: above empty but at/under the auto top-up threshold — one light
  // nudge; trial/comped accounts with an entitlement bypass are not nagged.
  const chatLowBalanceNudge =
    !chatOutOfWorkUnits &&
    !!currentUid &&
    lowBalanceState.hasUser === true &&
    lowBalanceState.balanceSettled === true &&
    !lowBalanceState.hasEntitlementBypass &&
    lowBalanceState.balanceUsd > 0 &&
    lowBalanceState.balanceUsd <= chatAutoTopUpSettings.thresholdUsd;

  // Owner directive 2026-08-12: the footer shows the user's REAL first + last
  // name from the profile page, live — not the email local-part that
  // AuthContext's login-time `name` snapshot carries for many accounts. The
  // live profile doc (the exact doc the profile form edits, onSnapshot) wins;
  // the AuthContext chain stays as the fallback while it loads or when no
  // profile name exists yet.
  const liveProfileName = useLiveProfileName(currentUid);
  const sidebarAccount = useMemo<SidebarAccountIdentity | null>(() => {
    const authUser = maybeAuth?.isAuthenticated ? maybeAuth.user : null;
    if (!authUser) return null;
    const name = liveProfileName?.fullName
      || ((authUser.firstName && authUser.lastName)
        ? `${authUser.firstName} ${authUser.lastName}`
        : (authUser.name || authUser.email));
    // Lowercase initials — the Header's MobileInitials pattern; same
    // live-first sourcing as the name so the avatar bubble matches it.
    const first = (liveProfileName?.firstName || authUser.firstName || '').trim();
    const last = (liveProfileName?.lastName || authUser.lastName || '').trim();
    const initials = (first && last)
      ? `${first[0]}${last[0]}`.toLowerCase()
      : ((name || '').trim().charAt(0).toLowerCase() || '?');
    return { name, initials, avatarUrl: authUser.profileImage || undefined };
  }, [maybeAuth, liveProfileName]);

  // ────────────────────────────────────────────────────────────────────
  // Personalized new-thread greeting (2026-07-13, Option A: pure client
  // composition, zero network). Every thread minted by newThread /
  // resetAllThreads opens with a concise greeting grounded in the account's
  // recent projects (thread titles + topicSummaries the client already
  // holds); accounts with no usable history get exactly the configured
  // static welcome copy. The composer reads the thread list through a ref
  // because `threadStore` is the RESULT of the useChatThreads call the
  // composer is passed into (same pattern as getLatestMessages).
  // ────────────────────────────────────────────────────────────────────
  const threadsForGreetingRef = useRef<ChatThread[]>([]);
  const composeGreeting = useCallback((): {
    text: string;
    html: string;
    tokenUsage?: TokenUsageSnapshot;
    greetingRank?: number;
    starters?: string[];
  } | null => {
    // Multi-tab greeting parity (2026-08-09): never compose from an UNSETTLED
    // config — greetings minted from a tab's transient default copy are how
    // the SAME thread showed different welcomes in different windows. Null
    // defers; the seeder retries when the composer identity changes (the
    // documented contract the 2026-08-03 identity-transition guard relies on).
    if (config.welcomeReady === false) return null;
    // Chips ride the greeting: persisted WITH the message so every tab shows
    // the same starters for the same thread, independent of its own config
    // load timing.
    const greetingStarters = config.welcomeStarters?.length
      ? [...config.welcomeStarters]
      : undefined;
    const baseWelcome = config.welcomeMessage?.text ?? null;
    const result = composePersonalizedGreeting({
      baseWelcome,
      // GlobalChatConfig carries no persona/firm identity fields today
      // (checked 2026-07-13 — headerTitle is a UI header label, not a
      // persona name), so both stay omitted until config grows explicit
      // fields.
      // REVISED (owner directive 2026-07-13 night): open threads are the
      // NET-NEW exclusion span only — never rendered into the greeting.
      // Suggestions come from the account's MEMORY system, distilled
      // server-side (refreshGreetingSuggestions) into the runtime config
      // and delivered here as config.suggestedTasks.
      openThreads: threadsForGreetingRef.current
        // Greeting-seeded drafts (hasUserContent:false) hold no project —
        // only threads with real user content constrain the suggestions.
        .filter((t) => t.hasUserContent !== false)
        .map((t) => ({
          title: t.title,
          topicSummary: t.topicSummary,
          messageCount: t.messageCount,
          userRenamed: t.userRenamed,
        })),
      suggestedTasks: config.suggestedTasks,
      uiLanguage: getUserUiLanguage(),
    });
    if (!result) return null;
    const greetingRank = computeGreetingRank(config.welcomeSource, !!result.usedSuggestions);
    // No-suggestions fallback must be EXACTLY the current welcome copy —
    // including the configured rich html when one exists.
    if (baseWelcome !== null && result.text === baseWelcome && config.welcomeMessage?.html) {
      return {
        text: baseWelcome,
        html: config.welcomeMessage.html,
        greetingRank,
        ...(greetingStarters ? { starters: greetingStarters } : {}),
      };
    }
    // The distillation usage rides along ONLY when its output is actually on
    // screen (owner directive 2026-07-13: AI-composed intro copy carries its
    // real compute data behind the mandatory ⓘ). Static/fallback greetings
    // stay usage-less — the ⓘ shows its "no extensive compute" copy.
    return {
      text: result.text,
      html: result.html,
      greetingRank,
      ...(greetingStarters ? { starters: greetingStarters } : {}),
      ...(result.usedSuggestions
        ? { tokenUsage: config.suggestedTasksUsage ?? undefined }
        : {}),
    };
  }, [
    config.welcomeMessage,
    config.suggestedTasks,
    config.suggestedTasksUsage,
    config.welcomeReady,
    config.welcomeSource,
    config.welcomeStarters,
  ]);

  // Registered-participant thread ids (null until a membership read succeeds) —
  // consumed by the shared-store listener's scale guard below, fail-open on
  // null. Declared BEFORE useChatThreads: the hook receives it as an option.
  // Populated by the recipient-surfacing effect further down (same membership
  // snapshot), which also refreshes right after a successful add-people write.
  const [sharedThreadIds, setSharedThreadIds] = useState<ReadonlySet<string> | null>(null);
  /** True once a membership read ANSWERED — the group/solo classifier is trustworthy. */
  const sharedThreadIdsKnown = sharedThreadIds !== null;
  // threadId → participant count, for the header's "how many people are here"
  // line (owner directive 2026-07-24: never label a thread group/non-group —
  // the count itself tells the user). Same membership snapshot, no extra read;
  // a thread absent from the registry is a solo thread ⇒ "Just you".
  const [sharedThreadParticipants, setSharedThreadParticipants] = useState<Record<string, number>>({});
  // threadId → who created it. The creator is the naming authority for a shared
  // thread: their canonical title is what the server propagates to every
  // participant's index (see threadTitleAuthority). Without this the LLM labeler
  // runs independently in each browser and the same conversation ends up with a
  // different name per person (reported 2026-08-05).
  const [sharedThreadOwners, setSharedThreadOwners] = useState<Record<string, { createdBy?: string | null }>>({});
  const sharedThreadOwnersRef = useRef(sharedThreadOwners);
  sharedThreadOwnersRef.current = sharedThreadOwners;
  /** threadId → explicit relaxation of the @Prest0n summon requirement (W3b). */
  const [requireMentionByThread, setRequireMentionByThread] = useState<Record<string, boolean>>({});

  // Multi-org sealed switch (2026-08-19): ONE handler for every foreign-
  // workspace thread open — the combined view's explicit rows AND the thread
  // store's central guard (routine rows, manager panel, any future path).
  // The controller raises the full-screen blocking overlay (MIN_VISIBLE_MS
  // floor, overlapped with the switch since 2026-08-20), runs the
  // server-verified switch beneath it, stamps the pending-open +
  // continuation markers, and hard-navigates. NEVER renders the thread in
  // place (invariant 1).
  const openForeignThread = useCallback((thread: ChatThread) => {
    const target = thread.accountId;
    if (!target) return;
    beginWorkspaceTransition({
      targetAccountId: target,
      uid: currentUid,
      threadId: thread.id,
      threadTitle: (thread.title || '').trim() || undefined,
      navigation: 'desktop',
    });
  }, [currentUid]);

  const threadStore = useChatThreads({
    instanceId: config.instanceId,
    enabled: threadSwitcherEnabled,
    messages: state.messages,
    isProcessing: state.isProcessing,
    dispatch,
    onForeignThreadSwitch: openForeignThread,
    // PR #97 — Bug #1 fix: pass a synchronous messages accessor so
    // snapshotActive() reads the post-dispatch messages array within
    // the same tick. Without this, addMessage(userMsg) + switchThread()
    // batched in the same tick snapshot the PRE-dispatch messages and
    // the just-added userMsg vanishes.
    getLatestMessages: () => refs.messagesSyncRef.current,
    composeGreeting,
    // Group-threading live listener race guard (2026-07-23 #2): the restored
    // active thread subscribes only once the async account resolution lands,
    // with the flag resolved against THIS id (not the later-published one).
    // Org stickiness round 2 (2026-08-19): while the async resolution is
    // still in flight, seed with the LAST CONFIRMED workspace for the SAME
    // uid (confirmedWorkspaceCache — written only on strict resolution
    // success) — the workspace thread filter used to run fail-open (every
    // org's threads) for those frames and then snap, reading as "my threads
    // keep changing". View-level only; authz stays server-enforced.
    groupReadAccountId: prest0nSessionAccountId
      ?? (currentUid ? (readConfirmedWorkspace(currentUid)?.accountId ?? null) : null),
    // COLD-BOOT FAIL-CLOSED (2026-08-21, incognito churn diagnosis): a fresh
    // profile has neither a resolved identity nor a confirmed-workspace cache
    // to seed the line above, so the rail used to subscribe uid-globally,
    // render EVERY workspace's threads fail-open, and snap strict when the
    // gate landed (threads appear, then vanish — invisible for single-
    // workspace owners, dramatic for multi-org members). While the gate is
    // still checking OR holding unresolved with no seed, thread subscriptions
    // wait; guests (no uid) are never held.
    workspaceIdentityPending: Boolean(currentUid)
      && !(prest0nSessionAccountId
        ?? (currentUid ? (readConfirmedWorkspace(currentUid)?.accountId ?? null) : null))
      && (isMigrationGateChecking || workspaceUnresolved),
    sharedThreadIds,
    // Shared-thread naming authority (2026-08-05). The LLM labeler runs in every
    // participant's browser; without these two hooks each one names the SAME
    // conversation differently and then re-clobbers whatever the server
    // propagated. The creator publishes; everyone else adopts.
    isAutoTitleSuppressed: useCallback(
      (threadId: string) =>
        isAutoTitleSuppressed(
          resolveThreadTitleAuthority({
            threadId,
            selfId: currentUid,
            sharedThreads: sharedThreadOwnersRef.current,
          }),
        ),
      [currentUid],
    ),
    onAutoTitleResolved: useCallback(
      (threadId: string, title: string) => {
        const authority = resolveThreadTitleAuthority({
          threadId,
          selfId: currentUid,
          sharedThreads: sharedThreadOwnersRef.current,
        });
        if (!shouldPublishCanonicalTitle(authority, title)) return;
        // Same authorized write the manual rename uses. Best-effort: a miss just
        // leaves participants on the previous canonical until the next label.
        void createThreadMembership(threadId, undefined, undefined, title).catch(() => undefined);
      },
      [currentUid],
    ),
  });

  // Keep the greeting composer's thread snapshot fresh (effect, not render
  // assignment, so StrictMode double-renders can't observe a torn value).
  useEffect(() => {
    threadsForGreetingRef.current = threadStore.threads;
  }, [threadStore.threads]);

  // Thread View Multi-Choice Panel controller (flag-gated; provably inert unless
  // the panel flag resolves ON for this account). Fed by the user's OWN thread
  // list (no client membership reader exists yet — the server gate remains the
  // isolation boundary), and routes a CONFIRMED, fail-closed-validated choice
  // through the existing safe `switchThread` — it never touches the send path.
  // Track B (group-threading P1b): the client membership source. Server-mediated
  // (Firestore rules deny direct client reads of `prest0n_thread_participants`), so
  // it hydrates through the fail-closed `threadMembershipRead` CF. Constructed once
  // per signed-in user (a user switch gets a fresh cache); inert — it never fetches
  // until the membership-source flag turns Track B on for this account.
  // Strict fetcher (2026-08-08 tab-parity fix): a transport failure THROWS
  // instead of masquerading as "zero group threads", so refresh() can report
  // the miss and the sidebar's populated participant map is never wiped by a
  // failed fetch. canPost() stays fail-closed-empty either way.
  const threadMembershipSource = useMemo(
    () => createClientMembershipSource(fetchThreadMembershipStrict),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUid],
  );
  // Thread View Multi-Choice Panel REMOVED from this surface (owner directive
  // 2026-07-31): the ambient "Choose a thread" panel rendered for any account
  // with ≥2 active threads after the 2026-07-24 '*' rollout, covering the
  // transcript mid-turn, and its "Reply here" was only a switchThread — thread
  // navigation/disambiguation now lives exclusively in the left sidebar (see
  // ThreadSidebar "similar name" hint). The threadChoicePanel service and
  // component survive as a library for a future SEND-TIME interstitial only.
  // threadMembershipSource stays: P4 recipient-side surfacing depends on it.

  // Participant uid → display label for the panel rows (Track B shows REAL members).
  // Loaded from the account roster ONLY when the panel is enabled for this account,
  // so non-panel sessions pay no extra Firestore read. Fail-closed to the raw uid.
  // P3d: group-thread bubbles label other participants by name, so member
  // labels must be available whenever the group READ is on — not only when the
  // multi-choice panel is. Load them if EITHER surface needs them.
  // Publish the active account for the repository-layer group reads (which have
  // no accountId in scope), and resolve the render-path flag for this account.
  useEffect(() => {
    setGroupThreadReadAccount(prest0nSessionAccountId);
    setGroupThreadTagsAccount(prest0nSessionAccountId);
    // Multi-org P2 (2026-08-16): same publish, third consumer — the thread
    // index repository stamps the immutable workspace binding on new threads.
    setActiveWorkspaceAccount(prest0nSessionAccountId);
    // (No last-workspace write here — confirmedWorkspaceCache is written
    // ONLY on strict resolution success inside the migration gate, so a
    // held/cached identity can never refresh its own timestamp.)
  }, [prest0nSessionAccountId, currentUid]);
  const groupThreadReadEnabled = useMemo(
    () => resolveGroupThreadReadEnabled({ accountId: prest0nSessionAccountId }),
    [prest0nSessionAccountId],
  );
  const groupThreadTagsEnabled = useMemo(
    () => resolveGroupThreadTagsEnabled({ accountId: prest0nSessionAccountId }),
    [prest0nSessionAccountId],
  );
  const [threadMemberLabels, setThreadMemberLabels] = useState<Record<string, string>>({});
  // Roster facts (name / email / role / owner) behind the per-message identity
  // pop-out card. Loaded from the SAME roster read as the labels, so the card
  // costs no extra Firestore work. Sender identity is now shown on EVERY message
  // (owner directive 2026-07-24), so this load is no longer conditional on the
  // group flags — an account with no roster simply resolves 'You' / 'Prest0n'.
  const [senderProfiles, setSenderProfiles] = useState<Record<string, SenderProfile>>({});
  // Instant-open seed for the Add People panel (2026-08-09): the raw member
  // rows from the SAME mount-time roster read below. Previously discarded
  // after label-building, forcing the panel to re-fetch the roster through two
  // heavy RBAC callables before showing anyone ("Loading workspace members…").
  const [addPeopleSeedRoster, setAddPeopleSeedRoster] = useState<AddPeopleRosterEntry[]>([]);
  useEffect(() => {
    if (!prest0nSessionAccountId) {
      setThreadMemberLabels({});
      setSenderProfiles({});
      setAddPeopleSeedRoster([]);
      return;
    }
    let alive = true;
    let retryTimer: number | null = null;
    let attempt = 0;
    /**
     * Roster read with RECOVERY (2026-08-26 sender-name work). This used to be
     * a single mount-time read whose failure was swallowed to [] — one
     * transient miss and every teammate rendered as "Teammate <uid>" for the
     * rest of the session, with nothing ever retrying. Now: an empty/failed
     * read retries on a short backoff, and a tab-return re-reads (the roster
     * may have gained members or names while the tab slept). Results still
     * only ever REPLACE state on success-with-content, so a late failure can
     * never wipe a good roster.
     */
    const ROSTER_RETRY_DELAYS_MS = [3_000, 10_000, 30_000];
    const load = async (): Promise<void> => {
      // Three resilient sources so the owner (and self) always resolve a real
      // name, never the "Teammate <uid>" fallback: the member roster, the
      // account owner from the profile (covers a not-yet-seeded owner member
      // doc), and the signed-in user's own auth identity.
      const [members, profile] = await Promise.all([
        getAccountMembers(prest0nSessionAccountId).catch(() => []),
        loadAccountProfile(prest0nSessionAccountId).catch(() => null),
      ]);
      if (!alive) return;
      if (members.length === 0 && !profile) {
        // Nothing resolved — schedule a retry instead of silently settling on
        // uid tags. The existing state (possibly from a previous good read)
        // is deliberately left standing.
        if (attempt < ROSTER_RETRY_DELAYS_MS.length) {
          retryTimer = window.setTimeout(() => { void load(); }, ROSTER_RETRY_DELAYS_MS[attempt]);
          attempt += 1;
        }
        return;
      }
      attempt = 0;
      const ownerUid = profile?.ownerUid ? String(profile.ownerUid).trim() : '';
      const ownerLabel = (profile?.displayName || profile?.firmName || '').trim();
      const self = getAuth().currentUser;
      const labels = buildMemberLabelMap({
        members,
        owner: ownerUid ? { uid: ownerUid, label: ownerLabel } : null,
        self: self
          ? { userId: self.uid, displayName: self.displayName || undefined, email: self.email || undefined }
          : null,
      });
      // Fill-preserving merge: uids only the membership projection named (a
      // cross-workspace teammate) must survive a roster read that does not
      // know them.
      setThreadMemberLabels((prev) => ({ ...prev, ...labels }));
      setSenderProfiles((prev) => ({ ...prev, ...buildSenderProfiles({ members, ownerUid, labels }) }));
      setAddPeopleSeedRoster(
        normalizeRosterEntries(members, 'firestore').map((entry) => ({
          userId: entry.userId,
          displayName: entry.displayName,
          email: entry.email,
          active: entry.active,
          status: entry.status,
        })),
      );
    };
    void load();
    const onReturn = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      alive = false;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [prest0nSessionAccountId]);
  /** The viewer's own display name for their identity card ('You' stays the label). */
  const selfDisplayLabel = useMemo(() => {
    const uid = (currentUid ?? '').trim();
    const fromRoster = uid ? (senderProfiles[uid]?.label || threadMemberLabels[uid] || '').trim() : '';
    if (fromRoster) return fromRoster;
    const self = getAuth().currentUser;
    return (self?.displayName || self?.email || '').trim();
  }, [currentUid, senderProfiles, threadMemberLabels]);
  /** One identity card open at a time, mirroring the usage-ⓘ single-open rule. */
  // 2026-08-14 owner report ("member popup doesn't really come up"): popup
  // open-state is keyed by STABLE message id, not render index — list churn
  // (sync merges, dedupe passes, notices arriving) shifts indices every few
  // seconds, which detached or instantly closed a just-opened card. Same fix
  // for the ⓘ usage popup; the two are mutually exclusive (they collide).
  const [activeSenderCardId, setActiveSenderCardId] = useState<string | null>(null);

  const threadTags = useThreadTagsController({
    enabled: groupThreadTagsEnabled,
    accountId: prest0nSessionAccountId,
    actorId: currentUid,
    activeThreadId: threadStore.activeThreadId,
    threads: threadStore.threads,
  });

  const visibleThreads = threadTags.visibleThreads;

  // ────────────────────────────────────────────────────────────────────
  // P4: recipient-side thread surfacing (group-threading, Option B).
  //
  // A group thread is owned by its creator; a participant who was ADDED to it
  // holds no record of it in their OWN thread index, so nothing puts it in their
  // sidebar — they can never open it, the P3c overlay never runs, and they see
  // neither the shared transcript nor the "+ add people" affordance. This effect
  // closes the gap: it reads the group threads THIS user participates in (the
  // server-mediated membership reader returns only the caller's own threads) and
  // MATERIALIZES each not-already-local one as a first-class local thread via the
  // same index write guest-adoption uses. The existing open/read pipeline
  // (getThreadMessages → overlay) then fills in the shared transcript, and
  // replies round-trip through the shared store (P3b writes both turns).
  //
  // Gated on the SAME account-scoped READ flag as the overlay, so a real account
  // never surfaces a foreign thread. Additive — no thread-hook internals change.
  // Refs keep the callback identity stable so it runs on mount + tab focus, not
  // on every threads/labels re-render.
  const sharedSurfacedIdsRef = useRef<Set<string>>(new Set());
  // 2026-07-31 multi-device directive: SESSION memory of membership rows whose
  // stub writes were rules-rejected (e.g. the thread is archived server-side,
  // so the create's archived:false stamp is denied). Previously the silent
  // catch retried the same doomed write on every tab focus, forever.
  const sharedSurfaceRejectedIdsRef = useRef<Set<string>>(new Set());
  const sharedSurfaceInFlightRef = useRef(false);
  // 2026-08-08 tab-parity fix: bounded retry after a FAILED membership read
  // (previously a mount-time miss left the tab on the flat one-section layout
  // until the next tab focus — indefinitely for the tab the user is already
  // looking at). Attempt counter resets on every successful read.
  const sharedSurfaceRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sharedSurfaceRetryAttemptRef = useRef(0);
  // The uid whose real server answer THIS tab has applied (null = none) — a
  // stale cache snapshot must never overwrite a live read (stale-while-
  // revalidate), and a PREVIOUS identity's answer must never gate the next
  // one's cache adoption (the component survives in-place user switches).
  const sharedParticipantsServerFreshUidRef = useRef<string | null>(null);
  // Fetch-START stamp of the currently-applied participants map. Orders this
  // tab's own reads against sibling tabs' storage-event snapshots so a slow
  // stale response can never overwrite fresher data (see participantsCache).
  const sharedParticipantsAppliedAtRef = useRef(0);
  const threadsRef = useRef(threadStore.threads);
  threadsRef.current = threadStore.threads;
  const threadMemberLabelsRef = useRef(threadMemberLabels);
  threadMemberLabelsRef.current = threadMemberLabels;
  const surfaceSharedThreads = useCallback(async () => {
    const accountId = prest0nSessionAccountId;
    const selfUid = currentUid;
    if (!groupThreadReadEnabled || !accountId || !selfUid) return;
    if (sharedSurfaceInFlightRef.current) return;
    sharedSurfaceInFlightRef.current = true;
    try {
      let membershipRead = true;
      const membershipFetchStartedAt = Date.now();
      const rows = await (async () => {
        try {
          // refresh() resolves atomically with ITS OWN read's outcome + rows.
          // A transport miss (ok:false) must not read the fail-closed-empty
          // cache as "zero group threads" — that is what used to collapse a
          // populated "group threads" section to the flat layout on one
          // failed fetch — and a concurrent refresh from another consumer
          // (add-people, summon, rename, the panel) can never make a failed
          // read look answered or hide a successful one.
          const read = await threadMembershipSource.refresh();
          if (!read || read.ok !== true) {
            membershipRead = false;
            return [];
          }
          return read.rows ?? [];
        } catch {
          membershipRead = false;
          return []; // fail-closed — a membership read miss surfaces nothing
        }
      })();
      // Auth teardown / in-place user switch raced this pass (the gate above
      // read a stale currentUid): whatever came back is not an answer about
      // selfUid — apply nothing, persist nothing, arm nothing. Without this,
      // a sign-out landing between auth clearing and the React commit let a
      // stale-identity run persist {} over the signed-out user's last-good
      // cache and broadcast the flat layout to sibling tabs.
      if (getAuth().currentUser?.uid !== selfUid) return;
      if (membershipRead) {
        sharedSurfaceRetryAttemptRef.current = 0;
        if (sharedSurfaceRetryTimerRef.current !== null) {
          clearTimeout(sharedSurfaceRetryTimerRef.current);
          sharedSurfaceRetryTimerRef.current = null;
        }
      } else {
        // The read said nothing about real membership — keep the last-known
        // participant/owner maps untouched and retry shortly with backoff
        // instead of waiting for a tab focus that may never come.
        const attempt = sharedSurfaceRetryAttemptRef.current;
        const delayMs = MEMBERSHIP_READ_RETRY_DELAYS_MS[attempt];
        if (delayMs !== undefined && sharedSurfaceRetryTimerRef.current === null) {
          sharedSurfaceRetryAttemptRef.current = attempt + 1;
          sharedSurfaceRetryTimerRef.current = setTimeout(() => {
            sharedSurfaceRetryTimerRef.current = null;
            void surfaceSharedThreadsRef.current?.();
          }, delayMs);
        }
      }
      // Publish the registered-participant thread ids for the shared-store
      // listener's scale guard (see useChatThreads opts.sharedThreadIds). Only
      // on a SUCCESSFUL read — a miss leaves the set null so the listener
      // fails OPEN and live delivery is never suppressed.
      if (membershipRead) {
        setSharedThreadIds(
          new Set(
            rows
              .map((r) => (r?.threadId ?? '').trim())
              .filter(Boolean)
              .concat([...sharedSurfacedIdsRef.current]),
          ),
        );
        const counts: Record<string, number> = {};
        for (const r of rows) {
          const id = (r?.threadId ?? '').trim();
          if (!id) continue;
          const n = Array.isArray(r?.participantIds)
            ? new Set(r.participantIds.map((p) => String(p ?? '').trim()).filter(Boolean)).size
            : 0;
          if (n > 0) counts[id] = n;
        }
        // Tab-parity (2026-08-08): a real server answer is the account-wide
        // truth — persist it so (a) a reload paints the correct sections
        // before its own fetch resolves and (b) the cross-tab storage event
        // converges every sibling tab's sectioning on this read. Recency-
        // gated by fetch-start stamp: if a sibling's FRESHER read was adopted
        // while this one was in flight, this slower answer must not clobber
        // it (and then re-poison the siblings through the cache).
        if (membershipFetchStartedAt >= sharedParticipantsAppliedAtRef.current) {
          setSharedThreadParticipants(counts);
          sharedParticipantsAppliedAtRef.current = membershipFetchStartedAt;
          sharedParticipantsServerFreshUidRef.current = selfUid;
          saveSharedParticipantsCache(
            typeof localStorage === 'undefined' ? null : localStorage,
            config.instanceId,
            selfUid,
            counts,
            membershipFetchStartedAt,
          );
        }
        const owners: Record<string, { createdBy?: string | null }> = {};
        for (const r of rows) {
          const id = (r?.threadId ?? '').trim();
          if (!id) continue;
          owners[id] = { createdBy: (r?.createdBy ?? '').trim() || null };
        }
        setSharedThreadOwners(owners);
        // Only an explicit relaxation is carried; absent reads as "required".
        const summonByThread: Record<string, boolean> = {};
        for (const r of rows) {
          const id = (r?.threadId ?? '').trim();
          if (!id) continue;
          if ((r as { requireMentionToSummon?: unknown })?.requireMentionToSummon === false) {
            summonByThread[id] = false;
          }
        }
        setRequireMentionByThread(summonByThread);
        // Server-resolved participant names (2026-08-26): merged into the
        // label map and the sender profiles as FILL-ONLY — the viewer's own
        // roster read stays authoritative for uids it actually named, so a
        // fresher rename is never demoted; these close the gaps that rendered
        // "Teammate kFZ5" (cross-workspace members, roster-read misses).
        const projectedNames: Record<string, string> = {};
        for (const r of rows) {
          const names = (r as { participantNames?: Record<string, string> })?.participantNames;
          if (!names) continue;
          for (const [nameUid, name] of Object.entries(names)) {
            if (nameUid && name && !projectedNames[nameUid]) projectedNames[nameUid] = name;
          }
        }
        if (Object.keys(projectedNames).length) {
          setThreadMemberLabels((prev) => {
            let changed = false;
            const merged = { ...prev };
            for (const [nameUid, name] of Object.entries(projectedNames)) {
              if (!merged[nameUid]) { merged[nameUid] = name; changed = true; }
            }
            return changed ? merged : prev;
          });
          setSenderProfiles((prev) => {
            let changed = false;
            const merged = { ...prev };
            for (const [nameUid, name] of Object.entries(projectedNames)) {
              if (!merged[nameUid]?.label) {
                merged[nameUid] = { ...(merged[nameUid] ?? {}), label: name };
                changed = true;
              }
            }
            return changed ? merged : prev;
          });
        }
      }
      // 2026-07-31 multi-device directive: "already local" used to mean "in
      // the current TOP-40 working set + this session's in-memory marker", so
      // every real index doc outside the window was re-stubbed
      // (merge-clobbering title/messageCount/recency) on every reload and tab
      // focus. The union below adds the PERSISTED per-(instance, uid) marker
      // so reloads are idempotent; anything still selected is then verified
      // against the server (existence probe) before any write — see
      // materializeSurfacedThreads.
      const surfacedMarkerStorage = typeof localStorage === 'undefined' ? null : localStorage;
      const persistedSurfacedIds = loadSurfacedThreadIds(
        surfacedMarkerStorage,
        config.instanceId,
        selfUid,
      );
      const localIds = new Set<string>([
        ...threadsRef.current.map((t) => t.id),
        ...sharedSurfacedIdsRef.current,
        ...persistedSurfacedIds,
      ]);
      const toSurface = selectThreadsToSurface({
        rows,
        localThreadIds: localIds,
        accountId,
        selfId: selfUid,
      });
      const outcome = await materializeSurfacedThreads({
        rows: toSurface,
        selfId: selfUid,
        instanceId: config.instanceId,
        memberLabels: threadMemberLabelsRef.current,
        storage: surfacedMarkerStorage,
        rejectedThreadIds: sharedSurfaceRejectedIdsRef.current,
        // Replicates writeThreadToServer's durable-tombstone guard: a stub
        // create must never resurrect (or retry against) a thread this user
        // archived. The defer-create guard is inherently satisfied — stubs
        // carry real content (hasUserContent:true), never empty drafts.
        isThreadTombstoned: (threadId) =>
          isThreadTombstonedInStorage(config.instanceId, selfUid, threadId),
        threadIndexDocExists: (threadId) => chatThreadIndexDocExists(selfUid, threadId),
        createThreadStub: (stub) =>
          upsertChatThread(selfUid, config.instanceId, stub, { isCreate: true }),
      });
      // Mark surfaced in-session immediately (created AND confirmed-existing)
      // so a focus re-fire before the index subscription round-trips cannot
      // re-process the same rows.
      for (const threadId of [...outcome.created, ...outcome.skippedExisting]) {
        sharedSurfacedIdsRef.current.add(threadId);
      }
      // Relabel pass (2026-07-23): heal already-surfaced stubs still carrying
      // the raw-uid / generic fallback title — minted before the roster labels
      // resolved (surfacing races the roster load) or before the members-doc
      // rules fix let members read the roster at all. Identically-uid-titled
      // chips made the SAME shared thread read as two different ones in live
      // QA. Pure selection (tested); only degraded titles are ever rewritten,
      // and only once a real display name resolves.
      const relabels = selectStubsToRelabel({
        rows,
        localThreads: threadsRef.current,
        accountId,
        selfId: selfUid,
        memberLabels: threadMemberLabelsRef.current,
      });
      for (const { threadId, title } of relabels) {
        const existing = threadsRef.current.find((t) => t.id === threadId);
        if (!existing || existing.title === title) continue;
        try {
          // M1 producer guard (INCIDENT-20260818): an isCreate:false upsert
          // at a doc that does not exist yet creates an `archived`-less,
          // index-invisible doc. Verify existence first — a missing doc is
          // left for the next surfacing pass, which creates full-shape.
          if (!(await chatThreadIndexDocExists(selfUid, threadId))) continue;
          await upsertChatThread(selfUid, config.instanceId, { ...existing, title }, { isCreate: false });
        } catch {
          // best-effort; retries on the next focus / mount.
        }
      }
    } finally {
      sharedSurfaceInFlightRef.current = false;
    }
  }, [groupThreadReadEnabled, prest0nSessionAccountId, currentUid, threadMembershipSource, config.instanceId]);
  // Stable handle for the failed-read retry timer (the timer must invoke the
  // LATEST callback identity, not the one captured when it was armed).
  const surfaceSharedThreadsRef = useRef(surfaceSharedThreads);
  surfaceSharedThreadsRef.current = surfaceSharedThreads;
  // Live-membership surfacing, pull-to-converge trigger (2026-08-16): when the
  // live index subscription delivers a thread the SERVER marked `sharedThread`
  // that this tab's membership snapshot doesn't know yet (the user was just
  // added — the chip appeared with no reload), refresh the membership read
  // immediately so participant counts, owners, and summon settings converge
  // within seconds instead of waiting for a tab focus that may never come.
  // One request per (uid, thread): the ref remembers requested ids so a
  // registry that lags (or a thread that legitimately left membership) can
  // never drive a refresh loop.
  const membershipRefreshRequestedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!groupThreadReadEnabled || !currentUid) return;
    const unknownFlagged = threadStore.threads.filter(
      (t) =>
        t.sharedThread === true
        && (!sharedThreadIds || !sharedThreadIds.has(t.id))
        && !membershipRefreshRequestedRef.current.has(`${currentUid}:${t.id}`),
    );
    if (unknownFlagged.length === 0) return;
    for (const t of unknownFlagged) {
      membershipRefreshRequestedRef.current.add(`${currentUid}:${t.id}`);
    }
    void surfaceSharedThreadsRef.current?.();
  }, [threadStore.threads, sharedThreadIds, groupThreadReadEnabled, currentUid]);
  // Per-identity presentation state must not survive an in-place user switch
  // (GlobalChatSystem is a persistent mount — the unmount was never the auth
  // boundary): fresh retry budget, fresh cache-adoption eligibility, and no
  // previous identity's counts/owners on screen. Declared BEFORE the cache
  // hydrate effect below so the same commit re-hydrates the new identity's
  // cache (effects run in declaration order).
  const sharedParticipantsIdentityRef = useRef<string | null>(null);
  useEffect(() => {
    const identity = `${config.instanceId}__${currentUid ?? ''}`;
    if (
      sharedParticipantsIdentityRef.current !== null
      && sharedParticipantsIdentityRef.current !== identity
    ) {
      sharedSurfaceRetryAttemptRef.current = 0;
      if (sharedSurfaceRetryTimerRef.current !== null) {
        clearTimeout(sharedSurfaceRetryTimerRef.current);
        sharedSurfaceRetryTimerRef.current = null;
      }
      sharedParticipantsServerFreshUidRef.current = null;
      sharedParticipantsAppliedAtRef.current = 0;
      setSharedThreadParticipants({});
      setSharedThreadOwners({});
      setRequireMentionByThread({});
      setSharedThreadIds(null);
    }
    sharedParticipantsIdentityRef.current = identity;
  }, [config.instanceId, currentUid]);
  // Tab-parity (2026-08-08): hydrate the participant-count map from the last
  // SUCCESSFUL read (stale-while-revalidate — this tab's own fetch overwrites
  // it), and adopt every sibling tab's successful read via the cross-tab
  // storage event, so all tabs of an account render the same "group threads" /
  // "threads" sectioning in real time. Adoption is recency-gated by the
  // payload's fetch-start stamp. Presentation only — authorization reads stay
  // fail-closed.
  useEffect(() => {
    if (!groupThreadReadEnabled || !currentUid) return;
    const storage = typeof localStorage === 'undefined' ? null : localStorage;
    if (sharedParticipantsServerFreshUidRef.current !== currentUid) {
      const cached = loadSharedParticipantsCache(storage, config.instanceId, currentUid);
      if (
        cached
        && Object.keys(cached.counts).length > 0
        && cached.at >= sharedParticipantsAppliedAtRef.current
      ) {
        sharedParticipantsAppliedAtRef.current = cached.at;
        setSharedThreadParticipants(cached.counts);
      }
    }
    const cacheKey = sharedParticipantsCacheKey(config.instanceId, currentUid);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== cacheKey) return;
      const snap = parseSharedParticipantsCache(e.newValue);
      if (snap && snap.at >= sharedParticipantsAppliedAtRef.current) {
        sharedParticipantsAppliedAtRef.current = snap.at;
        setSharedThreadParticipants(snap.counts);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [groupThreadReadEnabled, currentUid, config.instanceId]);
  // The roster label map resolves AFTER the mount surfacing pass (and after
  // stub creation on first surfacing), so re-run once labels land to apply the
  // relabel pass without waiting for the next tab focus. In-flight guard +
  // pure no-op selection make the extra run cheap and idempotent.
  const threadMemberLabelsReady = Object.keys(threadMemberLabels).length > 0;
  useEffect(() => {
    if (!groupThreadReadEnabled || !prest0nSessionAccountId || !currentUid) return;
    if (!threadMemberLabelsReady) return;
    void surfaceSharedThreads();
  }, [groupThreadReadEnabled, prest0nSessionAccountId, currentUid, threadMemberLabelsReady, surfaceSharedThreads]);
  useEffect(() => {
    if (!groupThreadReadEnabled || !prest0nSessionAccountId || !currentUid) return;
    void surfaceSharedThreads();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void surfaceSharedThreads();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      // Failed-read retry must not fire into an unmounted/re-gated surface.
      if (sharedSurfaceRetryTimerRef.current !== null) {
        clearTimeout(sharedSurfaceRetryTimerRef.current);
        sharedSurfaceRetryTimerRef.current = null;
      }
    };
  }, [groupThreadReadEnabled, prest0nSessionAccountId, currentUid, surfaceSharedThreads]);

  // Add-people affordance (P2c) — flag-gated "+ add people" over the account roster,
  // writing through the authorized P2a client. Inert unless the add-people flag is
  // on for this account; never touches the composer/send path.
  const activeThreadRecord = threadStore.threads.find((t) => t.id === threadStore.activeThreadId);
  const canonicalThreadTitle = activeThreadRecord
    && !isDefaultTitle(activeThreadRecord.title)
    && activeThreadRecord.title !== SHARED_THREAD_TITLE_FALLBACK
    ? activeThreadRecord.title
    : undefined;
  const threadAddPeople = useThreadAddPeople({
    accountId: prest0nSessionAccountId,
    activeThreadId: threadStore.activeThreadId,
    selfId: currentUid,
    membershipSource: threadMembershipSource,
    threadTitle: canonicalThreadTitle,
    // Instant-open: the mount-time roster paints the member list immediately;
    // the panel's own load() revalidates in the background.
    seedRoster: addPeopleSeedRoster,
    // Immediate migration on add (owner feedback 2026-08-07): re-run the host
    // loader so the sidebar category, participant count, and @-mention toggle
    // reflect the new member NOW rather than on the next tab-focus refresh.
    onMembershipChanged: surfaceSharedThreads,
  });

  // In-chat member invite (owner directive 2026-08-13): the SAME InviteModal
  // the Team & Roles settings page uses, mounted here so the chat surface can
  // open it without navigating away. Openers: the add-people panel's "invite
  // someone new" affordance, and the invite-member global tool's
  // `chat:open-invite-member` event (detail may carry a prefill email parsed
  // from "invite jane@acme.com"). Permission is enforced server-side by
  // rbacCreateInvitation — an unauthorized send gets the honest error banner
  // inside the modal, never a silent no-op.
  const [inviteModal, setInviteModal] = useState<{ open: boolean; email?: string }>({ open: false });
  useEffect(() => {
    const onOpenInvite = (e: Event) => {
      const detail = (e as CustomEvent).detail as { email?: string } | undefined;
      setInviteModal({ open: true, email: detail?.email });
    };
    window.addEventListener('chat:open-invite-member', onOpenInvite);
    return () => window.removeEventListener('chat:open-invite-member', onOpenInvite);
  }, []);

  // @-mention summon (P2b) — a STABLE fire-and-forget `summonFromText` captured into
  // a ref so the incident-sensitive send wrapper can call it without dep churn. Inert
  // unless the mentions flag is on; it never blocks or alters the message send.
  const threadMentionSummon = useThreadMentionSummon({
    accountId: prest0nSessionAccountId,
    selfId: currentUid,
    membershipSource: threadMembershipSource,
    threadTitle: canonicalThreadTitle,
  });
  const mentionSummonRef = useRef(threadMentionSummon.summonFromText);
  mentionSummonRef.current = threadMentionSummon.summonFromText;

  // W3b: per-thread "@Prest0n mention required" gate. Account-flagged (default
  // OFF), so this is inert everywhere until the allowlist admits an account.
  const threadSummonSetting = useThreadSummonSetting({
    activeThreadId: threadStore.activeThreadId,
    selfId: currentUid,
    accountId: prest0nSessionAccountId,
    membershipSource: threadMembershipSource,
    participantCounts: sharedThreadParticipants,
    requireMentionByThread: requireMentionByThread,
    /**
     * Close the loop after an accepted write. `requireMentionByThread` is
     * rebuilt only by the membership effect below, which a setting write does
     * not re-trigger — so without this the map stayed stale and the toggle
     * visibly flipped back on the next render.
     *
     * Mirrors the projection's own shape: it carries ONLY an explicit
     * relaxation, and absent reads as "required".
     */
    onPersisted: (tid, requireMention) => {
      setRequireMentionByThread((prev) => {
        if (requireMention === false) {
          if (prev[tid] === false) return prev;
          return { ...prev, [tid]: false };
        }
        if (!(tid in prev)) return prev;
        const copy = { ...prev };
        delete copy[tid];
        return copy;
      });
    },
  });
  // Read through a ref inside the send handler so the gate never re-creates it.
  const summonSettingRef = useRef(threadSummonSetting);
  summonSettingRef.current = threadSummonSetting;
  /**
   * Composer placeholder override while the active thread requires an
   * @Prest0n mention (owner feedback 2026-08-07: "It needs to be intuitive and
   * clear to the user when this is on or off"). The placeholder sits exactly
   * where the user is looking when they compose, so it is the strongest
   * ambient signal of the thread's mode. Null ⇒ the configured per-route
   * placeholder applies, so non-gated surfaces are untouched. Follows the
   * low-balance override precedent on the same data-placeholder ternaries.
   */
  const summonMentionPlaceholder =
    threadSummonSetting.gatesThisThread && threadSummonSetting.requireMention !== false
      ? (getUserUiLanguage() === 'es'
          ? 'escribe al grupo — menciona a @Prest0n para que responda…'
          : 'message the group — mention @Prest0n for a reply…')
      : null;

  // ────────────────────────────────────────────────────────────────────
  // Avatar/Platform Bridge (2026-05-23)
  //
  // Registers a handler map on AvatarBridge (the singleton service) so
  // the Prest0n avatar iframe — served from the user's account VM via
  // Caddy at the realtime endpoint (see useRealtimeDesktopTarget.ts for
  // the actual path) — can control the dashboard's thread store and
  // programmatically send messages into a specific thread. Protocol:
  // AVATAR_BRIDGE_PROTOCOL.md.
  //
  // This hook is unconditional: handlers install on every account that
  // has a GlobalChatSystem mounted. Per-account scope is enforced by
  // origin check in AvatarBridge + the avatar VM being user-scoped.
  // The iframe registration (with the actual element reference +
  // allowed-origins list) happens separately in AgentActivity.tsx
  // where the iframe actually lives.
  // ────────────────────────────────────────────────────────────────────
  const canonicalRenameQueueRef = useRef<Promise<void>>(Promise.resolve());
  const renameThreadWithCanonicalSync = useCallback((threadId: string, title: string) => {
    threadStore.renameThread(threadId, title);
    canonicalRenameQueueRef.current = canonicalRenameQueueRef.current.catch(() => undefined).then(async () => {
      const outcome = await createThreadMembership(threadId, undefined, undefined, title);
      if (outcome.ok) await threadMembershipSource.refresh();
    });
  }, [threadStore.renameThread, threadMembershipSource]);

  const bridgeThreadStore: BridgeThreadStore = {
    newThread: threadStore.newThread,
    switchThread: threadStore.switchThread,
    renameThread: renameThreadWithCanonicalSync,
    threads: threadStore.threads,
    activeThreadId: threadStore.activeThreadId,
  };
  const bridgeChat: BridgeChatSystem = { sendProgrammaticMessage };
  useAvatarBridge({
    threadStore: bridgeThreadStore,
    chat: bridgeChat,
    label: config.instanceId,
  });


  // ────────────────────────────────────────────────────────────────────
  // Avatar/Platform Bridge — capture active thread id for broadcastBridgeEvent
  //
  // The workflow handlers (src/services/agenticWorkflows/handlers.ts) need
  // the active thread id to attach to workflow.started / thread.clarifying-question
  // / workflow.progress / workflow.completed events. Those handlers run
  // outside React state, so they can't read threadStore.activeThreadId
  // directly. Instead we call captureActiveThreadId() here whenever the
  // active thread changes, storing it in a module-level variable that
  // broadcastBridgeEvent can read.
  //
  // This was a silent failure point: if AvatarBridge.getActiveThreadId()
  // returned null (e.g. the hook hadn't registered the getter yet), all
  // workflow events were dropped silently. Now broadcastBridgeEvent falls
  // back to this captured value, so events always fire.
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    captureActiveThreadId(threadStore.activeThreadId);
  }, [threadStore.activeThreadId]);

  // ────────────────────────────────────────────────────────────────────
  // Thread-isolation bridge
  //
  // Wire `useGlobalChat`'s thread-isolation refs to the thread store so
  // the streaming pipeline can:
  //   1. capture the originating thread id at send-time,
  //   2. on every chunk/finalize, route the update to the originating
  //      thread — dispatching via React when it's the active thread, or
  //      writing directly to the thread's per-thread localStorage when
  //      the user has switched away mid-stream.
  //
  // Without this bridge, an in-flight stream on thread A will be
  // silently dropped (UPDATE_MESSAGE_BY_ID can't find the id in the
  // active thread's state.messages) the moment the user switches to a
  // different thread — which is exactly the cross-thread interference
  // Miles reproduced on the live demo (5/9/2026).
  // ────────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    const ref = refs.getActiveThreadIdRef;
    if (!ref) return;
    // CROSS-THREAD DELIVERY INCIDENT (owner incognito repros, 2026-08-16):
    // this closure used to capture `threadStore.activeThreadId` — React STATE
    // — so between any thread transition (switchThread / newThread / the
    // BOOT-DEDUPE server-snapshot adoption) and this effect's next run, EVERY
    // capture through the bridge returned the PREVIOUS thread id. A send
    // firing in that window keyed its dispatch (sess_thr_<id>, the durable
    // task payload's threadId, the streaming-helper bundle) to the OLD thread
    // while the pane/mirror already belonged to the new one: the user bubble
    // + auto-title landed in the freshly-minted chip thread and the answer
    // was delivered/persisted into "New thread"; with two concurrent turns, a
    // calendar answer appended into the email thread via the same stale
    // capture in the resume path. Read the store's SYNCHRONOUS accessor
    // (written before every transition's state dispatch) so the bridge can
    // never lag a transition. State fallback kept for test doubles / legacy
    // threadStore shapes that don't provide the sync accessor.
    ref.current = () => (
      threadStore.getActiveThreadIdSync
        ? threadStore.getActiveThreadIdSync()
        : threadStore.activeThreadId
    );
    return () => { ref.current = null; };
  }, [refs.getActiveThreadIdRef, threadStore.getActiveThreadIdSync, threadStore.activeThreadId]);

  // ────────────────────────────────────────────────────────────────────
  // Shared-thread predicate bridge (2026-09-07 owner report: a photo sent
  // to another user in a shared thread vanished when coming back). The send
  // path waits for the FULL attachment upload on a GROUP send — the turn
  // body is what the server copies into the shared doc the other
  // participants read — so it needs the same "is this thread shared" answer
  // the overlay listener uses (useChatThreads: membership snapshot OR the
  // server-owned `sharedThread` index marker). Reads the threads through the
  // ref so ordinary list churn never re-installs the predicate; fail-CLOSED
  // to solo behaviour (the 8 s race + inline fallback) when nothing says
  // shared.
  // ────────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    const ref = refs.isThreadSharedRef;
    if (!ref) return;
    ref.current = (threadId: string) => {
      const id = (threadId || '').trim();
      if (!id) return false;
      if (sharedThreadIds?.has(id)) return true;
      return (threadsRef.current || []).some((t) => t.id === id && t.sharedThread === true);
    };
    return () => { ref.current = null; };
  }, [refs.isThreadSharedRef, sharedThreadIds]);

  // ────────────────────────────────────────────────────────────────────
  // Routine routing hint (2026-08-01 — FIXED backend contract): keep
  // useGlobalTools' threadId → routineId lookup current so EVERY turn
  // dispatched from a routine thread carries requestedWorkflow:'routines',
  // regardless of message text (the "resume"-typed-in-thread production
  // RCA). Mirrors the getActiveThreadIdRef bridge above: the dispatch
  // pipeline runs outside React state, so it reads through a wired
  // callback instead of threadStore directly. Guarded — legacy test
  // doubles may omit the setter.
  // ────────────────────────────────────────────────────────────────────
  const setThreadRoutineLookup = globalTools?.setThreadRoutineLookup;
  React.useEffect(() => {
    if (!setThreadRoutineLookup) return;
    const threads = threadStore.threads;
    setThreadRoutineLookup((threadId: string) =>
      threads.find((t) => t.id === threadId)?.routineId || null);
    return () => setThreadRoutineLookup(null);
  }, [setThreadRoutineLookup, threadStore.threads]);

  // Personalized greeting → mount welcome injector bridge: the once-per-mount
  // welcome in useGlobalChat uses the SAME composer, so the bootstrap thread's
  // persisted welcome is personalized too (static config copy remains the
  // fallback whenever the composer is absent or yields null). Guarded because
  // legacy test doubles may omit the ref.
  React.useEffect(() => {
    const ref = refs.composeGreetingRef;
    if (!ref) return;
    ref.current = composeGreeting;
    return () => { ref.current = null; };
  }, [refs.composeGreetingRef, composeGreeting]);

  // One-click empty-delete signal (USER messages only, personalized greeting
  // 2026-07-13): a greeting-seeded draft shows one assistant message yet
  // holds nothing of the user's — its chip must stay one-click deletable.
  const activeThreadUserMessageCount = useMemo(
    () => state.messages.reduce((n, m) => n + (m.isUser ? 1 : 0), 0),
    [state.messages],
  );

  React.useEffect(() => {
    const writeRef = refs.writeThreadMessagesRef;
    const readRef = refs.readThreadMessagesRef;
    if (!writeRef || !readRef) return;
    if (!threadSwitcherEnabled) {
      writeRef.current = null;
      readRef.current = null;
      return;
    }
    const keys = getThreadStorageKeys(config.instanceId, currentUid);
    const msgKey = (threadId: string) => `${keys.THREAD_MSG_PREFIX}${threadId}`;
    // PR #112 (Miles 2026-05-10): route every writeRef-driven per-thread
    // persist through `safelyPersistThreadMessages`. Previously this lambda
    // called `setToLocalStorage` directly, bypassing the PR #110 guards
    // (empty-over-non-empty block, suspicious-shrink, quota → IDB,
    // BroadcastChannel). That parallel path is what produced Miles's
    // 2026-05-10 23:10 UTC cross-thread orphan-bubble screenshot in the
    // "Pitch Deck De Prest…" thread.
    writeRef.current = (threadId, messages) => {
      void safelyPersistThreadMessages({
        instanceId: config.instanceId,
        threadId,
        uid: currentUid,
        messages,
        source: 'write-ref-routed',
        // Session-hardening S1: write-ref persists are THIS view's writes
        // (addMessage sync-persist, finalize, queue drain). Without the token
        // they self-notified the view's own thread-sync subscription, which
        // re-dispatched storage over the pane — the exact step that wiped the
        // welcome message at first send (#501 tokened only hook-internal
        // persists; this closes the gap).
        writerToken: threadStore.threadSyncWriterToken,
      });
      // Durability audit #15 (2026-07-14): routed background/cross-thread
      // finalizes were localStorage-only — the finished turn never reached
      // Firestore until the user happened to re-activate the thread on THIS
      // device, so every other device/session stayed permanently blind to
      // it. Fire-and-forget mirror to the server transcript; the per-thread
      // localStorage write above remains the pane's source of truth.
      if (currentUid && Array.isArray(messages) && messages.length > 0) {
        void upsertThreadMessages(currentUid, config.instanceId, threadId, messages).catch((error) => {
          // eslint-disable-next-line no-console
          console.warn('[GlobalChatSystem] background-thread server sync failed', error);
        });
      }
    };
    readRef.current = (threadId) => {
      try {
        const raw = getFromLocalStorage<ChatMessage[]>(msgKey(threadId), []);
        return raw.map(m => ({
          ...m,
          timestamp: m.timestamp instanceof Date
            ? m.timestamp
            : new Date(m.timestamp as unknown as string),
        }));
      } catch {
        return [];
      }
    };
    return () => {
      writeRef.current = null;
      readRef.current = null;
    };
  }, [refs.writeThreadMessagesRef, refs.readThreadMessagesRef, config.instanceId, currentUid, threadSwitcherEnabled, threadStore.threadSyncWriterToken]);

  // Pending thread-delete request — the bar emits a request when the user
  // clicks the (hidden-until-hover) × on a chip. We stash the target id and
  // open a ConfirmationModal, so nothing is deleted without an explicit
  // second confirmation. Also: the thread's messages are archived (not
  // permanently deleted) by `threadStore.removeThread`.
  const [pendingDeleteThreadId, setPendingDeleteThreadId] = useState<string | null>(null);
  const pendingDeleteThread = pendingDeleteThreadId
    ? threadStore.threads.find(t => t.id === pendingDeleteThreadId) || null
    : null;
  const handleRequestDeleteThread = useCallback((threadId: string) => {
    setPendingDeleteThreadId(threadId);
  }, []);

  // RECONCILIATION (#909 vs #925, 2026-08-19): #909 removed the combined
  // "all workspaces" view under the 2026-08-18 strict-containment directive;
  // the owner's NEWER 2026-08-19 directive (screenshot of the live combined
  // view + "when the user clicks into a thread that's in a different
  // workspace … a smooth message … minimum of 3 seconds") redesigns the
  // extended view instead of removing it. Containment still holds in full:
  // a foreign-workspace thread NEVER opens in place — every path routes
  // through the SEALED transition (openForeignThread above: overlay,
  // verified switch, pending-open marker, hard navigation), and the thread
  // renders only inside its own organization's portal after the reload.
  // The pending-open consumption below serves that flow and any
  // prior-session marker.

  // Consume the pending-open marker once threads are ready: activate the
  // thread the user asked for before the switch. Marker TTL + one-shot
  // consume make a crashed switch harmless.
  const pendingOpenConsumedRef = useRef(false);
  useEffect(() => {
    if (pendingOpenConsumedRef.current || !threadStore.ready) return;
    const pendingId = consumePendingThreadOpen();
    pendingOpenConsumedRef.current = true;
    if (pendingId && threadStore.threads.some((t) => t.id === pendingId)) {
      threadStore.switchThread(pendingId);
    }
  }, [threadStore.ready, threadStore.threads, threadStore.switchThread]);
  const handleCancelDeleteThread = useCallback(() => {
    setPendingDeleteThreadId(null);
  }, []);
  // T5a (thread-workflow UX): transient archive confirmation — the chip
  // vanishing must never be the only feedback. Fired from BOTH archive paths
  // (confirmed + one-click empty) BEFORE removal so the title is still known.
  const [archiveToast, setArchiveToast] = useState<string | null>(null);
  const archiveToastTimerRef = useRef<number | null>(null);
  const showArchiveToast = useCallback((threadId: string) => {
    const t = threadStore.threads.find((x) => x.id === threadId);
    const raw = (t?.title || '').trim();
    const name = raw ? `“${raw.slice(0, 32)}${raw.length > 32 ? '…' : ''}”` : 'Conversation';
    setArchiveToast(`${name} archived — Prest0n keeps it in memory for reference.`);
    if (archiveToastTimerRef.current) window.clearTimeout(archiveToastTimerRef.current);
    archiveToastTimerRef.current = window.setTimeout(() => setArchiveToast(null), 2600);
  }, [threadStore]);
  useEffect(() => () => {
    if (archiveToastTimerRef.current) window.clearTimeout(archiveToastTimerRef.current);
  }, []);
  // PASSING shared-archive notices (owner directive 2026-08-26): a group
  // thread archived by someone else while this user was in a different
  // thread surfaces as a clear line in the toast band, instead of the row
  // silently disappearing from the rail. Blocking notices render as the
  // full-screen overlay component; this effect only carries the quiet kind.
  useEffect(() => subscribeThreadArchiveNotice((notice) => {
    if (!notice || notice.kind !== 'passing') return;
    const who = (notice.archivedByName || '').trim() || 'a teammate';
    const title = (notice.threadTitle || '').trim();
    setArchiveToast(
      title
        ? `${who} archived “${title}” for everyone — Prest0n keeps it in memory.`
        : `${who} archived a group thread for everyone — Prest0n keeps it in memory.`,
    );
    if (archiveToastTimerRef.current) window.clearTimeout(archiveToastTimerRef.current);
    archiveToastTimerRef.current = window.setTimeout(
      () => setArchiveToast(null),
      THREAD_ARCHIVE_PASSING_NOTICE_MS,
    );
    clearThreadArchiveNotice(notice.threadId);
  }), []);

  /**
   * Shared-archive fanout (owner directive 2026-08-26): archiving a GROUP
   * thread archives it for every member, forever. The server stamps the
   * permanent flag on the shared registry — which is what stops the thread
   * ever re-surfacing in anyone's rail — and fans archived:true to each
   * member's own index doc, so their rails update live. Fire-and-forget
   * beside the local archive (the local path is this user's immediate UX
   * either way); one retry covers a transient miss, and a definitive server
   * refusal is left alone — the local archive still stands for this user,
   * exactly as it did before shared archives existed.
   */
  const archiveSharedThreadForEveryone = useCallback((threadId: string) => {
    const isShared =
      (sharedThreadParticipants[threadId] ?? 0) >= 2
      || threadStore.threads.some((t) => t.id === threadId && t.sharedThread === true);
    if (!isShared) return;
    void (async () => {
      try {
        // Retry semantics (2026-08-27 review fix): only a 2xx acceptance or a
        // DEFINITIVE server decision (a 4xx policy refusal) ends the attempt.
        // The old `status > 0` treated a 429/500/503 as final — a transient
        // blip and the group-wide archive the user was just promised silently
        // never happened. Transients and network misses retry on a short
        // backoff; exhaustion says so instead of staying quiet.
        const definitive = (o: { ok: boolean; status: number }): boolean =>
          o.ok || (o.status >= 400 && o.status < 500 && o.status !== 429);
        let outcome = await archiveGroupThreadForEveryone(threadId);
        for (const delay of [2_000, 8_000]) {
          if (definitive(outcome)) break;
          await new Promise((resolve) => window.setTimeout(resolve, delay));
          outcome = await archiveGroupThreadForEveryone(threadId);
        }
        if (!outcome.ok) {
          // The archive stands for THIS user (local path already ran); the
          // others still see the thread. Say so — a silent half-archive is
          // how "it came back for everyone else" reports start.
          setArchiveToast(
            'archived for you — but the archive for the rest of the group didn\u2019t go through. open the thread again and re-archive to retry.',
          );
          if (archiveToastTimerRef.current) window.clearTimeout(archiveToastTimerRef.current);
          archiveToastTimerRef.current = window.setTimeout(() => setArchiveToast(null), 6_000);
        }
      } catch { /* the local archive already stands for this user */ }
    })();
  }, [sharedThreadParticipants, threadStore.threads]);

  const handleConfirmDeleteThread = useCallback(() => {
    if (pendingDeleteThreadId) {
      // Same-thread queue cleanup (PR feat/same-thread-queue-while-streaming):
      // drop every pending entry for the thread we're about to delete and
      // abort any in-flight orchestrator on it. Without this, deleting a
      // thread mid-stream + mid-queue would leak the in-flight stream
      // (no thread to write its chunks back to) and would still drain
      // queued entries against a thread that no longer exists.
      try {
        dispatch({ type: 'REMOVE_PENDING_FOR_THREAD', payload: { threadId: pendingDeleteThreadId } });
      } catch { /* ignore */ }
      try {
        if (globalTools?.cancelRunningTool) {
          // F1 (2026-08-03): archive/delete is NOT a user Stop — never stamp
          // a "stopped" bubble (into this thread or, via the null-key
          // fallback, any other thread's live turn).
          globalTools.cancelRunningTool(pendingDeleteThreadId, 'thread_archived');
        }
      } catch { /* ignore */ }
      // Finding 8c (2026-07-15): disarm the archived thread's send-ack
      // watchdog so a pending fire can't append a retry bubble into (or
      // hold the queue of) a thread that no longer exists.
      try { cancelThreadSendWatchdog(pendingDeleteThreadId); } catch { /* ignore */ }
      showArchiveToast(pendingDeleteThreadId);
      archiveSharedThreadForEveryone(pendingDeleteThreadId);
      threadStore.removeThreadAndReturnFallback(pendingDeleteThreadId);
      // Keep the global chat surface open after archive and let the
      // thread store land on the most-recent remaining thread (or a
      // fresh localized default if this was the last thread).
      dispatch({ type: 'SET_CHAT_OPEN_PRIORITY', payload: true });
    }
    setPendingDeleteThreadId(null);
  }, [pendingDeleteThreadId, threadStore, dispatch, globalTools, showArchiveToast, archiveSharedThreadForEveryone, cancelThreadSendWatchdog]);

  // Empty-thread one-click delete (Miles 2026-05-10):
  // The ThreadSwitcherBar invokes this directly when the user clicks
  // × on a chip whose thread has zero messages. Skipping the
  // confirmation modal is intentional — there is nothing to
  // accidentally lose. Same in-flight cleanup as the confirmed-delete
  // path runs (defensively): an empty thread shouldn't have a
  // pending queue or running tool, but if a brand-new thread fired a
  // request and the user closes it before any message lands, we
  // still want the orchestrator wound down cleanly.
  const handleDeleteEmptyThread = useCallback((threadId: string) => {
    if (!threadId) return;
    // A SHARED thread never takes the one-click shortcut (2026-08-27 review
    // fix): "empty" here means empty FOR THIS VIEWER (history floor, unloaded
    // transcript), and archiving now closes the thread for every member —
    // that deserves the confirm modal's plain warning, same as the row ×.
    const isShared =
      (sharedThreadParticipants[threadId] ?? 0) >= 2
      || threadStore.threads.some((t) => t.id === threadId && t.sharedThread === true);
    if (isShared) {
      handleRequestDeleteThread(threadId);
      return;
    }
    try {
      dispatch({ type: 'REMOVE_PENDING_FOR_THREAD', payload: { threadId } });
    } catch { /* ignore */ }
    try {
      if (globalTools?.cancelRunningTool) {
        // F1 (2026-08-03): same as the confirmed-delete path — not a user Stop.
        globalTools.cancelRunningTool(threadId, 'thread_archived');
      }
    } catch { /* ignore */ }
    // Finding 8c (2026-07-15): same watchdog teardown as the confirmed path.
    try { cancelThreadSendWatchdog(threadId); } catch { /* ignore */ }
    showArchiveToast(threadId);
    // Empty for THIS user can still be a real shared thread for the others
    // (their history floor differs) — the shared archive applies here too.
    archiveSharedThreadForEveryone(threadId);
    threadStore.removeThreadAndReturnFallback(threadId);
    dispatch({ type: 'SET_CHAT_OPEN_PRIORITY', payload: true });
  }, [threadStore, dispatch, globalTools, showArchiveToast, archiveSharedThreadForEveryone, sharedThreadParticipants, handleRequestDeleteThread, cancelThreadSendWatchdog]);

  // Round 3 (2026-08-01): the header rail's archive button routes EXACTLY
  // like the row × — the SAME shouldRouteCloseToEmptyDelete predicate, the
  // SAME handlers (one-click for empty drafts, ConfirmationModal →
  // removeThreadAndReturnFallback for real content — which then lands the
  // user on the fallback thread). One decision rule, three surfaces (chip ×,
  // sidebar row ×, header button); they can never diverge.
  const handleHeaderArchiveActiveThread = useCallback((threadId: string) => {
    const routeToEmptyDelete = shouldRouteCloseToEmptyDelete({
      threadId,
      activeThreadId: threadStore.activeThreadId,
      threads: threadStore.threads,
      activeThreadMessageCount: activeThreadUserMessageCount,
      hasEmptyDeleteHandler: true,
    });
    if (routeToEmptyDelete) {
      handleDeleteEmptyThread(threadId);
      return;
    }
    handleRequestDeleteThread(threadId);
  }, [
    threadStore.activeThreadId,
    threadStore.threads,
    activeThreadUserMessageCount,
    handleDeleteEmptyThread,
    handleRequestDeleteThread,
  ]);

  // Payment method status for dynamic popup behavior
  const paymentMethodStatus = usePaymentMethodStatus();

  // In-place top-up modal (non-throwing: may be absent in tests / non-app hosts)
  const topUp = useMaybeTopUp();

  // State for critical balance popup
  const [showCriticalBalancePopup, setShowCriticalBalancePopup] = useState(false);

  // Ref to track if low balance system message has been shown this session
  const lowBalanceMessageShownRef = useRef(false);

  const {
    isChatOpen,
    isChatDockedRight,
    isChatFullScreen,
    messages,
    prompt,
    attachments,
    isAttachmentPanelVisible,
    isDragging,
    isMobileViewport,
    showClearConfirmModal,
  } = state;

  // ── Render-storm tripwire (OOM open incident, Miles 2026-07-12) ──────────
  // Runs on EVERY render, including hidden persistent mounts. One-shot: the
  // first time the render rate exceeds any legitimate animation ceiling it
  // emits a single structured console.error, so an "Aw, Snap! Out of Memory"
  // crash leaves a screenshotable trace of what was looping instead of dying
  // silently. Ref mutation during render is deliberate — no state, no effect,
  // nothing that could itself feed a loop.
  const renderStormRef = useRef<RenderStormState | null>(null);
  if (renderStormRef.current === null) renderStormRef.current = createRenderStormState();
  if (trackRenderStorm(renderStormRef.current, Date.now())) {
    // eslint-disable-next-line no-console
    console.error(
      '[prest0n-chat] RENDER-STORM tripwire fired — a render loop is running; investigate before OOM',
      buildRenderStormDiagnostics({
        state: renderStormRef.current,
        route: typeof window !== 'undefined' ? window.location.pathname : '',
        viewMode: [
          isChatOpen ? 'open' : 'closed',
          config.hidden ? 'hidden' : null,
          isChatFullScreen ? 'fullscreen' : (isChatDockedRight ? 'docked' : 'floating'),
          isMobileViewport ? 'mobile' : null,
        ].filter(Boolean).join('/'),
        threadCount: threadStore.threads.length,
        activeThreadId: threadStore.activeThreadId,
        messageCount: messages.length,
      }),
    );
  }

  // ── Pane pending-overlay gate ─────────────────────────────────────────────
  // 2026-07-14 incident fix: the overlay previously ALSO required
  // `processingByThread[activeThreadId]` — so any mid-turn flag release
  // (watcher release, empty-content finalize, tool onError, not-handled
  // fall-through) blanked the pane AND every chip spinner simultaneously
  // while the extracted widget stayed silently wedged in state ("nothing
  // spinning, nothing came back"). The overlay is CONTENT: it renders
  // whenever a pending-shaped message exists in the active transcript,
  // independent of the rail flag. Rationale + awaiting-input suppression:
  // pendingOverlayGate.ts.
  const paneOverlayVisible = shouldRenderPendingOverlay(
    true, // combined with `pendingOverlay &&` at the render site
    state.inputState.status,
  );

  // Publish the chat panel's open/closed state to the module store so the
  // app-level GlobalLowBalanceBanner (a sibling, not a descendant) can suppress
  // its top strip while the chat shows its own low-balance banner.
  useEffect(() => {
    setChatPanelOpen(isChatOpen);
  }, [isChatOpen]);
  useEffect(() => () => setChatPanelOpen(false), []);

  // Publish the FULLSCREEN takeover on the same bus (2026-08-31 banner
  // consistency directive). The takeover is position:fixed/top:0 over the
  // whole viewport, so it covers the in-flow banner band; GlobalBannerCenter
  // reads this to pin the band above it — "at the very very top of the
  // page", never inside the chat interface. `config.hidden` joins the
  // condition for the same reason it does on the dock signal: a hidden-route
  // mount must not claim the viewport.
  const chatFullScreenActive = isChatOpen && isChatFullScreen && !config.hidden;
  useEffect(() => {
    setChatPanelFullScreen(chatFullScreenActive);
  }, [chatFullScreenActive]);
  useEffect(() => () => setChatPanelFullScreen(false), []);

  // PAGE-RECENTER SIGNAL (live app-wide — Miles 2026-08-24; prepared
  // 2026-07-13): stamp data-prest0-chat-mode="docked" on <html> while the
  // chat is OPEN as the docked right panel (and remove it otherwise). The
  // app shell (RoutedContentShell in App.tsx) keys its content reflow on
  // this attribute, so page content lays out in the remaining space on the
  // left exactly while the side panel is actually visible. isChatOpen and
  // !config.hidden joined the condition when the reflow shipped: a
  // closed-but-docked pill or a hidden-route mount must never squeeze the
  // page. (!isChatFullScreen is belt-and-suspenders: the reducer already
  // clears the dock flag when entering fullscreen.)
  useChatModeDocumentSignal(
    isChatOpen && isChatDockedRight && !isChatFullScreen && !config.hidden,
  );

  // Fullscreen page-scroll lock (2026-08-12 scrolling round): while the chat
  // covers the viewport, the page BEHIND it must not scroll — wheel/touch
  // past the thread's edges used to chain into the page and carry the whole
  // interface away. Reference-counted so preview overlays opened on top of
  // fullscreen keep the page locked until the LAST layer closes.
  useBodyScrollLock(isChatOpen && isChatFullScreen);

  // Docked density round (owner feedback, Miles 2026-08-01): ONE "is the
  // chat the docked right panel?" bit that the header two-line contract, the
  // ⋯ overflow rail, the chip-strip suppression, and the compact routine
  // meta all key off (the pure rules live in dockedDensity.ts). Mode-driven,
  // never page-specific — the same persistent mount serves every page
  // (chatSurfaceUniformity), so the density applies platform-wide.
  const dockedPanelDensity = isDockedPanelMode(isChatDockedRight, isChatFullScreen);

  // Mobile rail density (mobile UX round, 2026-08-14): phones get the same
  // 3-buttons-max header rail as the docked panel — [⋯ overflow][mode-cycle]
  // [hide] — because five 40px touch targets plus the title cannot share a
  // fold-phone header. Rail-only bit: every other docked-density decision
  // (timestamps, participants line, chip-strip suppression) stays keyed on
  // dockedPanelDensity alone — on mobile the chip strip is the ONLY thread
  // surface and must never be suppressed.
  const headerRailDensity = dockedPanelDensity || isMobileViewport;

  // Thread-list sheet/panel (mobile UX round 2026-08-14; docked form
  // 2026-08-24): vertical, finger-sized thread switching — the chip strip
  // stays as the compact always-visible surface, this is its expanded form.
  // Two hosts now share it: mobile wears it as the bottom SHEET (trigger at
  // ≥2 threads, unchanged), and the docked right panel wears it as an
  // anchored drop-up PANEL whose trigger appears only while the strip
  // actually overflows (more threads than fit — the strip's own measured
  // overflow bit, see onOverflowChange). The trigger renders beside the
  // strip and the list body-portals in both forms.
  const [threadSheetOpen, setThreadSheetOpen] = useState(false);
  // Anchor for the docked panel form, measured from the trigger at open
  // time (HeaderOverflowMenu idiom): offsets from the viewport's right and
  // bottom edges, so the panel rises from just above the ☰.
  const [threadSheetAnchor, setThreadSheetAnchor] = useState<{ right: number; bottom: number } | null>(null);
  // "More chips than fit" — reported by ThreadSwitcherBar from the same
  // geometry its ‹/› pills read; false whenever the strip is absent.
  const [threadStripOverflowing, setThreadStripOverflowing] = useState(false);
  useEffect(() => {
    // Leaving BOTH host modes (mobile sheet / docked panel) or closing the
    // chat retires the list — a takeover dismissal must not strand it open,
    // and the floating pane has no form of it at all.
    if (((!isMobileViewport && !dockedPanelDensity) || !isChatOpen) && threadSheetOpen) {
      setThreadSheetOpen(false);
    }
  }, [isMobileViewport, dockedPanelDensity, isChatOpen, threadSheetOpen]);

  const {
    handleSend,
    handleInput,
    handlePaste,
    handleKeyDown,
    handleToggleDock,
    handleToggleFullScreen,
    handleCycleMode,
    handleFileSelect,
    handleFileChange,
    handleRemoveAttachment,
    handleClearAttachments,
    handleAttachmentButtonClick,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleHide,
    handleOpen,
    getCurrentPlainText,
    handlePromptFocus,
    handlePromptBlur,
    handleClearChat,
    confirmClearChat,
    hideClearConfirmation,
  } = handlers;

  // ────────────────────────────────────────────────────────────────────
  // Suggestion → NEW THREAD + IMMEDIATE SEND (2026-08-18 owner feedback):
  // a bubble that offers a ready-to-go message (the post-connect done
  // card's "try: …" first action, the connect concierge's ask) must open a
  // fresh thread and SEND it — never park it as a composer draft the user
  // has to submit themselves. Dispatchers fire a cancelable
  // `prest0n:chat-new-thread-send` with { text, title?, forceDocked? };
  // preventDefault() is the "a chat system handled it" handshake, so
  // surfaces without a mounted chat fall back to the old globalchat:open
  // draft path instead of losing the message. Mirrors the avatar bridge's
  // mint→rename→programmatic-send sequence (newThread switches to the
  // fresh thread synchronously; the send-time capture reads
  // getActiveThreadIdSync, so the send lands in the new thread).
  //
  // Dock side: THERE IS NO DOCK SIDE. The chat overlay anchors RIGHT on
  // every open path, current and future (owner directive 2026-08-20; see
  // CHAT_LAYOUT.DOCK_SIDE). Concierge flows may pass forceDocked so the
  // page stays visible BESIDE the pane — beside always means to its left,
  // pane on the right. Any `dock` field a producer passes is ignored here,
  // by design: this handler is the chokepoint every programmatic open
  // passes through, so a future left-dock cannot be reintroduced without
  // editing this comment.
  // ────────────────────────────────────────────────────────────────────
  // Hidden-mount guard ref (owner repro 2026-08-20, onboarding): the
  // persistent mount keeps every hook alive on excluded routes while the
  // component renders null — the listener below must know it is invisible.
  const chatHiddenRef = useRef(false);
  chatHiddenRef.current = !!config.hidden;

  const newThreadSendRef = useRef<(text: string, title: string, forceDocked?: boolean) => void>(() => undefined);
  newThreadSendRef.current = (text: string, title: string, forceDocked?: boolean) => {
    if (forceDocked) {
      // The page-stays-visible promise needs DOCKED geometry — floating/
      // center would cover the page. Force dock on (never toggling an
      // already-docked pane off); fullscreen keeps fullscreen.
      if (!isChatDockedRight && !isChatFullScreen) handleToggleDock();
    }
    // ORG STICKINESS / bind-at-birth (INCIDENT-20260818): a programmatic
    // create firing before the workspace resolution publishes would mint an
    // UNBOUND thread (or, under the old uid fallback, a mis-bound one).
    // Signed-in users' creates QUEUE until the resolution lands — a one-shot
    // listener replays them; guests (no workspace to wait for) proceed.
    const signedIn = Boolean(currentUid);
    if (signedIn && !getActiveWorkspaceAccount()) {
      const off = onActiveWorkspaceResolved(() => {
        off();
        newThreadSendRef.current(text, title, forceDocked);
      });
      console.warn('[GlobalChatSystem] new-thread-send queued until workspace resolution');
      return;
    }
    const threadId = threadStore.newThread();
    try { renameThreadWithCanonicalSync(threadId, title); } catch { /* title is cosmetic */ }
    Promise.resolve()
      .then(() => sendProgrammaticMessage(text, { hidden: false }))
      .catch((err) => {
        console.error('[GlobalChatSystem] new-thread-send dispatch failed:', err);
      });
  };
  React.useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ text?: string; title?: string; forceDocked?: boolean }>;
      const text = (ce.detail?.text || '').trim();
      if (!text) return;
      // HIDDEN-MOUNT GUARD (owner repro 2026-08-20, onboarding): on excluded
      // routes the chat is mounted-but-null, yet this listener still claimed
      // the concierge handshake — preventDefault() made the dispatcher think
      // a chat took it, so it skipped its fallback (the direct desktop
      // sign-in surface) while THIS handler minted an INVISIBLE thread and
      // burned a real orchestrator turn the user could never see. Leave the
      // event uncancelled: requestConnectConcierge returns false and the
      // caller opens a VISIBLE surface instead.
      if (chatHiddenRef.current) return;
      ce.preventDefault();
      const title = (ce.detail?.title || text).trim().slice(0, 60);
      // Open the pane through the channel useGlobalChat already owns.
      document.dispatchEvent(new CustomEvent('globalchat:open', { detail: {} }));
      // NOTE: a legacy `dock` field in the detail is deliberately ignored —
      // the overlay anchors right, always (owner directive 2026-08-20).
      newThreadSendRef.current(text, title, ce.detail?.forceDocked === true);
    };
    document.addEventListener('prest0n:chat-new-thread-send', handler);
    return () => document.removeEventListener('prest0n:chat-new-thread-send', handler);
  }, []);

  // ────────────────────────────────────────────────────────────────────
  // OPEN THE CHAT FOR A THING (docked-companion round, 2026-08-24): a
  // surface outside the chat's React tree — today the file previewer —
  // asks for "Prest0n beside me, on the conversation this file came from".
  // Sibling of the new-thread-send handler above and follows its rules
  // exactly: cancelable handshake, hidden-mount guard, and NO dock side
  // (forceDocked means the RIGHT rail; the requester keeps the left).
  // Unlike that one this NEVER mints a thread and never sends a turn — it
  // is pure view state, so a mistaken request costs nothing.
  // ────────────────────────────────────────────────────────────────────
  const openChatThreadRef = useRef<(detail: ChatOpenThreadDetail) => void>(() => undefined);
  openChatThreadRef.current = (detail: ChatOpenThreadDetail) => {
    if (detail.forceDocked) {
      // Same idiom as newThreadSendRef: force dock ON, never toggle an
      // already-docked pane off; fullscreen stays fullscreen (it is already
      // the whole screen, so there is no "beside" to give). MOBILE NEVER
      // DOCKS — the reducer turns this into the fullscreen takeover.
      if (!isChatDockedRight && !isChatFullScreen) handleToggleDock();
    }
    const targetThreadId = detail.threadId;
    if (!targetThreadId || targetThreadId === threadStore.activeThreadId) return;
    // Same two-step every other switch site uses (rail chips, sidebar rows):
    // the outgoing thread's composer draft is flushed before we leave it.
    flushCurrentDraftNow(threadStore.activeThreadId);
    threadStore.switchThread(
      targetThreadId,
      detail.fallbackTitle ? { fallbackTitle: detail.fallbackTitle } : undefined,
    );
  };
  React.useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<ChatOpenThreadDetail>;
      // HIDDEN-MOUNT GUARD (same reasoning as the sibling handler): on
      // excluded routes the chat is mounted-but-null, so claiming the
      // handshake would tell the caller a chat is beside them when none is
      // visible. Leave it uncancelled and let them fall back.
      if (chatHiddenRef.current) return;
      ce.preventDefault();
      // Open the pane through the channel useGlobalChat already owns.
      document.dispatchEvent(new CustomEvent('globalchat:open', { detail: {} }));
      openChatThreadRef.current(ce.detail || {});
    };
    document.addEventListener(CHAT_OPEN_THREAD_EVENT, handler);
    return () => document.removeEventListener(CHAT_OPEN_THREAD_EVENT, handler);
  }, []);

  // ────────────────────────────────────────────────────────────────────
  // AUTHORIZED-SEND FAILURE → PREST0N EXPLAINS (owner directive
  // 2026-08-19: "if not, Prest0n needs to be initiated again to inform the
  // user why or what next steps are"). The authorize click handler posts a
  // durable outcome message for EVERY decision and dispatches
  // `prest0n:action-decided` (a channel that previously had no listener).
  // On a FAILED send, this listener runs a hidden Prest0n turn carrying the
  // real error, so the model explains what happened and decides the next
  // step itself (e.g. the right connect offer) — the user never has to ask
  // "did it send?". Success posts no extra turn — the sent ✓ record is the
  // confirmation. Debounced once per actionId per page load.
  // ────────────────────────────────────────────────────────────────────
  const actionFailureExplainedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        actionId?: string; decision?: string; success?: boolean; delivered?: boolean;
        outcome?: string; status?: string; error?: string; to?: string; subject?: string;
        decidedAt?: string;
      } | undefined;
      if (!detail || detail.success !== false) return;
      if (detail.decision !== 'authorize') return;
      // ONE VOICE PER EVENT (owner report 2026-08-24: "it's a bit redundant
      // and doesn't really need to be this way"). The receipt card already
      // states the outcome, the reason and the next step in writing. A second
      // turn is worth its spinner only when Prest0n can take an action the
      // card cannot — see sendOutcomePolicy. When it cannot, running it
      // anyway bought a twenty-second spinner, a paragraph restating the
      // card, and a fabricated "Gmail is connected".
      if (!failureNeedsExplainTurn(detail.outcome)) return;
      const actionId = String(detail.actionId || '').trim();
      // Keyed per DECISION, not per action: a re-armed draft (2026-08-24) can
      // be authorized again, and an action-only key made the second failure
      // look like a duplicate of the first — so the attempt the user was
      // actually watching got no explanation at all.
      const decisionKey = `${actionId}:${String(detail.decidedAt || '')}`;
      if (!actionId || actionFailureExplainedRef.current.has(decisionKey)) return;
      actionFailureExplainedRef.current.add(decisionKey);
      const target = [detail.to, detail.subject ? `"${detail.subject}"` : '']
        .filter(Boolean).join(', ');
      // UNCONFIRMED IS NOT FAILED (2026-08-20): when the provider returned no
      // confirmation, or the click never reached the server, the email's fate
      // is genuinely unknown — telling the model it "did NOT go out" would
      // trade one false certainty for another, and a confident resend is how
      // the same email goes out twice.
      const unknownFate = detail.outcome === 'unconfirmed' || detail.outcome === 'unreachable';
      void sendProgrammaticMessage(
        unknownFate
          ? `[system] The email send I just authorized${target ? ` (${target})` : ''} could NOT be confirmed — `
            + `the platform reported: ${detail.error || detail.status || 'no confirmation'}. `
            + `Tell me plainly that you cannot say whether it went out, and give the single next step. `
            + `Offer to check my Sent folder before anything is sent again. Never claim it was sent, and `
            + `never claim it failed.`
          : `[system] The email send I just authorized${target ? ` (${target})` : ''} did NOT go out — `
            + `the platform reported: ${detail.error || detail.status || 'unknown error'}. `
            + `Explain to me plainly what happened and give the single next step. If an app connection `
            + `caused it, decide the right fix yourself. Do not re-draft the email unless I ask.`,
        { hidden: true },
      );
    };
    document.addEventListener('prest0n:action-decided', handler);
    return () => document.removeEventListener('prest0n:action-decided', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendProgrammaticMessage]);

  // ────────────────────────────────────────────────────────────────────
  // SIGNED IN → THE CONVERSATION CARRIES ON (owner report 2026-09-03:
  // "it was originally asked what my health vitals are and after signing in
  // it came back to the chat but nothing occurred").
  //
  // The browser-lane sign-in surface opens OVER the thread that asked for it.
  // When the user finished, the surface closed and that was the end of it:
  // the thread still held an unanswered question, and the user's only way
  // forward was to type "I signed back in" themselves — which is exactly what
  // the owner did. The connection was live; nobody told the conversation.
  //
  // The announcement carries the thread it came from, so this switches back to
  // it (if the user wandered) and hands Prest0n the one fact it was missing.
  // The message is HIDDEN and `[system]`-voiced, the same idiom as the
  // authorized-send failure above: a fake visible "I signed back in" bubble
  // would put words in the user's mouth, and the visible artifact the user
  // actually wants is the answer they asked for, arriving.
  //
  // What to DO with it is the primary orchestrator's call, with the whole
  // thread in front of it (master rule) — this only delivers the fact.
  // ────────────────────────────────────────────────────────────────────
  const connectResumedRef = useRef<Set<string>>(new Set());
  // The thread a chat-lane sign-in was started FROM. The click handler that
  // opens the surface lives outside this tree and does not know the thread —
  // this does, at the moment the surface opens. `fromChat` is what makes it
  // safe: a user connecting an app from the Integrations page with a chat
  // pane open beside them asked nothing here, and must not be answered as
  // though they had.
  const pendingConnectOriginRef = useRef<{ appName: string; threadId: string; at: number } | null>(null);
  useEffect(() => {
    const onSurfaceOpened = (e: Event) => {
      const d = (e as CustomEvent).detail as { appName?: string; fromChat?: boolean } | undefined;
      if (!d?.fromChat || !d.appName || chatHiddenRef.current) return;
      const threadId = threadStore.getActiveThreadIdSync
        ? threadStore.getActiveThreadIdSync()
        : threadStore.activeThreadId;
      if (!threadId) return;
      pendingConnectOriginRef.current = {
        appName: String(d.appName).slice(0, 80), threadId, at: Date.now(),
      };
    };
    document.addEventListener(DESKTOP_SIGNIN_EVENT, onSurfaceOpened as EventListener);
    return () => document.removeEventListener(DESKTOP_SIGNIN_EVENT, onSurfaceOpened as EventListener);
  }, [threadStore.getActiveThreadIdSync, threadStore.activeThreadId]);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ConnectSignedInDetail | undefined;
      // WHICH thread — the one rule that can be wrong in a way nobody
      // notices, so it lives in connectResume.ts where a test can execute it.
      const origin = resolveConnectResumeOrigin({
        appName: String(detail?.appName || ''),
        announced: detail?.origin ?? null,
        remembered: pendingConnectOriginRef.current,
        now: Date.now(),
        windowMs: CONNECT_RESUME_WINDOW_MS,
      });
      // No origin = no conversation behind this sign-in (the Integrations
      // page). Nothing to resume, and never a turn in some other thread.
      if (!origin?.threadId || !detail?.appName) return;
      // Invisible route: minting a turn nobody can see is the 2026-08-20
      // hidden-mount defect, and it would burn a real orchestrator turn.
      if (chatHiddenRef.current) return;
      // Once per sign-in. The surface announces once, but a re-render or a
      // duplicate listener must never buy the user two answers.
      const key = `${origin.threadId}:${detail.appName}:${detail.customSlug || ''}`;
      if (connectResumedRef.current.has(key)) return;
      connectResumedRef.current.add(key);
      const appName = String(detail.appName).slice(0, 80);
      if (origin.threadId !== threadStore.activeThreadId) {
        // Same two-step every switch site uses — the thread the user is
        // looking at keeps its composer draft.
        flushCurrentDraftNow(threadStore.activeThreadId);
        threadStore.switchThread(origin.threadId, { fallbackTitle: `Connect ${appName}` });
      }
      // Microtask, like the new-thread send: the switch above is synchronous,
      // and the send-time capture reads the active thread id.
      void Promise.resolve().then(() =>
        sendProgrammaticMessage(
          `[system] I have finished signing in to ${appName} in your browser — that connection is live now. `
          + `If I asked you something before the sign-in, carry on with it now and answer it. `
          + `Do not ask me to repeat myself, and do not offer to connect ${appName} again.`,
          { hidden: true },
        ),
      );
    };
    document.addEventListener(CONNECT_SIGNED_IN_EVENT, handler as EventListener);
    return () => document.removeEventListener(CONNECT_SIGNED_IN_EVENT, handler as EventListener);
    // flushCurrentDraftNow is deliberately NOT a dependency: it is declared
    // further down this component (the per-thread draft section), so naming it
    // in an array evaluated during render is a temporal-dead-zone crash. The
    // effect BODY runs after render, where it is initialised — the same
    // reason openChatThreadRef above can call it. It is a stable useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendProgrammaticMessage, threadStore.activeThreadId, threadStore.switchThread]);

  // ============================================================================
  // FIX 1 — Per-thread composer draft persistence
  // ============================================================================
  //
  // The existing `useGlobalChat` already persists a single global draft to
  // localStorage. When the thread switcher is enabled we layer a per-thread
  // draft on top so the composer text + selected attachments survive thread
  // switches, page navigation, page refresh, logout/login, and widget
  // close/reopen — keyed by `prest0n:chat:draft:<threadId>` (and a sister
  // `prest0n:chat:draft-att:<threadId>` for attachment metadata).
  //
  // Drafts are debounce-written 300 ms after the last keystroke and removed
  // automatically on successful send (handleSend resets prompt + attachments,
  // which the debounced effect mirrors as an empty payload → key removed).
  //
  // Attachments < MAX_INLINE_ATTACHMENT_BYTES (2 MB) round-trip as base64;
  // larger files persist as metadata-only "placeholder" chips with a
  // re-attach affordance.
  //
  // Storage hygiene: on mount we sweep keys whose threadId no longer exists
  // in the user's thread list.
  // ----------------------------------------------------------------------------

  const draftPersistenceEnabled = threadSwitcherEnabled && threadStore.ready;

  const [placeholderAttachments, setPlaceholderAttachments] =
    useState<AttachmentPreviewPlaceholder[]>([]);

  // Wrapper ref around the attach button. Previously this was used
  // as the anchor for a portaled AttachmentPreviewPanel popup; that
  // popup was retired 2026-05-11 in favour of an inline preview
  // strip rendered directly above the composer. The ref is kept so
  // the wrapper div remains positioned (the badge still uses it).
  const attachAnchorRef = useRef<HTMLDivElement | null>(null);

  // Retired effect (kept as a no-op to preserve hook order with
  // surrounding effects). The dependency list intentionally references
  // state.isAttachmentPanelVisible so dependency-tracking ESLint rules
  // remain satisfied without extra suppressions.
  useEffect(() => {
    return undefined;
  }, [
    state.isAttachmentPanelVisible,
    state.attachments.length,
    state.isChatOpen,
    state.isChatDockedRight,
    state.isChatFullScreen,
  ]);

  // Refs mirror the latest live state so the switch-effect can read them
  // without re-firing on every keystroke (effects that listed prompt /
  // attachments in their deps would also save the outgoing draft on every
  // keypress, which would race with the debounced text-draft saver).
  const draftPromptHtmlRef = useRef(state.promptHtml);
  const draftPromptTextRef = useRef(state.prompt);
  const draftAttachmentsRef = useRef(state.attachments);
  useEffect(() => {
    draftPromptHtmlRef.current = state.promptHtml;
    draftPromptTextRef.current = state.prompt;
    draftAttachmentsRef.current = state.attachments;
  }, [state.promptHtml, state.prompt, state.attachments]);

  const readPromptDomSnapshot = useCallback(() => {
    const el = promptInputRef.current;
    if (!el) {
      return {
        html: draftPromptHtmlRef.current || '',
        text: draftPromptTextRef.current || '',
      };
    }
    // A MOUNTED composer is the truth VERBATIM — including when it is
    // empty (2026-08-12 sticky-composer round). The old
    // `el.innerHTML || staleRef` fallback meant a composer handleSend
    // had JUST cleared fell through to the pre-send refs, so any
    // flush/switch snapshot taken in that window resurrected the sent
    // text into the per-thread draft key. Refs remain the fallback only
    // when no composer element exists at all.
    const html = el.innerHTML || '';
    // Plain text via the same block-aware converter the send path uses —
    // `textContent` fuses `milk<div>eggs</div>` into "milkeggs" (owner report
    // 2026-09-07), and that fallback is exactly what runs under jsdom.
    const text = (html ? htmlToPlainText(html) : (el.innerText || el.textContent || '')).trim();
    return { html, text };
  }, []);

  /**
   * Synchronously persist the currently-visible composer DOM into the
   * active thread's draft key. This closes the thread-switch race where
   * the user types and immediately switches threads before React state
   * + the 300ms debounce have mirrored the latest content.
   */
  const flushCurrentDraftNow = useCallback((explicitThreadId?: string | null) => {
    if (!draftPersistenceEnabled) return;
    const id = explicitThreadId || currentDraftThreadIdRef.current;
    if (!id) return;
    const snapshot = readPromptDomSnapshot();
    saveDraft(id, snapshot);
    draftPromptHtmlRef.current = snapshot.html;
    draftPromptTextRef.current = snapshot.text;
    void saveAttachmentDraft(id, draftAttachmentsRef.current);
  }, [draftPersistenceEnabled, readPromptDomSnapshot]);

  /**
   * The thread id whose draft is currently mirrored into composer state.
   * Updated synchronously inside the switch-effect BEFORE dispatching new
   * content so the debounced save-effect (which fires next tick due to
   * dispatched state changes) writes to the *incoming* thread's key, not
   * the outgoing one.
   */
  const currentDraftThreadIdRef = useRef<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────
  // Just-sent suppression (Bug 3 — Slack C0AM8U804KZ/1778481193.723179):
  // when the user sends, the per-thread draft must be cleared
  // synchronously AND any pending debounced save (whose closure still
  // sees the about-to-be-cleared `state.prompt` content) must be
  // suppressed for a short window so it can't re-write the just-sent
  // text into the draft key. Without this, a remount within ~300 ms of
  // send (close+reopen, fast tab switch, hot-reload) would re-hydrate
  // the composer with the just-sent prompt — the exact repro Miles
  // flagged. The defensive empty-clear effect (lastNonEmptyDraftRef
  // below) is the secondary backstop for slow re-renders.
  // ─────────────────────────────────────────────────────────────────────
  const lastSentThreadIdRef = useRef<string | null>(null);
  const lastSentAtRef = useRef<number>(0);
  /** ms window during which post-send debounced saves are suppressed. */
  const JUST_SENT_SUPPRESS_MS = 800;

  // Sweep stale draft keys on mount (storage hygiene).
  useEffect(() => {
    if (!draftPersistenceEnabled) return;
    const ids = threadStore.threads.map(t => t.id);
    sweepStaleDraftKeys(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPersistenceEnabled]);

  // Switch + hydrate effect: runs whenever the active thread changes.
  // 1) Saves the outgoing thread's current composer state to its keys.
  // 2) Updates currentDraftThreadIdRef BEFORE dispatching new content.
  // 3) Hydrates the incoming thread's draft into composer state + DOM.
  useEffect(() => {
    if (!draftPersistenceEnabled) return;
    const oldId = currentDraftThreadIdRef.current;
    const newId = threadStore.activeThreadId;
    if (newId === oldId) return;

    // 1) Persist outgoing thread's current draft — synchronous text save +
    //    fire-and-forget attachment save (FileReader is async).
    if (oldId) {
      const outgoingSnapshot = readPromptDomSnapshot();
      saveDraft(oldId, outgoingSnapshot);
      draftPromptHtmlRef.current = outgoingSnapshot.html;
      draftPromptTextRef.current = outgoingSnapshot.text;
      void saveAttachmentDraft(oldId, draftAttachmentsRef.current);
    }

    // 2) Re-target the save-effect to the incoming thread before any
    //    dispatch fires so subsequent debounced writes land in the new key.
    currentDraftThreadIdRef.current = newId;

    // 3) Hydrate incoming text draft. Sentinel gate (2026-08-12
    //    sticky-composer round): a stored draft whose body matches the
    //    just-sent sentinel is a resurrection that slipped a save gate —
    //    hydrate empty and HEAL the key instead of re-seating the sent
    //    text in the composer.
    const loaded = newId ? loadDraft(newId) : null;
    let incomingHtml = loaded?.html || '';
    let incomingText = loaded?.text || '';
    if ((incomingHtml || incomingText) && draftIsSentTextMatch(incomingText, incomingHtml)) {
      if (newId) clearDraft(newId);
      incomingHtml = '';
      incomingText = '';
    }
    dispatch({ type: 'SET_PROMPT_HTML', payload: incomingHtml });
    dispatch({ type: 'SET_PROMPT', payload: incomingText });
    if (promptInputRef.current) {
      // An html-less draft is plain TEXT: re-seat it as escaped markup with
      // its line breaks intact, never as raw innerHTML (2026-09-07).
      promptInputRef.current.innerHTML = incomingHtml || (incomingText ? plainTextToComposerHtml(incomingText) : '');
    }

    // 4) Hydrate incoming attachment draft.
    //    Phase A (sync): use the localStorage metadata to render preview
    //    chips immediately so the user sees something on thread switch.
    //    Files small enough to embed as base64 dataUrl rebuild as live
    //    File objects right away; larger entries fall back to placeholder
    //    chips with a re-attach affordance.
    //    Phase B (async): IndexedDB holds the *real* File blobs (added
    //    2026-05-11). When IDB resolves, we replace state.attachments
    //    with the rehydrated blobs and clear the placeholder list, so
    //    sending the message includes every attached file even after
    //    thread switch / refresh / logout.
    const incomingEntries: AttachmentDraftEntry[] = newId
      ? loadAttachmentDraft(newId)
      : [];
    const hydratedFiles: File[] = [];
    const nextPlaceholders: AttachmentPreviewPlaceholder[] = [];
    for (const entry of incomingEntries) {
      const f = draftEntryToFile(entry);
      if (f) {
        hydratedFiles.push(f);
      } else {
        nextPlaceholders.push({
          name: entry.name,
          type: entry.type,
          size: entry.size,
          dataUrl: entry.dataUrl,
        });
      }
    }
    dispatch({ type: 'SET_ATTACHMENTS', payload: hydratedFiles });
    setPlaceholderAttachments(nextPlaceholders);

    if (newId) {
      const targetThreadId = newId;
      void loadAttachmentDraftBlobs(targetThreadId)
        .then((blobs) => {
          if (
            !blobs ||
            blobs.length === 0 ||
            currentDraftThreadIdRef.current !== targetThreadId
          ) {
            return;
          }
          // Replace state.attachments with the IDB-backed blobs (the
          // truth source). Clear placeholder list — these are now real
          // files in state, no re-attach affordance needed.
          dispatch({ type: 'SET_ATTACHMENTS', payload: blobs });
          setPlaceholderAttachments([]);
        })
        .catch(() => { /* ignore IDB hydration errors */ });
    }
    // promptInputRef is a stable ref; dispatch identity is stable per useGlobalChat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPersistenceEnabled, threadStore.activeThreadId, readPromptDomSnapshot]);

  // Debounced save of text draft on composer change.
  //
  // FH-QA composer-refill race (2026-05-21 13:50 UTC repro): when the
  // user types + attaches a file + clicks Send, `handleSend` (in
  // useGlobalChat) awaits `serializeAttachments` BEFORE it stamps the
  // suppression singleton or dispatches RESET_PROMPT. During that
  // await window (tens to hundreds of ms for FileReader), this
  // already-scheduled 300 ms `setTimeout` (whose closure still sees
  // `state.prompt` = "hello") fires and rewrites the just-typed text
  // BACK to the per-thread draft key — overwriting the synchronous
  // clear that `handleSendWithDraftHydration` had just done. The
  // narrow `isEmpty` gate alone misses this case because the closure
  // never observed an empty prompt: `state` is captured pre-send.
  //
  // Fix: check the SHARED suppression singleton (`shouldSuppressDraftRehydrate`)
  // at FIRE TIME. The wrapper stamps the singleton synchronously
  // BEFORE invoking handleSend so any in-flight 300 ms timer (or any
  // timer scheduled during the handleSend pre-await sync run) will
  // see suppression open when it fires and bail. This catches both
  // the empty-state stale closure and the non-empty stale closure
  // that the narrow `isEmpty` gate was missing.
  useEffect(() => {
    if (!draftPersistenceEnabled) return;
    const id = currentDraftThreadIdRef.current;
    if (!id) return;
    const isEmpty = !state.prompt && !state.promptHtml;
    if (
      isEmpty &&
      lastSentThreadIdRef.current === id &&
      lastSentAtRef.current &&
      Date.now() - lastSentAtRef.current < JUST_SENT_SUPPRESS_MS
    ) {
      return;
    }
    // Shared singleton gate — covers the FH-QA race where the closure
    // holds pre-send (non-empty) state and the local `isEmpty` gate
    // would have let the save through.
    if (shouldSuppressDraftRehydrate()) {
      return;
    }
    const handle = window.setTimeout(() => {
      // Recheck at fire time so a stale closure doesn't write
      // post-send content if the user is still inside the window.
      const stillEmpty = !state.prompt && !state.promptHtml;
      if (
        stillEmpty &&
        lastSentThreadIdRef.current === id &&
        lastSentAtRef.current &&
        Date.now() - lastSentAtRef.current < JUST_SENT_SUPPRESS_MS
      ) {
        return;
      }
      // Shared singleton gate at fire time — this is the one that
      // catches the FH-QA composer-refill race. The wrapper opens the
      // suppression window synchronously before handleSend's await on
      // `serializeAttachments`; any 300 ms timer that fires during
      // that await sees the window open here and skips its write,
      // even though its closure still has the pre-send `state.prompt`
      // text.
      if (shouldSuppressDraftRehydrate()) {
        return;
      }
      // FH-QA composer-refill race on LONG NON-attachment prompts
      // (2026-05-21 16:25 UTC): content-domain sentinel check at
      // fire-time. Even if the time-domain window slipped, if the
      // closure-captured prompt body matches the just-sent sentinel
      // we refuse to persist it back to the per-thread draft key — and
      // HEAL any already-poisoned key (2026-08-12) so a write that DID
      // slip an earlier gate can't outlive the sentinel in storage.
      if (draftIsSentTextMatch(state.prompt || '', state.promptHtml || '')) {
        const stored = loadDraft(id);
        if (stored && draftIsSentTextMatch(stored.text || '', stored.html || '')) {
          clearDraft(id);
        }
        return;
      }
      saveDraft(id, {
        html: state.promptHtml || '',
        text: state.prompt || '',
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [draftPersistenceEnabled, state.prompt, state.promptHtml]);

  // Debounced save of attachment draft on attachment change.
  // Mirrors the text-draft fix: respect the shared suppression
  // singleton at fire time so a stale closure scheduled before send
  // can't rewrite the just-attached files back to the draft key while
  // handleSend is still awaiting `serializeAttachments`.
  useEffect(() => {
    if (!draftPersistenceEnabled) return;
    const id = currentDraftThreadIdRef.current;
    if (!id) return;
    const isEmpty = state.attachments.length === 0;
    if (
      isEmpty &&
      lastSentThreadIdRef.current === id &&
      lastSentAtRef.current &&
      Date.now() - lastSentAtRef.current < JUST_SENT_SUPPRESS_MS
    ) {
      return;
    }
    if (shouldSuppressDraftRehydrate()) {
      return;
    }
    const handle = window.setTimeout(() => {
      const stillEmpty = state.attachments.length === 0;
      if (
        stillEmpty &&
        lastSentThreadIdRef.current === id &&
        lastSentAtRef.current &&
        Date.now() - lastSentAtRef.current < JUST_SENT_SUPPRESS_MS
      ) {
        return;
      }
      if (shouldSuppressDraftRehydrate()) {
        return;
      }
      void saveAttachmentDraft(id, state.attachments);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [draftPersistenceEnabled, state.attachments]);

  // Defensive: when the user successfully sends (handleSend clears prompt +
  // attachments synchronously) and the debounced effect persists empty
  // payloads → keys are removed by saveDraft/saveAttachmentDraft. We
  // additionally hard-clear here so a race between the debounce and a
  // subsequent navigation never leaves stale state in storage.
  const lastNonEmptyDraftRef = useRef(false);
  useEffect(() => {
    if (!draftPersistenceEnabled) return;
    const id = currentDraftThreadIdRef.current;
    if (!id) return;
    const isEmpty =
      !state.prompt &&
      !state.promptHtml &&
      state.attachments.length === 0;
    if (!isEmpty) {
      lastNonEmptyDraftRef.current = true;
      return;
    }
    if (lastNonEmptyDraftRef.current) {
      // Transitioned from non-empty → empty (send / explicit clear).
      lastNonEmptyDraftRef.current = false;
      clearDraft(id);
      clearAttachmentDraft(id);
      // Composer-level placeholder list belongs to the active thread, so it
      // also resets on send; switching threads will reload its own list.
      setPlaceholderAttachments([]);
    }
  }, [draftPersistenceEnabled, state.prompt, state.promptHtml, state.attachments]);

  /**
   * Attachment-overlay handler: clicking a placeholder chip opens the
   * file picker so the user can re-attach the file whose blob died on
   * full refresh.
   */
  const handleReattachPlaceholder = useCallback(
    (_placeholder: AttachmentPreviewPlaceholder) => {
      handleFileSelect();
    },
    [handleFileSelect],
  );

  /**
   * Send wrapper that ensures IDB-backed attachment drafts are fully
   * hydrated into state.attachments before invoking handleSend. Without
   * this, a user could attach large files in Thread A, switch to Thread
   * B, switch back to Thread A, type, and click Send before the async
   * IDB hydrate resolved — sending the message with zero attachments
   * (the bug Miles flagged 2026-05-11).
   */
  // ── Per-thread notification mode (owner directive 2026-08-26) ────────────
  // The header bell cycles chime → chime+platform → off, per VIEWER per
  // thread. Resolution stamps the current DEFAULTS on first encounter (see
  // services/threadNotifications), so later defaults edits never touch an
  // established thread.
  const [threadNotifyModes, setThreadNotifyModes] = useState<Record<string, ThreadNotifyMode>>({});
  const threadNotifyModesRef = useRef(threadNotifyModes);
  threadNotifyModesRef.current = threadNotifyModes;
  const resolveNotifyModeFor = useCallback((threadId: string): ThreadNotifyMode => {
    const cached = threadNotifyModesRef.current[threadId];
    if (cached) return cached;
    const uid = (currentUid ?? '').trim();
    if (!uid || !threadId) return 'chime';
    const record = threadStore.threads.find((t) => t.id === threadId);
    const isGroup =
      (sharedThreadParticipants[threadId] ?? 0) >= 2
      || record?.sharedThread === true;
    // The stamp freezes the defaults for this thread FOREVER, so it only
    // happens once the group/solo classification is trustworthy: a definitive
    // group signal, or a membership read that answered. A freshly surfaced
    // group thread's signals lag; stamping it "solo" in that window would be
    // irreversible by design (2026-08-27 review fix).
    const classificationCertain = isGroup || sharedThreadIdsKnown;
    if (!classificationCertain) {
      return modeFromDefaults(loadThreadNotificationDefaults(uid), isGroup);
    }
    return resolveThreadNotifyMode({ uid, threadId, isGroup });
  }, [currentUid, sharedThreadParticipants, threadStore.threads, sharedThreadIdsKnown]);
  const activeThreadNotifyMode: ThreadNotifyMode | null = threadStore.activeThreadId
    ? (threadNotifyModes[threadStore.activeThreadId]
        ?? resolveNotifyModeFor(threadStore.activeThreadId))
    : null;
  const handleCycleThreadNotifyMode = useCallback((threadId: string) => {
    const uid = (currentUid ?? '').trim();
    if (!uid || !threadId) return;
    const next = cycleThreadNotifyMode(resolveNotifyModeFor(threadId));
    setStoredThreadNotifyMode(uid, threadId, next);
    setThreadNotifyModes((prev) => ({ ...prev, [threadId]: next }));
    // Entering the platform state is the user gesture browsers accept the
    // permission prompt on — ask now, not at first delivery.
    if (next === 'chime-platform') ensurePlatformNotificationPermission();
  }, [currentUid, resolveNotifyModeFor]);

  // Incoming-message watcher: chime / platform-notify per the thread's mode.
  // Signals: the ACTIVE thread's transcript tail (sender known — self sends
  // never notify), and every OTHER thread's lastActiveAt movement (the server
  // now bumps those in real time on group activity). A short window after the
  // user's own send suppresses the echo of their own activity.
  const notifySeenRef = useRef<Map<string, string>>(new Map());
  const notifyBaselineDoneRef = useRef(false);
  useEffect(() => {
    const uid = (currentUid ?? '').trim();
    if (!uid) return;
    const seen = notifySeenRef.current;
    const firstPass = !notifyBaselineDoneRef.current;
    for (const t of threadStore.threads) {
      const last = typeof t.lastActiveAt === 'string' ? t.lastActiveAt : '';
      if (!last) continue;
      const prev = seen.get(t.id);
      seen.set(t.id, last);
      // The initial pass only BASELINES — everything already on the rail is
      // history. After it, a brand-new id (prev === undefined) IS an event:
      // a thread someone just added the viewer to, whose first message is
      // exactly what group notifications exist for (2026-08-27 review fix —
      // the old `prev === undefined → skip` silenced that first message
      // forever).
      if (firstPass) continue;
      if (prev !== undefined && last <= prev) continue;
      if (t.id === threadStore.activeThreadId) continue; // handled by the tail watcher
      // The viewer's own activity never notifies them — on ANY device. The
      // server stamps who caused each bump (lastActivityBy), which covers the
      // cross-device sends the per-tab window below never could; the window
      // stays as the fallback for docs from before the stamp existed.
      if ((t.lastActivityBy ?? undefined) === uid) continue;
      const sentRecently = lastSentThreadIdRef.current === t.id
        && Date.now() - lastSentAtRef.current < 5_000;
      if (sentRecently) continue;
      const mode = resolveNotifyModeFor(t.id);
      if (mode === 'off') continue;
      playThreadChime();
      if (mode === 'chime-platform') {
        showThreadPlatformNotification({ threadId: t.id, threadTitle: t.title || 'new message' });
      }
    }
    notifyBaselineDoneRef.current = true;
  }, [threadStore.threads, threadStore.activeThreadId, currentUid, resolveNotifyModeFor]);

  // Active-thread tail watcher: an incoming (non-self) message in the OPEN
  // thread chimes; the platform notification is added only when the tab is
  // hidden — a banner about the conversation on screen is self-noise, while a
  // backgrounded tab is exactly when it earns its place.
  const lastNotifiedTailIdRef = useRef<string | null>(null);
  /**
   * The thread the tail watcher has BASELINED. Opening or switching to a
   * thread replaces `messages` wholesale with its stored transcript — whose
   * tail is something the viewer already read. The first pass for each
   * active-thread identity therefore only records the tail; only a tail that
   * CHANGES while the same thread stays active is a live arrival (2026-08-27
   * review fix — every thread switch used to chime at yesterday's last reply).
   */
  const tailBaselinedThreadRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = (currentUid ?? '').trim();
    const threadId = threadStore.activeThreadId;
    if (!uid || !threadId) return;
    const tail = [...messages].reverse().find((m) => !(m as ChatMessage).isSystem) as ChatMessage | undefined;
    if (!tail || !tail.id) return;
    if (tailBaselinedThreadRef.current !== threadId) {
      tailBaselinedThreadRef.current = threadId;
      lastNotifiedTailIdRef.current = tail.id; // baseline, never notify
      return;
    }
    if (lastNotifiedTailIdRef.current === tail.id) return;
    const isIncoming = tail.isUser
      ? Boolean(tail.senderId && tail.senderId !== uid)
      : tail.isUser === false; // assistant replies count as incoming
    lastNotifiedTailIdRef.current = tail.id;
    if (!isIncoming) return;
    const mode = resolveNotifyModeFor(threadId);
    if (mode === 'off') return;
    playThreadChime();
    if (mode === 'chime-platform' && typeof document !== 'undefined' && document.hidden) {
      const record = threadStore.threads.find((t) => t.id === threadId);
      showThreadPlatformNotification({
        threadId,
        threadTitle: record?.title || 'new message',
        senderLabel: tail.isUser ? (tail.senderDisplayName || undefined) : 'Prest0n',
        preview: (tail.text || '').slice(0, 140),
      });
    }
  }, [messages, threadStore.activeThreadId, threadStore.threads, currentUid, resolveNotifyModeFor]);

  /**
   * Copying out of the transcript puts MEANING on the clipboard, not chrome
   * (owner report 2026-08-26).
   *
   * There was no copy handler before this, so a select-all-copy was the raw
   * serialised DOM — every styled-components class of the bubble and its row.
   * Pasted back into the app those classes resolve against the app's own
   * stylesheet again, which is how a copied message returned as a gold bubble
   * hanging off the right edge of the screen.
   *
   * Bold / italic / underline / strikethrough / headings / lists / links
   * survive; colours, backgrounds, padding, widths and layout do not. A
   * `text/plain` flavour is written alongside so a paste into a terminal or a
   * plain field reads as text rather than one run-on line.
   *
   * Deliberately non-fatal in both directions: an empty selection, a selection
   * of pure UI chrome, or an unwritable clipboard all leave the browser's own
   * copy alone rather than clearing it — a copy that puts NOTHING on the
   * clipboard is worse than one that puts too much.
   */
  const handleTranscriptCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    try {
      const selectionHtml = selectionToHtml(
        typeof window !== 'undefined' ? window.getSelection() : null,
      );
      if (!selectionHtml) return;
      if (applyChatClipboardPayload(selectionHtml, e.clipboardData)) {
        e.preventDefault();
      }
    } catch { /* defensive — the browser's default copy is the fallback */ }
  }, []);

  const handleSendWithDraftHydration = useCallback(async () => {
    // Bug 3 fix (Miles 2026-05-12, Slack C0AM8U804KZ/1778481193.723179):
    // The previous implementation called `flushCurrentDraftNow()` here,
    // which immediately persisted `state.prompt` into the per-thread
    // `prest0n:chat:draft:<threadId>` key. handleSend then dispatched
    // RESET_PROMPT a few statements later, but with React 18's batched
    // renders the persisted draft was occasionally still on disk when
    // a remount fired (chat close+reopen, route change, hot reload),
    // re-hydrating the composer with the just-sent text — Malaika's
    // QA repro.
    //
    // The surgical fix is to clear the active thread's draft
    // SYNCHRONOUSLY at the top of the send wrapper instead of flushing
    // it. We also stamp `lastSentThreadIdRef` / `lastSentAtRef` so the
    // debounced text/attachment save effects suppress writes for the
    // next ~800 ms — that closes the secondary race where a still-
    // pending 300 ms timer scheduled before send-time fires after the
    // synchronous clear and resurrects the draft. The defensive
    // empty-clear effect remains as a tertiary backstop.
    const sendThreadId =
      currentDraftThreadIdRef.current
      || (threadStore.ready ? threadStore.activeThreadId : null);

    // FH-QA composer-refill race (2026-05-21 13:50 UTC):
    // `handleSend` (in useGlobalChat) awaits `serializeAttachments`
    // BEFORE it stamps the suppression singleton or dispatches
    // RESET_PROMPT. During that await window, any debounced save
    // timer that was scheduled BEFORE send (closure holds the just-
    // typed text) can fire and rewrite the just-cleared draft key
    // back to localStorage. We close that gap by stamping the
    // SHARED singleton synchronously HERE, before `void handleSend()`
    // — so any timer that fires while handleSend is still awaiting
    // serializeAttachments sees the suppression window open and
    // bails. Paired with the `shouldSuppressDraftRehydrate()` check
    // inside both per-thread debounced save effects and the global
    // CHAT_DRAFT debounce in useGlobalChat's handleInput.
    //
    // Safety-net `draftMarkProcessingEnded` runs after a 3 s window
    // so a fire-and-forget send that never flips isProcessing (or a
    // future code path that drops the matching markProcessingEnded
    // call) can never leave the suppression flag latched. The window
    // was 2 s prior to FH-QA 2026-05-21 16:25 UTC; extended to 3 s
    // after a long NON-attachment prompt (1200 chars, no
    // `serializeAttachments` await) still surfaced a stale debounced
    // save firing on the tail of the message. 3 s is comfortably
    // longer than the post-send race we have observed in the wild
    // and shorter than any legitimate cross-tab restore the user
    // would expect to see.
    draftMarkSentNow();
    // FH-QA composer-refill race on LONG NON-attachment prompts
    // (2026-05-21 16:25 UTC): stamp the content-domain sentinel here
    // too, at the synchronous start of the wrapper, BEFORE handleSend
    // runs. handleSend stamps it again with its own `text`/`safeHtml`
    // values; both stamps converge on the same payload because we
    // read from the same React state snapshot the dispatch reducer
    // is about to clear via RESET_PROMPT.
    try {
      const presendText = state.prompt || '';
      const presendHtml = state.promptHtml || '';
      if (presendText || presendHtml) {
        draftMarkSentText(presendText, presendHtml);
      }
    } catch { /* defensive — sentinel is best-effort */ }
    // P2b @-mention summon: fire-and-forget once the send text is captured. Never
    // blocks/alters the send; the ref's callback no-ops unless the mentions flag is
    // on for this account, and any failure is swallowed (message still sends).
    try {
      const mentionText = state.prompt || '';
      if (mentionText && sendThreadId) void mentionSummonRef.current?.(mentionText, sendThreadId);
    } catch { /* defensive — summon is best-effort */ }

    // W3b: the summon gate. In a thread with two or more humans that requires an
    // @Prest0n mention, a message without one is DELIVERED to the other
    // participants but summons no reply. Inert unless the account flag is on;
    // `decide()` returns summon:true for every other case, including a
    // one-on-one, an unresolved membership, and a thread that relaxed the rule.
    try {
      const gateText = state.prompt || '';
      const gate = summonSettingRef.current;
      // ATTACHMENTS NO LONGER EXEMPT THE GATE (owner report 2026-08-26).
      //
      // They used to, and the reason was honest at the time: the
      // suppressed-delivery path carried text only, so suppressing an
      // attachment send would have delivered the words and silently dropped
      // the file. The cost of that exemption turned out to be the whole
      // feature — Miles wrote "@justinzel please let me know all the triage
      // group threads you're still in after refreshing" with a screenshot
      // attached, in a thread whose header read "Prest0n replies only when
      // mentioned", and Prest0n answered a message that was plainly for
      // Justin.
      //
      // A file says what the message is ABOUT. It says nothing about WHO it is
      // for, and who it is for is the only question this gate asks. So the
      // delivery path learned to carry attachments (postWithoutSummon →
      // messageAttachments) and the exemption is gone.
      const gateAttachments = Array.isArray(state.attachments) ? state.attachments : [];
      const gateHasAttachments = gateAttachments.length > 0;
      // A send with NEITHER text nor files has nothing to gate.
      if ((gateText.trim() || gateHasAttachments) && sendThreadId && gate) {
        // Refuse a contentless summons BEFORE it becomes a turn (INCIDENT
        // fern-msjhf1xu, 2026-08-07): a bare "@Prest0n" dispatches a dedicated-VM
        // turn with nothing to answer → empty agent output → the adapter's
        // empty-response guard 502s → the misleading "returned an unexpected
        // response" card, which Regenerate then loops. The user's intent was to
        // summon Prest0n ABOUT something, so keep their text in the composer and
        // ask for the something, rather than delivering a doomed request.
        // Scoped to gate-enabled accounts — the population whose UI advertises
        // the @Prest0n gesture.
        //
        // A bare "@Prest0n" WITH a file attached is exempt from this refusal:
        // there the mention is not contentless at all, the file is the request
        // ("read this"), and the server synthesizes a prompt for exactly that
        // case (buildAttachmentOnlyPrompt). Note this is the opposite direction
        // from the gate below — attachments make a summons answerable, and say
        // nothing about who a message is addressed to.
        if (gate.enabled && !gateHasAttachments && isAssistantMentionOnly(gateText)) {
          const hintLang = detectThreadLanguage(state.messages);
          const hint = hintLang === 'es'
            ? 'Escribe tu pregunta junto a @Prest0n — una mención sola no le da a Prest0n nada que responder.'
            : "Add your question next to @Prest0n — a mention alone doesn't give Prest0n anything to answer.";
          const tail = state.messages[state.messages.length - 1] as ChatMessage | undefined;
          // Idempotent under repeat attempts: never stack two copies of the hint.
          if (!(tail && tail.isSystem && tail.text === hint)) {
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: `summon_hint_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                text: hint,
                isUser: false,
                isSystem: true,
                timestamp: new Date(),
              } as ChatMessage,
            });
          }
          return; // send refused — the prompt stays in the composer to finish
        }
        const decision = gate.decide(gateText);
        if (!decision.summon) {
          // Deliver the message ourselves — Prest0n answering is normally what
          // carries a human turn to the other participants (see W3a).
          const localId =
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `post_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          dispatch({
            type: 'ADD_MESSAGE',
            payload: {
              id: localId,
              text: gateText,
              isUser: true,
              timestamp: new Date(),
              senderId: currentUid || undefined,
              // Render the sender's own files on their own bubble immediately.
              // These are the local File objects; the storage-backed copies the
              // OTHER participants read are uploaded just below.
              ...(gateHasAttachments ? { attachments: gateAttachments } : {}),
            } as ChatMessage,
          });
          // The message was DELIVERED, so the composer must clear like any
          // other send. A bare RESET_PROMPT left the text alive in the
          // uncontrolled contenteditable DOM and in the draft keys — both are
          // cleared only on the handleSend path this branch returns before —
          // so the sent text sat in the composer and the sidebar draft, send
          // after send (owner repro 2026-08-08).
          clearComposerForSuppressedDelivery();
          if (draftPersistenceEnabled && sendThreadId) {
            try {
              clearDraft(sendThreadId);
              clearAttachmentDraft(sendThreadId);
            } catch { /* defensive empty-clear is the backstop */ }
            lastSentThreadIdRef.current = sendThreadId;
            lastSentAtRef.current = Date.now();
            lastNonEmptyDraftRef.current = false;
          }
          // Deliver text AND files. The upload has to finish before the post,
          // because the other participants read the file by URL — an inline
          // base64 copy would blow past Firestore's per-document limit and
          // fail the whole delivery. Upload failure degrades to a text-only
          // delivery with an explicit notice rather than a message that looks
          // sent but arrived without its attachment.
          void (async () => {
            let delivered: GroupPostAttachment[] | undefined;
            let uploadFailed = false;
            if (gateHasAttachments) {
              try {
                const uid = getCurrentUserUid();
                const stored = uid
                  ? await serializeAttachmentsWithStorage(gateAttachments, uid, localId)
                  : [];
                delivered = stored
                  .filter((a) => typeof a.storageUrl === 'string' && a.storageUrl.startsWith('https://'))
                  .map((a) => ({
                    name: a.name,
                    type: a.type,
                    size: a.size,
                    storageUrl: a.storageUrl as string,
                    storagePath: a.storagePath,
                  }));
                uploadFailed = delivered.length < gateAttachments.length;
              } catch {
                uploadFailed = true;
              }
            }
            const ok = await gate.postWithoutSummon(gateText, localId, delivered);
            if (ok && uploadFailed) {
              // Say so. A file that quietly failed to reach the group is the
              // one outcome worse than the reply we just suppressed.
              dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                  id: `${localId}__attach_warn`,
                  text: getUserUiLanguage() === 'es'
                    ? 'Tu mensaje llegó al grupo, pero no se pudo compartir el archivo. Vuelve a adjuntarlo.'
                    : "Your message reached the group, but the file didn't upload — try attaching it again.",
                  isUser: false,
                  isSystem: true,
                  timestamp: new Date(),
                } as ChatMessage,
              });
            }
          })();
          // Explain the silence ONCE per user per thread — and only when the
          // sender plausibly EXPECTED a reply, i.e. their message directly
          // follows a Prest0n turn (owner directive 2026-08-08). After another
          // human's message, silence is the group's normal state and the note
          // is noise. Local system notes are skipped when finding the tail:
          // they are client-side chrome, not thread content. `state.messages`
          // is the pre-dispatch snapshot, so the user's own just-delivered
          // message is not in it yet.
          // Copy note (2026-08-07): the first version said "Type @Prest0n to
          // bring it in", which taught the exact gesture that crashes — a bare
          // mention is a contentless turn (see the guard above). Say "include it
          // in your message" so the taught gesture always carries a question.
          const lastThreadMessage = [...state.messages]
            .reverse()
            .find((m) => !(m as ChatMessage).isSystem);
          const repliedToAssistant =
            !!lastThreadMessage &&
            (lastThreadMessage.isUser !== true
              || (lastThreadMessage as ChatMessage).senderId === ASSISTANT_SENDER_ID);
          if (gate.shouldExplainSilence && repliedToAssistant) {
            gate.markSilenceExplained();
            const noteLang = detectThreadLanguage(state.messages);
            dispatch({
              type: 'ADD_MESSAGE',
              payload: {
                id: `${localId}__summon_hint`,
                text: noteLang === 'es'
                  ? 'Prest0n solo responde en este hilo cuando lo mencionan. Incluye @Prest0n en tu '
                    + 'mensaje para recibir respuesta, o desactívalo en el menú del hilo.'
                  : 'Prest0n only replies in this thread when mentioned. Include @Prest0n in your '
                    + 'message to get a reply, or turn that off in the thread menu.',
                isUser: false,
                isSystem: true,
                timestamp: new Date(),
              } as ChatMessage,
            });
          }
          return; // no turn, no assistant reply
        }
      }
    } catch { /* defensive — a gate failure must never block a send */ }
    draftMarkProcessingStarted();
    window.setTimeout(() => {
      draftMarkProcessingEnded();
    }, 3000);

    if (draftPersistenceEnabled && sendThreadId) {
      try {
        clearDraft(sendThreadId);
        clearAttachmentDraft(sendThreadId);
      } catch { /* ignore — defensive empty-clear is the backstop */ }
      lastSentThreadIdRef.current = sendThreadId;
      lastSentAtRef.current = Date.now();
      // Reset the empty-clear tracker so the post-RESET_PROMPT effect
      // pass doesn't re-invoke clearDraft for an already-clear key
      // (no-op, but keeps the trace clean for debugging).
      lastNonEmptyDraftRef.current = false;
    }

    if (!draftPersistenceEnabled) {
      void handleSend();
      return;
    }
    const id = currentDraftThreadIdRef.current;
    if (id && state.attachments.length === 0 && placeholderAttachments.length > 0) {
      try {
        const blobs = await loadAttachmentDraftBlobs(id);
        if (
          blobs &&
          blobs.length > 0 &&
          currentDraftThreadIdRef.current === id
        ) {
          dispatch({ type: 'SET_ATTACHMENTS', payload: blobs });
          setPlaceholderAttachments([]);
          // Wait one microtask for the dispatch to settle before sending.
          await Promise.resolve();
        }
      } catch { /* ignore IDB hydration errors and proceed */ }
    }
    void handleSend();
  }, [
    draftPersistenceEnabled,
    handleSend,
    clearComposerForSuppressedDelivery,
    state.attachments.length,
    placeholderAttachments.length,
    dispatch,
    threadStore.ready,
    threadStore.activeThreadId,
  ]);

  const handleDismissPlaceholder = useCallback((index: number) => {
    setPlaceholderAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Wraps `confirmClearChat` so the user-visible "wipe" feels complete:
   *
   *   • `confirmClearChat` (in useGlobalChat) clears React state, the
   *     legacy global localStorage chat-history/draft keys, and the
   *     Firestore conversation-history doc (namespace-aware).
   *
   *   • This wrapper additionally hard-resets the per-instance thread
   *     store: every per-thread message key, the thread list, the
   *     active-thread id, the archive, and the IDB attachment-blob
   *     store are dropped, then a fresh empty default thread is
   *     bootstrapped. Without this, a user with N threads pressing the
   *     trash icon would see their messages reload from per-thread
   *     storage on the next switch (Miles 2026-05-12, Slack thread
   *     C0AM8U804KZ/1778481193.723179).
   *
   *   • Composer drafts (`prest0n:chat:draft:<threadId>` and the
   *     attachment-metadata sister key) are also swept so the
   *     composer doesn't repopulate from a now-orphan thread id on
   *     the next mount.
   */
  const confirmClearChatWithReset = useCallback(async () => {
    await confirmClearChat();

    // Sweep composer drafts (text + attachment metadata + IDB blobs)
    // for every thread on this instance BEFORE we wipe the thread
    // list. We pass an empty Set so `sweepStaleDraftKeys` treats every
    // existing draft key as orphaned and removes it.
    if (draftPersistenceEnabled) {
      try {
        sweepStaleDraftKeys(new Set<string>());
      } catch (err) {
        // Non-fatal — drafts will be re-orphaned on next mount.
        console.warn('[ClearChat] draft sweep failed:', err);
      }
      // Also reset the synchronous draft trackers so the post-reset
      // hydration effect doesn't try to load a draft for a stale thread id.
      currentDraftThreadIdRef.current = null;
      setPlaceholderAttachments([]);
    }

    if (threadSwitcherEnabled && threadStore.ready) {
      // Hard reset: wipes per-thread localStorage, IDB blobs, the
      // thread list, the archive, and bootstraps a fresh empty thread.
      threadStore.resetAllThreads();
    }
  }, [
    confirmClearChat,
    threadSwitcherEnabled,
    threadStore,
    draftPersistenceEnabled,
  ]);

  // Bilingual aria-label / title for the top-level clear-chat button.
  // Mirrors PR #68's localized fresh-state pattern.
  const clearChatLanguage = detectThreadLanguage(state.messages);
  const clearChatAriaLabel = clearChatLanguage === 'es'
    ? 'Borrar historial de chat'
    : 'Clear chat history';
  const clearChatTitle = clearChatLanguage === 'es'
    ? 'Borrar chat'
    : 'Clear chat';
  const showTopLevelClearButton = shouldShowGlobalClearButton(
    threadSwitcherEnabled ? threadStore.threads : null,
  );

  // Navigate to wallet with optional action parameter for deep linking
  const handleNavigateToWallet = useCallback((destination?: 'add-payment' | 'add-tokens') => {
    // Close chat if open
    if (isChatOpen) {
      handleHide();
    }
    // Navigate with action param for deep linking
    navigate(destination ? `/wallet?action=${destination}` : '/wallet');
  }, [navigate, isChatOpen, handleHide]);

  // Prefer the in-place top-up modal (no navigation) when available; fall back
  // to the /wallet deep-link if the TopUpModalProvider isn't mounted.
  const handleTopUpRequest = useCallback((destination?: 'add-payment' | 'add-tokens') => {
    if (topUp) {
      topUp.open('chat-low-balance');
      return;
    }
    handleNavigateToWallet(destination);
  }, [topUp, handleNavigateToWallet]);

  // Handle click on blocked prompt (shows popup)
  const handleBlockedPromptClick = useCallback(() => {
    // Expired trial: open chat so the trial-ended message is visible — never
    // the top-up popup (trial accounts can't add tokens; owner 2026-07-31).
    if (trialExpiredBlock) {
      if (!isChatOpen) {
        handleOpen();
      }
      return;
    }
    if (lowBalanceState.shouldBlock) {
      // Still open chat to show history
      if (!isChatOpen) {
        handleOpen();
      }
      // Show popup
      setShowCriticalBalancePopup(true);
    }
  }, [trialExpiredBlock, lowBalanceState.shouldBlock, isChatOpen, handleOpen]);

  // ============================================================================
  // Onboarding Welcome — auto-open chat + stream personalized first message
  // ============================================================================
  const welcomeActions = useMemo(() => ({
    openChat: () => {
      if (!isChatOpen) {
        handleOpen();
      }
    },
    sendMessage: (text: string) => sendProgrammaticMessage(text, { hidden: true }),
  }), [isChatOpen, handleOpen, sendProgrammaticMessage]);

  // isReady when: chat system mounted and user is authorized
  const welcomeReady = globalTools.isAuthorized;
  useOnboardingWelcome(welcomeActions, welcomeReady, isPrest0nSessionReady);

  // ============================================================================
  // Testing Environment: Auto-detect dev/preview URLs
  // ============================================================================

  // Track test mode active state in React so the visual indicator re-renders
  const [isTestModeActive, setIsTestModeActive] = useState(() => shouldTagAsTest());
  // Exempt organizations (task #76, 2026-08-18 owner directive): the
  // test-mode pill must NEVER render in an exempt workspace — it has to look
  // exactly like a real customer account. Display-only suppression: key
  // routing/tagging via shouldTagAsTest() is untouched.
  const trialChrome = useTrialStatus();

  // Keep the React test-mode indicator in sync with the SOURCE OF TRUTH (the
  // persisted admin toggle, re-read from localStorage) rather than overwriting
  // it. The previous version called setRuntimeTestingEnvironment(isDevPreviewOrigin())
  // on every focus/visibilitychange, which on a production origin evaluates to
  // false and silently WIPED the admin's testing toggle mid-session — turning
  // real billing back on without any signal. Dev/preview origins still force
  // test mode because shouldTagAsTest() already ORs in isDevPreviewOrigin().
  useEffect(() => {
    const syncTestingMode = () => {
      // Re-hydrate the persisted admin choice; never clobber it with a derived value.
      setIsTestModeActive(refreshRuntimeTestingFromStorage());
    };

    syncTestingMode();
    const handleVisibilityChange = () => syncTestingMode();
    window.addEventListener('focus', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // React to the admin toggling test mode in another tab/the Settings page.
    window.addEventListener('storage', handleVisibilityChange);
    window.addEventListener('testing-environment-changed', handleVisibilityChange as EventListener);
    return () => {
      window.removeEventListener('focus', handleVisibilityChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleVisibilityChange);
      window.removeEventListener('testing-environment-changed', handleVisibilityChange as EventListener);
    };
  }, []);

  // Trial-ended graceful message (owner directive 2026-07-31): expired trial
  // users cannot chat — inject one clear message pointing at their Prest0
  // point of contact, and retract it if the account gets activated
  // (trialExpiredBlock flips false when the subscription doc changes).
  const trialEndedMessageShownRef = useRef(false);
  useEffect(() => {
    if (trialExpiredBlock && !trialEndedMessageShownRef.current) {
      const timer = setTimeout(() => {
        if (trialEndedMessageShownRef.current) return;
        trialEndedMessageShownRef.current = true;
        const systemMessage: ChatMessage = {
          id: 'trial_ended_banner',
          text: TRIAL_ENDED_CHAT_MESSAGE,
          html: `<p>${TRIAL_ENDED_CHAT_MESSAGE}</p>`,
          isUser: false,
          timestamp: new Date(),
          sourceTag: 'system:trial_ended',
        };
        dispatch({ type: 'ADD_MESSAGE', payload: systemMessage });
      }, 400);
      return () => clearTimeout(timer);
    }
    if (!trialExpiredBlock && trialEndedMessageShownRef.current) {
      trialEndedMessageShownRef.current = false;
      dispatch({ type: 'REMOVE_MESSAGE_BY_ID', payload: { id: 'trial_ended_banner' } });
    }
    return undefined;
  }, [trialExpiredBlock, dispatch]);

  // Add system message to chat history when balance blocking is detected
  useEffect(() => {
    // Hotfix #2 (2026-05-13) — belt-and-suspenders: when an entitlement
    // bypass is in effect (trial / client-free / enterprise / legacy
    // trial.active) we must NEVER inject the low-balance banner. The
    // useLowBalanceWarning hook already forces `shouldBlock=false`
    // under bypass (see useLowBalanceWarning.ts:124-142), so this
    // guard is redundant on the happy path — but it prevents a future
    // regression in the bypass logic from re-injecting the banner.
    if (lowBalanceState.hasEntitlementBypass) {
      if (lowBalanceMessageShownRef.current) {
        lowBalanceMessageShownRef.current = false;
      }
      return;
    }

    // Expired trial takes precedence: the trial-ended message explains next
    // steps; a simultaneous "top up Work Units in your wallet" banner would tell a
    // trial user to do the one thing they can't (owner 2026-07-31).
    if (trialExpiredBlock) {
      if (lowBalanceMessageShownRef.current) {
        lowBalanceMessageShownRef.current = false;
        dispatch({ type: 'REMOVE_MESSAGE_BY_ID', payload: { id: 'low_balance_banner' } });
      }
      return;
    }

    // Only add message once per session when shouldBlock becomes true.
    //
    // STABILITY GATE (false-banner fix, Miles 2026-07-12): SPA sign-in
    // produces exactly ONE committed render of "signed in + settled $0
    // wallet" before the real summary loads (WalletSummaryContext now also
    // reports loading through that window — this gate is the belt-and-
    // suspenders). A genuine low balance persists; a transition blip clears
    // within a frame or two. Inject only if the block still holds after a
    // short settle window.
    if (lowBalanceState.shouldBlock && !lowBalanceMessageShownRef.current) {
      const timer = setTimeout(() => {
        if (lowBalanceMessageShownRef.current) return;
        lowBalanceMessageShownRef.current = true;
        const systemMessage: ChatMessage = {
          id: 'low_balance_banner',
          text: "⚠️ you're out of PRO Work Units — that's why sending is unavailable. top up or redeem a code to resume instantly.",
          html: "<p>⚠️ you're out of PRO Work Units — that's why sending is unavailable. top up or redeem a code to resume instantly.</p>",
          isUser: false,
          timestamp: new Date(),
          sourceTag: 'system:low_balance',
        };
        dispatch({ type: 'ADD_MESSAGE', payload: systemMessage });
      }, 400);
      // shouldBlock flipping false before the timer fires cancels the inject —
      // the effect re-runs (dep below) and this cleanup runs first.
      return () => clearTimeout(timer);
    }

    // Balance recovered (top-up or a transition blip that already injected):
    // the banner claims chatting is blocked — when it no longer is, RETRACT
    // it instead of leaving a stale scary message in the conversation
    // (owner repro 2026-07-12: banner persisted while the reply flowed).
    if (!lowBalanceState.shouldBlock && lowBalanceMessageShownRef.current) {
      lowBalanceMessageShownRef.current = false;
      dispatch({ type: 'REMOVE_MESSAGE_BY_ID', payload: { id: 'low_balance_banner' } });
    }
    return undefined;
  }, [lowBalanceState.shouldBlock, lowBalanceState.hasEntitlementBypass, trialExpiredBlock, dispatch]);

  const {
    promptInputRef,
    chatMessagesRef,
    transcriptContentRef,
    fileInputRef,
    promptBarRef,
    floatingPromptBarRef,
    chatContainerRef,
    onProcessingBlockedRef,
    sendRouteOverrideRef,
  } = refs;
  /**
   * Route Enter through the SAME wrapper the send buttons use (2026-08-07
   * incident): the hook's handleKeyDown called handleSend directly, so every
   * keyboard send skipped handleSendWithDraftHydration — draft sentinels, the
   * @-mention summon, and the W3b summon gate all silently bypassed. That is
   * why "Prest0n replies only when mentioned" kept replying in QA: the gate
   * was correct and never on the path. Assigned every render so the ref always
   * holds the current wrapper identity. Guarded: test doubles of the hook may
   * not provide the ref, and they get the direct-send behaviour they mock.
   */
  if (sendRouteOverrideRef) sendRouteOverrideRef.current = handleSendWithDraftHydration;

  // @-mention autocomplete (P2b follow-up) — composer dropdown over the account
  // roster. Detection is read-only (inspects the live Selection) and insertion goes
  // through execCommand, so the send/draft path is never touched. Shares the same
  // account-scoped mentions flag as the summon; inert (no dropdown, keys fall
  // through) unless that flag is on for this account.
  const mentionAutocomplete = useThreadMentionAutocomplete({
    accountId: prest0nSessionAccountId,
    selfId: currentUid,
    inputRef: promptInputRef,
  });

  // ────────────────────────────────────────────────────────────────────
  // Thread recommendations (owner directive 2026-08-11)
  //
  // 1. LIVE connection state for capability grounding: chips (starter AND
  //    follow-up) are filtered/rewritten against what is actually connected,
  //    so the product never recommends a message Prest0n can't act on. `null`
  //    = unknown → grounding is skipped (fail open). The cache invalidates
  //    the moment an in-chat connect card completes, so a fresh connection is
  //    usable in suggestions immediately.
  // 2. Follow-up chips for EVERY thread: after each completed Prest0n turn a
  //    personalized "what to say next" row renders (suppressed in gated group
  //    threads while humans are talking to each other — the pure rule lives
  //    in followupVisibility.ts).
  // ────────────────────────────────────────────────────────────────────
  const [connectedAppSlugs, setConnectedAppSlugs] = React.useState<string[] | null>(null);
  // Permission fit (owner directive 2026-08-12): connect chips only for
  // viewers who can actually complete a connect. hasPermission is
  // owner-inclusive; loading/error keep the gate OPEN (house fail-open bias —
  // the connect card itself degrades gracefully to an "ask an admin" note on
  // a server-enforced 403), so only a cleanly-resolved "no permission" hides
  // connect recommendations. UNRESOLVED ≠ DENIED (2026-08-19, owner report:
  // blank new threads in some organizations): a walk that finds NO member
  // doc anywhere ends all-empty with error:null — every resolved membership
  // stamps membershipStatus (default 'active'), so null means "we never
  // resolved this viewer's membership", which must fail OPEN like
  // loading/error do, not read as a permission-less member.
  const viewerRbac = useRbac();
  const allowConnectChips =
    viewerRbac.loading ||
    viewerRbac.error !== null ||
    viewerRbac.membershipStatus === null ||
    viewerRbac.hasPermission('integrations.manage');
  const connectionCompleteCount = React.useMemo(
    () => state.messages.filter((m) => (m as ChatMessage).sourceTag === 'composio_connection_complete').length,
    [state.messages],
  );
  React.useEffect(() => {
    if (!currentUid) { setConnectedAppSlugs(null); return; }
    let cancelled = false;
    if (connectionCompleteCount > 0) invalidateConnectedAppsCache();
    void getConnectedAppSlugs().then((slugs) => {
      if (!cancelled) setConnectedAppSlugs(slugs);
    });
    return () => { cancelled = true; };
  }, [currentUid, connectionCompleteCount]);

  /**
   * Replace the whole draft with `text` through the composer's own input
   * pipeline (owner directive 2026-08-12 attachment staging). Same idiom as
   * the mention insert: focus + Range + execCommand('insertText') so onInput
   * fires and prompt/draft state sync exactly as if the user typed — the
   * composer is an UNCONTROLLED contentEditable and bare state writes desync
   * it (2026-08-08 owner repro).
   */
  const stageDraftText = React.useCallback((text: string) => {
    const el = promptInputRef.current;
    if (!el || typeof window === 'undefined' || typeof document === 'undefined') return;
    try {
      el.focus({ preventScroll: true });
      const sel = window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand('insertText', false, text);
    } catch { /* best-effort — a failed stage leaves the draft untouched */ }
  }, [promptInputRef]);

  /**
   * Suggestion-chip pick router (owner directive 2026-08-12): a chip that
   * REFERENCES a document the user must supply ("Translate this letter to
   * Spanish") must not auto-send into a turn with no letter. Instead, in the
   * same click: the chip text becomes the draft and the attachment picker
   * opens (the click gesture keeps the native file dialog allowed). The user
   * attaches, then sends — text + document travel together. If a document is
   * ALREADY staged, the pick sends immediately through the REAL send path so
   * the attachments ride along (programmatic sends are text-only). Everything
   * else keeps today's one-click send.
   */
  const handleSuggestionPick = React.useCallback((text: string) => {
    const attachmentsAllowed = config.allowAttachments !== false;
    if (attachmentsAllowed && chipNeedsAttachment(text)) {
      const hasStagedAttachments = state.attachments.length > 0 || placeholderAttachments.length > 0;
      if (!hasStagedAttachments) {
        stageDraftText(text);
        handleAttachmentButtonClick();
        return;
      }
      stageDraftText(text);
      // Defer one macrotask so the staged draft's state flush lands before the
      // send wrapper (assigned to sendRouteOverrideRef every render) reads it.
      window.setTimeout(() => { void sendRouteOverrideRef?.current?.(); }, 0);
      return;
    }
    void sendProgrammaticMessage(text);
  }, [
    config.allowAttachments,
    state.attachments.length,
    placeholderAttachments.length,
    stageDraftText,
    handleAttachmentButtonClick,
    sendProgrammaticMessage,
    sendRouteOverrideRef,
  ]);

  const followupRecsEnabled = React.useMemo(
    () => resolveFollowupRecsEnabled({ accountId: prest0nSessionAccountId }),
    [prest0nSessionAccountId],
  );
  const activeThreadBusyForFollowups = threadStore.activeThreadId
    ? Boolean(state.processingByThread?.[threadStore.activeThreadId])
    : state.isProcessing;
  const threadFollowups = useThreadFollowupSuggestions({
    // Never run while THIS thread is mid-turn (2026-09-02). The tag-based
    // pending filter is a heuristic that has drifted before; the per-thread
    // busy bit is the structural signal, so a turn in progress can no longer
    // mint a tail, take the generation lease, or flap the row. Kept as belt
    // and braces WITH the widened tag filter: the busy bit has a documented
    // safety floor (long tasks, mid-task clarifying finalize) where it reads
    // false mid-turn, which is exactly when the tag filter has to hold.
    enabled: followupRecsEnabled && !isInternalPrest0n && !activeThreadBusyForFollowups,
    threadId: threadStore.activeThreadId,
    messages: state.messages,
    selfId: currentUid,
  });
  // Dynamic new-thread starters (owner directive 2026-09-02, the new-thread
  // screenshot). Fills resolveSuggestionChips' `personalized` slot with a set
  // generated from the live account picture — memory including anything the
  // user or organization has TAUGHT about how to suggest, recent thread
  // topics, connected apps, routines — so a fresh thread stops offering the
  // same four hardcoded prompts to everyone forever. Runs ONLY on a thread
  // with no real content yet (that is what starters are), and never mid-turn,
  // reusing the same structural busy signal the follow-up row uses.
  const dynamicStarters = useStarterSuggestions({
    threadId: threadStore.activeThreadId,
    enabled: followupRecsEnabled
      && !isInternalPrest0n
      && !activeThreadBusyForFollowups
      && !state.messages.some((m) => m.isUser === true),
  });

  // ────────────────────────────────────────────────────────────────────
  // One-keystroke mention affordance (owner directive 2026-08-11): in a
  // gated group thread a small chip at the composer's right edge shows who
  // one Tab press (or one click) will mention — Prest0n after a Prest0n
  // turn, otherwise the human who spoke last. Roster loads lazily, only
  // once a gated thread is active (same source the autocomplete uses).
  // ────────────────────────────────────────────────────────────────────
  const [affordanceRoster, setAffordanceRoster] = React.useState<MentionRosterEntry[]>([]);
  const affordanceRosterLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (!threadSummonSetting.gatesThisThread || affordanceRosterLoadedRef.current) return;
    const acct = (prest0nSessionAccountId || '').trim();
    if (!acct) return;
    affordanceRosterLoadedRef.current = true;
    void getAccountMembers(acct)
      .then((members) => setAffordanceRoster(Array.isArray(members) ? (members as MentionRosterEntry[]) : []))
      .catch(() => { affordanceRosterLoadedRef.current = false; });
  }, [threadSummonSetting.gatesThisThread, prest0nSessionAccountId]);

  const mentionAffordanceTarget = React.useMemo(
    () => resolveMentionAffordance({
      gatesThisThread: threadSummonSetting.gatesThisThread,
      requireMention: threadSummonSetting.requireMention,
      draftText: state.prompt,
      messages: state.messages,
      selfId: currentUid,
      roster: affordanceRoster,
    }),
    [
      threadSummonSetting.gatesThisThread,
      threadSummonSetting.requireMention,
      state.prompt,
      state.messages,
      currentUid,
      affordanceRoster,
    ],
  );

  /**
   * Insert the affordance mention at the START of the draft. Same idiom as the
   * autocomplete's insertHandle: focus + Range + execCommand('insertText') so
   * the composer's own onInput fires and prompt/draft state syncs exactly as
   * if the user typed — no bespoke state writes (the composer is an
   * UNCONTROLLED contentEditable; see the 2026-08-08 owner repro).
   */
  const insertAffordanceMention = React.useCallback(() => {
    const target = mentionAffordanceTarget;
    const el = promptInputRef.current;
    if (!target || !el || typeof window === 'undefined' || typeof document === 'undefined') return;
    try {
      el.focus({ preventScroll: true });
      const sel = window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(true); // caret to the very start — a mention ADDRESSES, so it leads
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand('insertText', false, `${target.display} `);
    } catch { /* best-effort — a failed insert leaves the draft untouched */ }
  }, [mentionAffordanceTarget, promptInputRef]);

  /**
   * Composer-level Tab: when the affordance chip is visible and the
   * autocomplete dropdown is NOT open (it gets first refusal and consumes Tab
   * while open), a bare Tab inserts the mention instead of moving focus. The
   * visible chip with its `tab` keycap is what discloses the remap.
   */
  const handleAffordanceKeyDown = React.useCallback((e: React.KeyboardEvent): boolean => {
    if (e.key !== 'Tab' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return false;
    if (!mentionAffordanceTarget) return false;
    e.preventDefault();
    insertAffordanceMention();
    return true;
  }, [mentionAffordanceTarget, insertAffordanceMention]);

  /**
   * "The cursor should be there" (owner directive 2026-08-11): when the
   * affordance appears — a gated thread landed on a mentionable moment — put
   * focus in the composer so Tab works with zero clicks. Only steals focus
   * from NOTHING: any real focused control (another input, a menu, a link)
   * is left alone.
   */
  const affordanceFocusKey = mentionAffordanceTarget
    ? `${threadStore.activeThreadId}:${mentionAffordanceTarget.display}`
    : '';
  React.useEffect(() => {
    if (!affordanceFocusKey) return;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (active && active !== document.body) return;
    promptInputRef.current?.focus({ preventScroll: true });
  }, [affordanceFocusKey, promptInputRef]);

  // Track which message has the token info popup open (by index)
  const [activeTokenInfoId, setActiveTokenInfoId] = React.useState<string | null>(null);

  // Processing tooltip state - shown when user clicks send while processing
  const [showProcessingTooltip, setShowProcessingTooltip] = React.useState(false);
  const [activeTooltipButton, setActiveTooltipButton] = React.useState<'main' | 'floating' | null>(null);

  // Message collapsing for performance - show only last N messages by default
  const [showAllMessages, setShowAllMessages] = React.useState(false);
  // Track animation state for smooth reveal of older messages
  const [isRevealing, setIsRevealing] = React.useState(false);
  const revealContainerRef = React.useRef<HTMLDivElement>(null);
  // Track pending reveal for useLayoutEffect - ensures scroll position is set BEFORE paint
  const pendingRevealRef = React.useRef<{ scrollHeightBefore: number } | null>(null);
  // I4 ("Load earlier messages", 2026-07-15): scroll-anchor capture for
  // prepending an OLDER SERVER PAGE. Standard scroll-anchor technique —
  // record geometry before the prepend, then (layout effect below) offset
  // scrollTop by the added height so the user's view doesn't jump. Unlike
  // pendingRevealRef (local reveal, same tick), the fetch is ASYNC: the
  // anchor also records which thread + top message it was captured against,
  // so a mid-fetch thread switch drops it and a mid-fetch live append merely
  // refreshes the captured geometry.
  const pendingEarlierAnchorRef = React.useRef<{
    threadId: string | null;
    topMessageId: string | undefined;
    scrollHeightBefore: number;
    scrollTopBefore: number;
  } | null>(null);
  // VISIBLE_MESSAGE_COUNT lives in collapsedHistory.ts (leaf) so the
  // collapse arithmetic is pinnable without rendering this surface.
  // Calculate hidden message count for floating button bar
  const hiddenCount = showAllMessages ? 0 : Math.max(0, messages.length - VISIBLE_MESSAGE_COUNT);
  const sendButtonRef = React.useRef<HTMLButtonElement>(null);
  const floatingSendButtonRef = React.useRef<HTMLButtonElement>(null);

  // ── Persisted view restore (returning-tab reliability, 2026-09-01) ──────
  // One restore attempt per thread arrival, armed lazily by the restore
  // layout effect below (peek is non-destructive, so StrictMode double-runs
  // and visibility-wake double-restores re-read the same marker). done=true
  // ends the attempt — resolved, fallbacks exhausted, or no marker.
  const viewRestoreRef = React.useRef<{
    threadId: string | null;
    marker: ThreadViewMarker | null;
    earlierLoads: number;
    done: boolean;
  }>({ threadId: null, marker: null, earlierLoads: 0, done: true });
  // The thread the restore EXPANDED the reveal-older container for — the
  // reveal-reset effect below must not clobber that expansion (its passive
  // reset flushes after the restore's layout write in the same commit).
  const viewRestoreExpandedRef = React.useRef<string | null>(null);
  // Render-drop breadcrumb (P0 thread-durability, 2026-09-02): the
  // contentless-assistant render skip below used to be SILENT — the exact
  // spot where a connect card whose html was blanked by the store backstop
  // vanished with no trace. One warn per message id per page session (the
  // rail-empty-tripwire idiom), never per render.
  const renderDropWarnedRef = React.useRef<Set<string>>(new Set());
  // The thread whose collapsed history was expanded because it hid a standing
  // connect tile — once per thread arrival, so a reader who collapses it again
  // is left alone.
  const autoExpandedForTileRef = React.useRef<string | null>(null);
  // The delegated transcript click listener is installed once and closes over
  // its first render's values, so a stamp that needs the CLICKED tile's
  // message must read it through a ref (P0 thread-durability, 2026-09-02 —
  // the browser-lane `opened` stamp).
  const latestMessagesRef = React.useRef<ChatMessage[]>(messages);
  React.useEffect(() => {
    latestMessagesRef.current = messages;
  }, [messages]);
  // Writer echo guard: a restore's own scrollTop write fires a scroll event;
  // the persisted writer skips samples inside this window so the echo can't
  // immediately overwrite the marker being restored.
  const persistSuppressUntilRef = React.useRef(0);

  // Reveal state is per-thread VIEW state, not chat state: without this reset
  // a "previous 12" reveal on thread A left showAllMessages=true, so thread B
  // rendered its full history expanded (2026-08 scroll redesign).
  React.useEffect(() => {
    // Restore-aware (2026-09-01): when the persisted restore expanded THIS
    // thread's history in the same commit (layout effect, before this
    // passive reset flushes), resetting would clobber the restored view.
    if (viewRestoreExpandedRef.current !== threadStore.activeThreadId) {
      setShowAllMessages(false);
    }
    setIsRevealing(false);
    pendingRevealRef.current = null;
    // Per-thread view refs reset with the view: the render-drop breadcrumb's
    // id-less fallback key carries a transcript POSITION, which means
    // something different in every thread.
    renderDropWarnedRef.current.clear();
    autoExpandedForTileRef.current = null;
  }, [threadStore.activeThreadId]);

  // COLLAPSED HISTORY NEVER HIDES A STANDING TILE (plan §3.3; review
  // 2026-09-02). The diagnosed Fitbit thread holds 15 messages and no server
  // pages older than the loaded window, so the load-earlier control — the
  // other half of D6 mechanism (a) — never renders for it: the ONLY thing
  // that puts its card back on screen is opening the thread expanded. When a
  // connect tile sits inside the collapsed region, expand through the
  // sanctioned restore idiom (no reveal animation: pendingRevealRef stays
  // null so the reveal layout effect early-returns; viewRestoreExpandedRef
  // keeps the per-thread reveal reset above from clobbering it). Once per
  // thread arrival, and a purely local VIEW change — no data is touched and
  // no message is posted (the re-offer decision is the primary's).
  React.useEffect(() => {
    const activeId = threadStore.activeThreadId;
    if (!activeId || showAllMessages) return;
    if (autoExpandedForTileRef.current === activeId) return;
    if (!collapsedRegionHoldsRow(messages.length, (i) => isRebuildableConnectCard(messages[i]))) return;
    autoExpandedForTileRef.current = activeId;
    viewRestoreExpandedRef.current = activeId;
    pendingRevealRef.current = null;
    setIsRevealing(false);
    setShowAllMessages(true);
  }, [messages, showAllMessages, threadStore.activeThreadId]);

  // Owner directive 2026-08-22 ("anytime a triage thread is working it should
  // say 'still working'"): opening or switching to a thread pokes active-task
  // discovery, so work the SERVER started in this thread (a triage analysis,
  // a routine run) lights its in-flight chip within one round-trip instead of
  // waiting for the next visibility change or poll tick. Discovery is
  // self-guarded (authed-only, reconciles stale chips) and cheap.
  React.useEffect(() => {
    if (!threadStore.activeThreadId) return;
    try { void pokeActiveTaskDiscovery?.(); } catch { /* never blocks a thread open */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadStore.activeThreadId]);

  // Handle scroll position adjustment BEFORE browser paint when revealing older messages
  // This prevents the "flash" of wrong scroll position (jumping to top then scrolling down)
  // useLayoutEffect runs synchronously after DOM mutation but BEFORE paint
  React.useLayoutEffect(() => {
    if (!pendingRevealRef.current || !showAllMessages) return;

    const el = chatMessagesRef.current;
    if (!el) {
      pendingRevealRef.current = null;
      return;
    }

    const { scrollHeightBefore } = pendingRevealRef.current;
    const scrollHeightAfter = el.scrollHeight;
    const addedHeight = scrollHeightAfter - scrollHeightBefore;

    // Set scroll position SYNCHRONOUSLY during layout phase — an instant
    // jump (ChatMessages has no CSS scroll-behavior since the 2026-08 scroll
    // redesign; animation comes from scrollToTopAnimated below). User keeps
    // seeing recent messages (their original view), not oldest messages.
    el.scrollTop = addedHeight;

    // Clear the pending flag
    pendingRevealRef.current = null;

    // Start animation immediately - no RAF delay needed since useLayoutEffect
    // already ran after DOM mutation and we've set scrollTop synchronously
    scrollToTopAnimated(() => {
      setIsRevealing(false);
    });
  }, [showAllMessages, scrollToTopAnimated]);

  // I4: preserve the viewport when an older SERVER page is prepended.
  // Runs synchronously after the DOM mutation, before paint (no flash).
  React.useLayoutEffect(() => {
    const anchor = pendingEarlierAnchorRef.current;
    if (!anchor) return;
    const el = chatMessagesRef.current;
    if (!el) {
      pendingEarlierAnchorRef.current = null;
      return;
    }
    // Thread switched mid-fetch — the anchor's geometry belongs to another
    // transcript; never adjust the new thread's scroll with it.
    if (threadStore.activeThreadId !== anchor.threadId) {
      pendingEarlierAnchorRef.current = null;
      return;
    }
    const topId = messages.length > 0 ? messages[0]?.id : undefined;
    if (topId === anchor.topMessageId) {
      // No prepend landed yet (e.g. a live append committed mid-fetch) —
      // refresh the captured geometry so the eventual prepend computes a
      // clean delta against the CURRENT layout.
      anchor.scrollHeightBefore = el.scrollHeight;
      anchor.scrollTopBefore = el.scrollTop;
      return;
    }
    pendingEarlierAnchorRef.current = null;
    const addedHeight = el.scrollHeight - anchor.scrollHeightBefore;
    if (addedHeight <= 0) return;
    // Instant jump — no CSS scroll-behavior on ChatMessages (2026-08 scroll
    // redesign), so a raw scrollTop write cannot animate into the flash this
    // anchor exists to prevent.
    el.scrollTop = anchor.scrollTopBefore + addedHeight;
  }, [messages, chatMessagesRef, threadStore.activeThreadId]);

  // ── Persisted view RESTORE (returning-tab reliability, 2026-09-01) ───────
  // Puts a returning tab back on the exact message it left, from the marker
  // threadScrollRestore persisted. Layout effect DECLARED AFTER useGlobalChat
  // so it commits after useTranscriptScroll's switch branch in the same
  // commit — the anchor-based position deterministically overrides the
  // hook's bottom/raw-px landing before paint (override-always: both
  // represent the same reading position; anchor-based survives layout
  // changes the raw px does not).
  //
  // PROD-185 discipline: every step is once-guarded through viewRestoreRef
  // (done / earlierLoads / threadId), so this effect can never supply
  // per-commit fuel to an upstream churn loop — it either advances the
  // one-shot state machine or returns without touching state.
  React.useLayoutEffect(() => {
    const activeId = threadStore.activeThreadId;
    if (!activeId) return;
    const r = viewRestoreRef.current;
    if (r.threadId !== activeId) {
      // First sight of this thread: arm (or disarm when no marker). Peek is
      // non-destructive, so a StrictMode double-run re-arms identically.
      const marker = peekThreadViewMarker(buildThreadViewKey(currentUid, config.instanceId, activeId));
      viewRestoreRef.current = { threadId: activeId, marker, earlierLoads: 0, done: !marker };
      viewRestoreExpandedRef.current = null;
    }
    const restore = viewRestoreRef.current;
    if (restore.done || !restore.marker) return;
    const el = chatMessagesRef.current;
    if (!el || messages.length === 0) return; // waits for hydration — the effect re-runs on messages
    const marker = restore.marker;

    // Resolve anchor → CURRENT array index on every pass. data-msg-nav
    // carries the messages-array INDEX, which shifts on every earlier-page
    // prepend — an index must never be cached across runs. Canonical-id
    // match via perDocMessageId (anchor beats own id), neighbors as the
    // pruned/non-rendered fallback.
    let idx = messages.findIndex((m: ChatMessage) => perDocMessageId(m) === marker.anchorMessageId);
    if (idx === -1) {
      for (const neighborId of marker.neighborIds) {
        idx = messages.findIndex((m: ChatMessage) => perDocMessageId(m) === neighborId);
        if (idx !== -1) break;
      }
    }
    if (idx === -1) {
      // Anchor beyond the loaded window: pull older server pages, bounded.
      // Called directly on the store (NOT handleLoadEarlierMessages) so
      // pendingEarlierAnchorRef stays null and the I4 anchor effect stays
      // inert — it must not fight this restore's positioning.
      if (threadStore.earlierMessagesAvailable && restore.earlierLoads < 3 && !threadStore.isLoadingEarlierMessages) {
        restore.earlierLoads += 1;
        void threadStore.loadEarlierMessages().catch(() => { /* fallback below on next pass */ });
        return; // re-runs when the prepend lands (messages change)
      }
      restore.done = true; // fallback = the hook's bottom default — never blank
      return;
    }

    const olderBoundary = Math.max(0, messages.length - VISIBLE_MESSAGE_COUNT);
    if ((marker.revealAll || idx < olderBoundary) && !showAllMessages) {
      // The anchor sits in the collapsed reveal-older region (or the reader
      // left expanded): expand WITHOUT the reveal animation — the reveal
      // layout effect early-returns on a null pendingRevealRef, so
      // scrollToTopAnimated never fires. viewRestoreExpandedRef keeps the
      // per-thread reveal-reset from clobbering this in the same commit.
      viewRestoreExpandedRef.current = activeId;
      pendingRevealRef.current = null;
      setIsRevealing(false);
      setShowAllMessages(true);
      return; // re-runs with the expanded DOM
    }

    let node = el.querySelector<HTMLElement>(`[data-msg-nav="${idx}"]`);
    if (!node || node.getBoundingClientRect().height === 0) {
      // The anchor message exists but renders no element (context-only,
      // retired notices, empty assistant bodies) — before giving up to the
      // bottom default, try the persisted neighbors' NODES the same way the
      // id fallback above tried their ids.
      node = null;
      for (const neighborId of marker.neighborIds) {
        const ni = messages.findIndex((m: ChatMessage) => perDocMessageId(m) === neighborId);
        if (ni === -1) continue;
        const candidate = el.querySelector<HTMLElement>(`[data-msg-nav="${ni}"]`);
        if (candidate && candidate.getBoundingClientRect().height > 0) {
          node = candidate;
          break;
        }
      }
      if (!node) {
        restore.done = true; // bottom default, never a throw
        return;
      }
    }
    persistSuppressUntilRef.current = Date.now() + 1500;
    // INSTANT write: the band machine decides pin state from the landing — a
    // mid-history landing unpins + shows the jump-to-latest pill (the exact
    // contract), a near-bottom landing re-pins. No programmatic marking:
    // beginProgrammatic is for smooth glides only.
    el.scrollTop += node.getBoundingClientRect().top - el.getBoundingClientRect().top - marker.offsetPx;
    restore.done = true;
    // Run the band machine SYNCHRONOUSLY: the browser's echo scroll event is
    // async, and until handleScroll flips pinnedRef the controller is still
    // pinned from the switch landing — any message commit or image-load
    // resize in that gap would re-stick the restored view to the bottom (and
    // the writer would then CLEAR the marker as "reader at tail"). One
    // synchronous dispatch closes the gap; the duplicate async echo is a
    // harmless second pass through the same math.
    try {
      el.dispatchEvent(new Event('scroll'));
    } catch {
      /* the async echo still lands — worst case is the pre-fix race window */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, showAllMessages, threadStore.activeThreadId, threadStore.earlierMessagesAvailable]);

  // ── Persisted view WRITER (returning-tab reliability, 2026-09-01) ────────
  // Samples the topmost VISIBLE message on a debounced trailing edge and
  // persists it as the layout-robust marker the restore above consumes.
  // Lives HERE, not in the scroll controller — the controller stays
  // storage-free (threadScrollingUx pins). Inputs ride a render-assigned ref
  // (sendRouteOverrideRef idiom) so the ONE listener never re-attaches per
  // commit.
  const viewWriterInputsRef = React.useRef({
    activeThreadId: threadStore.activeThreadId,
    messages,
    showAllMessages,
    uid: currentUid,
    instanceId: config.instanceId,
  });
  viewWriterInputsRef.current = {
    activeThreadId: threadStore.activeThreadId,
    messages,
    showAllMessages,
    uid: currentUid,
    instanceId: config.instanceId,
  };
  React.useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el) return undefined;
    const sample = () => {
      const { activeThreadId, messages: msgs, showAllMessages: revealAll, uid, instanceId } = viewWriterInputsRef.current;
      if (!activeThreadId) return;
      if (Date.now() < persistSuppressUntilRef.current) return; // restore echo
      const container = chatMessagesRef.current;
      if (!container) return;
      const key = buildThreadViewKey(uid, instanceId, activeThreadId);
      if (isPinnedNow()) {
        // Pinned-to-bottom = CLEAR: bottom stays the default landing, and a
        // reader who returned to the tail needs no marker.
        clearThreadViewMarker(key);
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const anchors = container.querySelectorAll<HTMLElement>('[data-msg-nav]');
      // While collapsed, ONLY the last-VISIBLE_MESSAGE_COUNT tail is really
      // on screen. The collapsed reveal-older container clips via height:0 +
      // overflow:hidden, but its CHILDREN keep full-height rects that
      // geometrically overlap the visible region (verified in Chromium) — a
      // rect filter alone would anchor markers on invisible history. Index
      // gating is the reliable visibility test here.
      const visibleFloor = revealAll ? 0 : Math.max(0, msgs.length - VISIBLE_MESSAGE_COUNT);
      for (let i = 0; i < anchors.length; i += 1) {
        const idx = Number(anchors[i].dataset.msgNav);
        if (!Number.isFinite(idx) || idx < visibleFloor) continue; // collapsed/invisible history
        const rect = anchors[i].getBoundingClientRect();
        if (rect.height === 0) continue; // renders no box (context-only, retired notices)
        if (rect.bottom <= containerRect.top + 1) continue; // fully above the viewport
        const anchorMessage = msgs[idx];
        if (!anchorMessage) return;
        // Element-relative measurement self-heals a clamped/odd scrollTop —
        // it always records a real visible message, never a raw px blob.
        saveThreadViewMarker(key, {
          anchorMessageId: perDocMessageId(anchorMessage),
          neighborIds: [msgs[idx + 1], msgs[idx + 2]]
            .filter(Boolean)
            .map((neighbor: ChatMessage) => perDocMessageId(neighbor)),
          offsetPx: rect.top - containerRect.top,
          revealAll,
          savedAt: Date.now(),
        });
        return;
      }
    };
    let debounceTimer: number | null = null;
    const onScroll = () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        sample();
      }, 500);
    };
    // Flush points: leaving the page, going hidden (the overnight-idle save),
    // and the self-heal reload's before-reload event — the marker must be on
    // disk before any of them.
    const flush = () => {
      if (debounceTimer !== null) {
        window.clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      sample();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('prest0:before-self-heal-reload', flush);
    return () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('prest0:before-self-heal-reload', flush);
    };
    // Re-arm when the container can have (re)mounted; the listener itself
    // reads live inputs through the ref (isChatOpen/activeThreadId are
    // re-attach keys, deliberately unread in the body).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessagesRef, isPinnedNow, isChatOpen, threadStore.activeThreadId]);

  // Copy-chunk affordance (owner directive 2026-08-17): after every message
  // render, decorate <pre>/<blockquote> chunks with a one-click copy button.
  // DOM-layer on purpose (both converters sanitize their HTML strings; see
  // chunkCopy.ts) and idempotent per element — dangerouslySetInnerHTML
  // re-renders wipe button + marker together, so this pass re-decorates.
  React.useEffect(() => {
    decorateCopyableChunks(chatMessagesRef.current);
  }, [messages, chatMessagesRef, threadStore.activeThreadId, showAllMessages]);

  // I4: click handler — capture the scroll anchor, then fetch + prepend.
  const handleLoadEarlierMessages = React.useCallback(() => {
    if (threadStore.isLoadingEarlierMessages) return;
    // Collapsed-history contract (P0 thread-durability, 2026-09-02 — the
    // Fitbit diagnosis: a standing connect card 12th from newest in a
    // 15-message thread sat behind the collapsed container, and this
    // control was hidden with it). The
    // control is now always reachable while collapsed; a click from the
    // collapsed state first EXPANDS local history through the restore idiom
    // (no reveal animation: pendingRevealRef stays null so the reveal layout
    // effect early-returns; viewRestoreExpandedRef keeps the per-thread
    // reveal-reset from clobbering the expansion), then fetches the server
    // page — so local history and the older page arrive in reading order.
    if (!showAllMessages && hiddenCount > 0) {
      viewRestoreExpandedRef.current = threadStore.activeThreadId;
      pendingRevealRef.current = null;
      setIsRevealing(false);
      setShowAllMessages(true);
    }
    const el = chatMessagesRef.current;
    pendingEarlierAnchorRef.current = el
      ? {
        threadId: threadStore.activeThreadId,
        topMessageId: messages.length > 0 ? messages[0]?.id : undefined,
        scrollHeightBefore: el.scrollHeight,
        scrollTopBefore: el.scrollTop,
      }
      : null;
    void threadStore.loadEarlierMessages()
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('chatThreadMessages.loadEarlier failed', error);
      })
      .finally(() => {
        // Safety clear: the prepend commit (and its layout effect) has long
        // flushed by now; a leftover anchor (e.g. empty page) must not linger
        // into unrelated future top-of-list changes.
        window.setTimeout(() => { pendingEarlierAnchorRef.current = null; }, 150);
      });
  }, [threadStore, messages, chatMessagesRef, showAllMessages, hiddenCount]);

  // Handler for clicking send button while processing is active
  const handleProcessingBlockedClick = React.useCallback((buttonType: 'main' | 'floating') => {
    if (isSystemProcessing()) {
      setActiveTooltipButton(buttonType);
      setShowProcessingTooltip(true);
    }
  }, [isSystemProcessing]);

  const dismissProcessingTooltip = React.useCallback(() => {
    setShowProcessingTooltip(false);
    setActiveTooltipButton(null);
  }, []);

  // Set up callback for when Enter key is pressed during processing
  React.useEffect(() => {
    onProcessingBlockedRef.current = (source: 'main' | 'floating') => {
      handleProcessingBlockedClick(source);
    };
    return () => {
      onProcessingBlockedRef.current = null;
    };
  }, [handleProcessingBlockedClick, onProcessingBlockedRef]);

  // Get the active tooltip anchor ref based on which button was clicked
  const activeTooltipAnchorRef = activeTooltipButton === 'floating' ? floatingSendButtonRef : sendButtonRef;

  // Close token info popup when user scrolls the chat
  React.useEffect(() => {
    const chatEl = chatMessagesRef.current;
    if (!chatEl) return;

    const handleScroll = () => {
      // Close popups on any scroll activity — their body-portaled cards are
      // fixed-positioned from measure time and would float detached from a
      // scrolled-away anchor. Covers the sender identity card too.
      if (activeTokenInfoId !== null) {
        setActiveTokenInfoId(null);
      }
      if (activeSenderCardId !== null) {
        setActiveSenderCardId(null);
      }
    };

    // Use passive listener for better scroll performance
    chatEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      chatEl.removeEventListener('scroll', handleScroll);
    };
  }, [activeTokenInfoId, activeSenderCardId, chatMessagesRef]);

  // 2026-08-14 owner report ("popups don't really come up"): the old
  // close-on-messages.length effect that lived here killed a just-opened
  // popup on ANY list churn — background sync merges, queued notices, dedupe
  // passes — usually within seconds of opening. With open-state keyed by
  // stable message id and the scroll handler above covering every genuine
  // reposition, arriving messages no longer dismiss an open popup.

  // Close token info popup when clicking outside the chat container
  React.useEffect(() => {
    if (activeTokenInfoId === null) return;

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      // Round O (O2.2): spare body-portaled chat surfaces — the popup itself
      // renders OUTSIDE chatContainerRef via createPortal, so a mousedown ON
      // the popup (e.g. the guest "Sign in" anchor) used to unmount it here
      // before the browser could fire the click, killing the navigation.
      if (isInsidePrest0nPortal(target)) return;

      // Check if click is inside the chat container
      const insideChat = chatContainerRef.current?.contains(target);
      if (!insideChat) {
        setActiveTokenInfoId(null);
      }
    };

    // Small delay to avoid closing immediately on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleGlobalClick);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [activeTokenInfoId, chatContainerRef]);

  // Event delegation for action buttons in chat messages (format, translate, etc.)
  // This approach survives React re-renders since handlers are on parent, not individual buttons
  React.useEffect(() => {
    const chatEl = chatMessagesRef.current;
    if (!chatEl) return;

    /**
     * Immediately apply button selection styles via direct DOM manipulation.
     * This provides instant visual feedback before React re-render completes.
     *
     * @param buttonElement - The button that was clicked
     * @param allButtons - All buttons in the same button group
     */
    const applyImmediateButtonSelectionState = (
      buttonElement: HTMLButtonElement,
      allButtons: HTMLButtonElement[]
    ): void => {
      allButtons.forEach((btn) => {
        if (btn === buttonElement) {
          // Selected button: ink + gold ring + checkmark + disabled to prevent
          // double-click stalls — matches applyButtonSelectionState's persisted
          // treatment (the old emerald hue was a drifted one-off).
          btn.style.background = '#1C1917';
          btn.style.boxShadow = '0 0 0 2px var(--brand-gold), 0 8px 20px rgba(198, 167, 94, 0.35)';
          btn.style.cursor = 'default';
          btn.disabled = true;

          const span = btn.querySelector('span');
          if (span && !span.textContent?.startsWith('✓')) {
            span.innerHTML = `✓ ${span.textContent || ''}`;
          }
        } else {
          // Unselected buttons: dimmed + disabled to prevent conflicting clicks
          btn.style.opacity = '0.7';
          btn.style.cursor = 'default';
          btn.disabled = true;
          btn.style.filter = 'grayscale(30%)';
        }
      });
    };

    // Helper to determine button index from button ID or DOM position
    // This is used for persisting which button was clicked for visual indication
    const getWorkflowButtonIndex = (button: Element, buttonId: string): number => {
      // Map known button IDs to indices based on order they appear in workflow action buttons
      // Button index hints - DOM fallback is authoritative for variable layouts
      const buttonIndexMap: Record<string, number> = {
        // Primary GlobalTools buttons
        'global-tool-translate-btn': 0,
        'global-tool-video-script-btn': 1,

        // Legacy buttons (backward compatibility)
        'chat-write-video-script-btn': 1,
        'chat-video-script-proceed-btn': 0,

        // Deep Research workflow buttons
        'global-tool-deep-research-skip-btn': 0,
        'global-tool-deep-research-start-btn': 1,

        // Video Script proceed button
        'global-tool-video-script-proceed-btn': 0,

        // Payment Gate buttons
        'payment-gate-pay-btn': 0,
        'payment-gate-cancel-btn': 1,
      };

      // Try exact match first
      if (buttonIndexMap[buttonId] !== undefined) {
        return buttonIndexMap[buttonId];
      }

      // Try prefix match for buttons with suffixes
      for (const [prefix, index] of Object.entries(buttonIndexMap)) {
        if (buttonId.startsWith(prefix)) {
          return index;
        }
      }

      // Fallback to DOM position - find index among sibling buttons
      const container = button.parentElement;
      if (container) {
        const buttons = Array.from(container.querySelectorAll('button'));
        const idx = buttons.indexOf(button as HTMLButtonElement);
        if (idx !== -1) return idx;
      }

      return -1;
    };

    const handleDelegatedClick = async (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // In-app links in replies (2026-09-03): a whitelisted internal href —
      // [Subscription](/subscription), [Wallet](/wallet), … — navigates
      // client-side so the chat stays open and the thread stays live, instead
      // of a full page load that rebooted the app. Modifier clicks and
      // target=_blank keep their native behavior (shouldNavigateInApp).
      const anchor = typeof target.closest === 'function'
        ? (target.closest('a[href]') as HTMLAnchorElement | null)
        : null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (shouldNavigateInApp(href, e, anchor.getAttribute('target'))) {
          e.preventDefault();
          navigate(href as string);
        }
        return;
      }
      const button = target.closest('button');
      if (!button) return;
      // Honor either an `id` attribute (legacy) OR a `data-button-id` attribute
      // (Phase 2 browser-tile inline pause UI uses data-button-id so multiple
      // tiles can render without colliding on duplicate ids).
      const resolvedButtonId = button.id || button.dataset.buttonId || '';
      if (!resolvedButtonId) return;
      // Prevent double-click: if button was already disabled by a prior click, ignore
      if ((button as HTMLButtonElement).disabled) return;
      // Workflow CTA lifecycle belt (2026-08-19): a retired CTA (workflow
      // advanced past its card) is pointer-events:none + disabled already;
      // this guard covers keyboard activation and any styling race.
      if (button.getAttribute(CTA_RETIRED_ATTR) === 'true') return;

      const buttonId = resolvedButtonId;

      // ============================================================
      // PENDING WIDGET STOP BUTTON: Cancel running operation
      // ============================================================
      if (buttonId === 'pending-widget-stop-btn') {
        e.preventDefault();
        e.stopPropagation();
        // Use cancelActiveThread (PR feat/same-thread-queue-while-streaming)
        // so the active thread's pending queue is flushed alongside the
        // in-flight abort — otherwise the drain effect would silently
        // fire queued messages after the user clicked stop.
        cancelActiveThread();
        return;
      }

      // ============================================================
      // BUG-DR-CLARIFYING-CARD-MISSING (2026-05-20) — TASK-QUEUE
      // CLARIFYING-CARD SKIP BUTTON
      //
      // The task-queue Deep Research path (Fernando + future dedicated-VM
      // tenants post-PR-169) renders a Stage-1 clarifying-questions card
      // from a backend SSE stage event. The card has a "Skip to Deep
      // Research" button with this dedicated id. Clicking it sends "skip"
      // as a programmatic user message, which the orchestrator's
      // midClarifyingFreeTextPreRoute then intercepts (because the
      // immediately-prior assistant turn has sourceTag
      // 'deep_research_qualification') and dispatches deep-research with
      // skipClarification=true. The deep-research tool (whether backend
      // executeDeepResearch via task-queue or in-browser deepResearch.ts
      // via global-tools) detects skipClarification + prior card in
      // history and bypasses the clarifying pre-stage on the next turn,
      // proceeding straight to qualification → blueprint → synthesis.
      //
      // Distinct from the legacy `global-tool-deep-research-skip-btn`
      // (which requires an in-browser awaiting-input runtime that does
      // not exist on the task-queue path).
      // ============================================================
      if (buttonId === 'task-queue-dr-clarifying-skip-btn') {
        e.preventDefault();
        e.stopPropagation();
        try {
          (button as HTMLButtonElement).disabled = true;
        } catch {
          // non-fatal
        }
        try {
          // ============================================================
          // BUG-DR-SKIP-DIRECT-DISPATCH (2026-05-21) — follow-up to
          // PR #207 (Layer D done-leak fix). PR #198 / #204 fixed the
          // skip-routing on the Cloud Functions task-queue + in-browser
          // orchestrator path via midClarifyingFreeTextPreRoute, but
          // Fernando's dedicated-VM tenant bypasses that orchestrator —
          // its messages go frontend → prest0nProcessTask → POST to
          // <vm>/prest0n/turn → web-adapter/server.mjs → openclaw →
          // OpenRouter → LLM. The VM-LLM sees bare `skip` after the
          // clarifying card and (correctly, per its training) reads it
          // as the user abandoning the request, so it emits
          // "Entendido. ¿En qué te puedo ayudar?" instead of running
          // deep-research.
          //
          // Account-agnostic fix: re-dispatch the user's ORIGINAL deep-
          // research query with an unambiguous bilingual "skip
          // clarification — run DR now" instruction appended. Works
          // on both the orchestrator path (midClarifyingFreeTextPreRoute
          // still matches and folds it as a scope-refinement note) and
          // the VM-LLM path (planner reads the explicit instruction and
          // dispatches deep-research directly).
          //
          // Sent HIDDEN so the cosmetic `skip` user-bubble that Malaika
          // flagged also stops appearing. See ./skipDirectDispatch.ts
          // for the full rationale.
          //
          // Fallback: if for any reason we can't recover the original
          // user query (e.g. the qualification card has no preceding
          // user turn in the in-memory history), fall back to the bare
          // `skip` behavior so the click is not silently dropped.
          // BUG-DR-SKIP-ORIGINAL-QUERY (2026-06-05) — recover the original DR
          // query in priority order so the skip survives THREAD FRAGMENTATION
          // (user starts a new thread between sending the prompt and clicking
          // skip; the qualification card + original prompt then live in a
          // DIFFERENT thread's in-memory history, so the history-walk below
          // returns null and we used to fall back to a bare `skip` — which the
          // planner answers on the lightweight lane: "no extensive compute",
          // zero tokens metered, zero wallet debit. Malaika Test 2 symptom).
          //   (1) the skip button's own `data-original-query` attribute
          //       (stamped at render time from the backend stage payload —
          //       self-contained, thread-fragmentation proof).
          //   (2) walk the ACTIVE thread's history (existing same-thread path).
          //   (3) bare `skip` (last resort — click never silently dropped).
          const history = refs.messagesSyncRef.current || [];
          const stampedQuery = (() => {
            try {
              const raw = (button as HTMLElement).getAttribute('data-original-query');
              const v = (raw || '').trim();
              return v || null;
            } catch {
              return null;
            }
          })();
          const originalQuery = pickOriginalQueryForSkip(
            stampedQuery,
            findOriginalUserQueryFromHistory(history),
          );
          if (originalQuery) {
            const isSpanish = detectSpanishFromQuery(originalQuery);
            const dispatchText = buildSkipDispatchMessage(originalQuery, isSpanish);
            await sendProgrammaticMessage(dispatchText, { hidden: true });
          } else {
            await sendProgrammaticMessage('skip');
          }
        } catch (err) {
          console.warn('[GlobalChatSystem] task-queue clarifying skip send failed', err);
        }
        return;
      }

      // ============================================================
      // BUG-DR-CARD-FINAL-CLEANUP (2026-05-20, PR #196 follow-up to
      // PR #195) — TASK-QUEUE CLARIFYING-CARD "Answer questions"
      // SECONDARY BUTTON.
      //
      // Malaika 2026-05-20 17:40 UTC: "bring both buttons and remove
      // questionary paragraph that is below those buttons". The
      // grey secondary CTA on the task-queue clarifying card is
      // labeled "Answer questions" (en) / "Responder preguntas" (es)
      // and its sole job is to .focus() the chat composer so the
      // user can immediately start typing their answers — NO
      // submit, NO programmatic message. The card itself stays
      // visible; the composer just gets keyboard focus. This is the
      // user-facing equivalent of pressing Tab to the input field.
      //
      // Distinct from the primary 'task-queue-dr-clarifying-skip-btn'
      // (which dispatches "skip" via sendProgrammaticMessage to
      // trigger midClarifyingFreeTextPreRoute with skipClarification).
      // ============================================================
      if (buttonId === 'task-queue-dr-clarifying-answer-btn') {
        e.preventDefault();
        e.stopPropagation();
        try {
          // Focus the composer. promptInputRef is the
          // contentEditable <PromptInput> div rendered by
          // PromptInputWrapper around line 2732 / 2922. .focus()
          // on the contenteditable surface raises the soft keyboard
          // on mobile and places the caret inside the input on
          // desktop, ready for typing.
          if (promptInputRef.current) {
            promptInputRef.current.focus({ preventScroll: true });
            // Place caret at the end if there's existing draft text
            // so the user types AFTER any pre-existing content
            // rather than overwriting it.
            try {
              const el = promptInputRef.current;
              if (el.childNodes.length > 0) {
                const range = document.createRange();
                range.selectNodeContents(el);
                range.collapse(false);
                const sel = window.getSelection();
                if (sel) {
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
              }
            } catch {
              // non-fatal caret-positioning best-effort
            }
          }
        } catch (err) {
          console.warn('[GlobalChatSystem] task-queue clarifying answer focus failed', err);
        }
        return;
      }

      // ============================================================
      // GENERALIZED CONTENT BRIDGE: Hydrate content from chat history for ALL downstream buttons
      // This ensures workflow outputs flow smoothly between agentic workflows regardless of which
      // system (GlobalTools vs legacy) produced them, and survives page refreshes
      // ============================================================
      const downstreamButtons = [
        'chat-write-video-script-btn',
        'global-tool-translate-btn',
        'global-tool-video-script-btn',
      ];

      const isDownstreamButton = downstreamButtons.some(prefix => buttonId.startsWith(prefix));

      if (isDownstreamButton) {
        // Load messages from localStorage (persisted chat history)
        const history = getFromLocalStorage<ChatMessage[]>(GLOBAL_CHAT_HISTORY_KEY, []);

        // Try to extract workflow output from history - check multiple types in priority order
        // Translation and formatted content should be checked first as they represent more processed content
        const workflowOutput = extractLatestWorkflowOutput(history, 'translation') ||
                               extractLatestWorkflowOutput(history, 'formatted_content') ||
                               extractLatestWorkflowOutput(history, 'research_report');

        if (workflowOutput) {
          // Determine source based on workflow output type
          const source = workflowOutput.type === 'research_report' ? 'research' : 'formatted';

          // Bridge to GlobalTools if available
          if (globalTools?.bridgeLegacyContent) {
            globalTools.bridgeLegacyContent({
              content: workflowOutput.content,
              title: workflowOutput.title || 'Content',
              timestamp: new Date(workflowOutput.timestamp),
              source: source as 'research' | 'formatted',
            });
          }

          // For research reports, also set lastResearchReport for backward compatibility
          if (workflowOutput.type === 'research_report' && agenticWorkflows?.setLastResearchReport) {
            agenticWorkflows.setLastResearchReport({
              content: workflowOutput.content,
              title: workflowOutput.title || 'Research Report',
              timestamp: new Date(workflowOutput.timestamp),
            });
          }

          // Set translatable content for all workflow types (handles translation → format flow)
          if (agenticWorkflows?.setLastTranslatableContent) {
            agenticWorkflows.setLastTranslatableContent({
              content: workflowOutput.content,
              title: workflowOutput.title || 'Content',
              timestamp: new Date(workflowOutput.timestamp),
              source: source,
            });
          }

          console.log('[GlobalChatSystem] Bridged workflow output:', workflowOutput.type, 'to downstream button:', buttonId);
        }

        // Fallback: Also check legacy drSession stage1Findings (for video script buttons)
        if (
          buttonId.includes('video-script') &&
          !workflowOutput &&
          agenticWorkflows?.drSessionRef?.current?.stage1Findings &&
          globalTools?.bridgeLegacyContent
        ) {
          const legacyFindings = agenticWorkflows.drSessionRef.current.stage1Findings;
          globalTools.bridgeLegacyContent({
            content: legacyFindings,
            title: 'Research Findings',
            timestamp: new Date(),
            source: 'research' as const,
          });
        }
      }

      // ============================================================
      // GlobalTools: Try GlobalTools first (handles all registered tools)
      // This is the global solution for all tool button clicks
      // ============================================================
      if (globalTools?.isAuthorized) {
        // Extract workflow output ID from button's data attribute
        // This enables explicit context passing from button to workflow
        const workflowOutputId = button.dataset.workflowOutputId;
        const workflowOutput = workflowOutputId ? getWorkflowOutput(workflowOutputId) : null;

        // Build context for handleButtonClick. Always include dataAttributes
        // (Phase 2 browser-tile inline pause UI relies on data-session-id /
        // data-field-id / data-locale being available on the click target).
        //
        // Finding 5 (2026-07-15): always carry the ACTIVE thread id at
        // click time. Button-created runtimes (Skip-to-DR, generic
        // runtime.start) must bind to the thread the user clicked in —
        // without it they fall back to the shared last-turn ref (stale or
        // null), and canRuntimeCaptureSend then rejects the user's
        // same-thread typed reply to the awaiting-input tool.
        const buttonContext = {
          workflowOutput: workflowOutput ?? null,
          buttonElement: button as HTMLButtonElement,
          dataAttributes: { ...button.dataset },
          threadId:
            (refs.getActiveThreadIdRef.current && refs.getActiveThreadIdRef.current()) || null,
        };

        if (workflowOutput) {
          console.log('[GlobalChatSystem] Passing workflow output to button handler:', {
            buttonId,
            outputId: workflowOutputId,
            source: workflowOutput.source,
          });
        }

        // LIFECYCLE `opened` STAMP (P0 thread-durability, contract C2,
        // 2026-09-02). The browser-lane handler paints "opened ✓" on the
        // element and that state dies with the page; the durable twin is
        // payload.data.lifecycle.opened on the card MESSAGE, re-rendered in
        // place so a day-old card reads "opened <time>" after any reload.
        // It is written HERE because this is the only surface that holds both
        // halves — the clicked tile's message (through the wrapper's
        // data-message-id and the messages REF, since this listener is
        // installed once and a closure over `messages` would be the first
        // render's copy) and the update dispatch. Best-effort and fenced: the
        // sign-in flow below is never blocked by it.
        try {
          // The resolve-and-stamp chain itself lives in
          // connectTileRehydrate.stampOpenedFromClick so it can be EXERCISED
          // (review 2026-09-02) rather than pinned as source text — the pin
          // that would have passed while the chain was broken. This site
          // supplies the three live references and nothing more.
          if (isConnectBrowserOpenButtonId(buttonId)) {
            stampOpenedFromClick({
              buttonEl: button,
              messages: latestMessagesRef.current,
              updateMessageById: (id, update) => dispatch({
                type: 'UPDATE_MESSAGE_BY_ID',
                payload: { id, message: update as Partial<ChatMessage> },
              }),
            });
          }
        } catch {
          // A missing tile, payload or dispatch never blocks the sign-in.
        }

        const handled = await globalTools.handleButtonClick(buttonId, buttonContext);
        if (handled) {
          e.preventDefault();
          e.stopPropagation();

          // TRUTHFUL BUTTON STATE (2026-08-24 WhatsApp repro): a handler that
          // resolved WITHOUT consuming the button (failed/needs-setup connect
          // — it restored the label for a retry) marks the element; painting
          // "✓ + disabled" and persisting a selected index over that restore
          // is how a click that did nothing ended up reading as done forever.
          if ((button as HTMLElement).dataset.prest0nKeepLive === 'true') {
            delete (button as HTMLElement).dataset.prest0nKeepLive;
            return;
          }

          // STANDING-DECISION SURFACES NEVER GET THE ONE-CHOICE PAINT
          // (2026-08-27, Miles's live repro: clicking "preview" on the batch
          // send card greyed EVERY button and persisted a wrong selection
          // index that re-greyed the card on each re-render forever). The
          // exemption registry — the same one the render lane's retirement
          // consults — marks multi-action cards that manage their own visual
          // state: the authorize/batch branches set their own labels, grey
          // their own row, and re-render the tile from the row model. The
          // click lane must honor it too, or the contract documented in
          // workflowCtaLifecycle is enforced on render and silently bypassed
          // on click.
          if (isWorkflowCtaExemptId(buttonId)) {
            return;
          }

          // Mark this button as selected in the message for visual persistence
          const messageElement = button.closest('[data-message-id]');
          if (messageElement) {
            // Get all buttons in this message for immediate styling
            const allButtons = Array.from(
              messageElement.querySelectorAll('button[id]')
            ) as HTMLButtonElement[];

            // Apply immediate visual feedback SYNCHRONOUSLY (before workflow starts)
            applyImmediateButtonSelectionState(button as HTMLButtonElement, allButtons);

            // Update state for persistence (async)
            const messageId = messageElement.getAttribute('data-message-id');
            const buttonIndex = getWorkflowButtonIndex(button, buttonId);

            if (messageId && buttonIndex !== -1) {
              dispatch({
                type: 'UPDATE_MESSAGE_BY_ID',
                payload: {
                  id: messageId,
                  message: { selectedActionButtonIndex: buttonIndex }
                }
              });
            }
          }

          return;  // GlobalTools handled the button
        }
      }

      // ============================================================
      // Legacy: Fall back to old agenticWorkflows handlers
      // ============================================================
      const helpers = agenticWorkflows?.chatHelpersRef?.current;

      // Helper to mark button as selected in the message
      const markButtonSelected = () => {
        const messageElement = button.closest('[data-message-id]');
        if (messageElement) {
          // Get all buttons in this message for immediate styling
          const allButtons = Array.from(
            messageElement.querySelectorAll('button[id]')
          ) as HTMLButtonElement[];

          // Apply immediate visual feedback SYNCHRONOUSLY
          applyImmediateButtonSelectionState(button as HTMLButtonElement, allButtons);

          // Update state for persistence (async)
          const messageId = messageElement.getAttribute('data-message-id');
          const buttonIndex = getWorkflowButtonIndex(button, buttonId);
          if (messageId && buttonIndex !== -1) {
            dispatch({
              type: 'UPDATE_MESSAGE_BY_ID',
              payload: {
                id: messageId,
                message: { selectedActionButtonIndex: buttonIndex }
              }
            });
          }
        }
      };

      // If authorized and helpers available, call handlers directly
      // NOTE: Translate buttons now handled by GlobalTools translateTool above
      if (agenticWorkflows?.isAuthorized && helpers) {
        if (buttonId === 'chat-write-video-script-btn-stage1') {
          e.preventDefault();
          e.stopPropagation();
          markButtonSelected();
          // Stage 1 handler reads data from drSessionRef - no data attributes needed
          await handleWriteVideoScriptFromStage1(helpers, agenticWorkflows);
          return;
        } else if (buttonId.startsWith('chat-write-video-script-btn')) {
          e.preventDefault();
          e.stopPropagation();
          markButtonSelected();
          await handleWriteVideoScript(helpers, agenticWorkflows);
          return;
        } else if (buttonId.startsWith('chat-video-script-proceed-btn')) {
          e.preventDefault();
          e.stopPropagation();
          markButtonSelected();
          await handleVideoScriptProceed(helpers, agenticWorkflows);
          return;
        }
      }

      // Fallback: dispatch custom events for page components to handle (backwards compatible)
      // NOTE: Translate buttons now handled by GlobalTools, no custom event fallback needed
      if (buttonId.startsWith('chat-write-video-script-btn')) {
        document.dispatchEvent(new CustomEvent('chat-action', { detail: { action: 'write-video-script' } }));
      }
    };

    // DIALOG-DRIVEN CONNECT COMPLETION (2026-08-25 adversarial review): a
    // needs-setup connect finishes through the app-wide setup dialog, not a
    // button click, so no handler resolves against the card — its "connect
    // <app>" button stayed live and invited a redundant second OAuth right
    // after the user connected. Flip the newest matching card to the same
    // done state a direct click paints, and persist the selection so it
    // survives reloads.
    const handleSetupConnected = (e: Event) => {
      const slug = String((e as CustomEvent).detail?.toolkitSlug || '').trim().toLowerCase();
      if (!/^[a-z0-9_-]{1,64}$/.test(slug)) return;
      const candidates = Array.from(
        chatEl.querySelectorAll(`button[id="global-tool-connect-app-btn-${CSS.escape(slug)}"]`)
      ) as HTMLButtonElement[];
      const button = candidates[candidates.length - 1];
      if (!button) return;
      delete button.dataset.prest0nKeepLive;
      button.textContent = '✓ connected';
      const messageElement = button.closest('[data-message-id]');
      if (!messageElement) return;
      const allButtons = Array.from(
        messageElement.querySelectorAll('button[id]')
      ) as HTMLButtonElement[];
      applyImmediateButtonSelectionState(button, allButtons);
      const messageId = messageElement.getAttribute('data-message-id');
      const buttonIndex = getWorkflowButtonIndex(button, button.id);
      if (messageId && buttonIndex !== -1) {
        dispatch({
          type: 'UPDATE_MESSAGE_BY_ID',
          payload: { id: messageId, message: { selectedActionButtonIndex: buttonIndex } },
        });
      }
    };
    document.addEventListener('prest0n:connect-setup-connected', handleSetupConnected as EventListener);

    chatEl.addEventListener('click', handleDelegatedClick);
    return () => {
      chatEl.removeEventListener('click', handleDelegatedClick);
      document.removeEventListener('prest0n:connect-setup-connected', handleSetupConnected as EventListener);
    };
  }, [chatMessagesRef, agenticWorkflows, globalTools, navigate]);

  // ============================================================================
  // Render Messages
  // ============================================================================

  const buildCopyText = React.useCallback((message: ChatMessage): string => {
    const base = message.html ? htmlToPlainText(message.html) : (message.text || '');
    const attachments =
      (message.attachments?.map((f) => f.name) ?? []) ||
      (message.attachmentsSerialized?.map((f) => f.name) ?? []);
    const attachmentLine = attachments.length ? `Attachments: ${attachments.join(', ')}` : '';
    return [base, attachmentLine].filter(Boolean).join('\n\n');
  }, []);

  const copyMessageToClipboard = React.useCallback((message: ChatMessage) => {
    const content = buildCopyText(message);
    if (!content) return;

    const textareaFallbackCopy = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = content;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('aria-hidden', 'true');
        document.body.appendChild(textarea);
        textarea.focus({ preventScroll: true });
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    };

    const tryClipboardApi = async () => {
      try {
        if (typeof window !== 'undefined' && window.isSecureContext && navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(content);
          return true;
        }
      } catch {
        // Fall through to fallback
      }
      return false;
    };

    tryClipboardApi().then((ok) => {
      if (ok) return;
      textareaFallbackCopy();
    });
  }, [buildCopyText]);

  const renderMessages = (): {
    olderElements: React.ReactNode[];
    recentElements: React.ReactNode[];
    pendingOverlay: ChatMessage | null;
  } => {
    const confirmationTs = waitlistController.confirmationTimestamp;
    let bubbleRendered = false;
    const olderElements: React.ReactNode[] = [];
    const recentElements: React.ReactNode[] = [];
    let pendingOverlay: ChatMessage | null = null;

    // Workflow CTA lifecycle (2026-08-19): per-thread busy flag for the
    // latest-actionable-only rule — same derivation the follow-up
    // suggestions use (per-thread map, global flag as the no-thread fallback).
    const ctaThreadProcessing = threadStore.activeThreadId
      ? Boolean(state.processingByThread?.[threadStore.activeThreadId])
      : state.isProcessing;

    // Calculate the boundary between "older" and "recent" messages
    // Older messages will be animated when revealed
    const olderBoundary = Math.max(0, messages.length - VISIBLE_MESSAGE_COUNT);

    // Flash fix (owner UX directive 2026-08-22): rows are keyed on identity
    // that survives store convergence (see messageRowIdentity.ts), never the
    // array index — the merges insert and re-sort, and index keys made React
    // re-render shifted rows as brand-new elements, replaying the entry
    // animation. The entry animation itself is gated to rows that just
    // arrived by their own stamp.
    const seenRowKeys = new Set<string>();
    const nowForEntryAnimation = Date.now();

    // Track indices separately for older and recent for stagger animation
    let olderIndex = 0;
    let recentIndex = 0;
    // Speaker-aware rhythm (2026-08 reading-comfort round): the turn key of
    // the last row that ACTUALLY rendered. Tracking the rendered row (rather
    // than messages[i-1]) means context-only rows and the overlay-hoisted
    // pending message never leave a phantom turn break behind them.
    let lastRenderedTurnKey: string | null = null;

    messages.forEach((message: ChatMessage, messageIndex: number) => {
      // Determine if this message is "older" (hidden by default) or "recent" (always visible)
      const isOlderMessage = messageIndex < olderBoundary;
      const targetArray = isOlderMessage ? olderElements : recentElements;
      const elementIndex = isOlderMessage ? olderIndex++ : recentIndex++;
      const rowKey = stableMessageRowKey(message, messageIndex, seenRowKeys);
      const animateEntry = shouldAnimateMessageEntry(message, nowForEntryAnimation);
      // Skip context-only messages (e.g., document analysis) - included in AI context but hidden from UI
      if (message.isContextOnly) {
        return;
      }

      // Stored standalone developer notices ("… is not available on this
      // lane.") are phantom bubbles from a fixed server bug — suppressed at
      // render, same discipline as the stored-bubble html heal (2026-08-28).
      if (isRetiredDevNoticeMessage(message)) {
        return;
      }

      // Hide the synthetic user prompt that triggered the onboarding welcome — only the
      // assistant's streamed response should be visible to the user.
      if (message.sourceTag === 'system:onboarding_welcome' && message.isUser) {
        return;
      }

      // Detach pending widgets from chat bubbles (render outside message flow)
      // These appear as floating overlays at the bottom of the chat area.
      //
      // 2026-08-12 (exactly-one-indicator invariant, turnIndicator.ts): the
      // old hardcoded sourceTag list here meant any pending-shaped message
      // minted under an unlisted tag (`prest0n_tool_start_*` rotations, the
      // structured-render "composing reply" placeholders) fell through to the
      // INLINE render lane below — a second animated spinner next to this
      // overlay. Detection is now SHAPE-based: every active pending-shaped
      // row is extracted into this single overlay lane (newest wins; the
      // rest stay out of the flow), so at most one indicator ever renders.
      // Stopped widgets and attachment-carrying messages are content and are
      // never extracted (see isActivePendingShapedMessage).
      if (isActivePendingShapedMessage(message)) {
        pendingOverlay = message;
        return; // Skip from main message flow - will render as overlay
      }

      // EMPTY ASSISTANT MESSAGES NEVER RENDER (generalized 2026-08-19, owner
      // screenshot: a blank Prest0n bubble frame below a real reply). This
      // subsumes the four sourceTag-specific skips that used to live here
      // (research_thinking*, attachment_read_*, stage1_assessment_*,
      // video_script_*_pending): ANY non-user message with no text, no html,
      // no attachments, no action buttons, and no title is an artifact of an
      // interrupted/empty stream — chrome, not content. Active pending
      // shapes were already extracted into the overlay above, so nothing
      // live is affected; user messages are never touched (an empty user
      // bubble would hide what the user believes they sent).
      if (
        !message.isUser &&
        (!message.text || message.text.trim() === '') &&
        (!message.html || message.html.trim() === '') &&
        !(message.attachments && message.attachments.length > 0) &&
        !(message.attachmentsSerialized && message.attachmentsSerialized.length > 0) &&
        !(message.actionButtons && message.actionButtons.length > 0) &&
        !(message.workflowActionButtonsHtml && message.workflowActionButtonsHtml.trim() !== '') &&
        !(message.title && String(message.title).trim() !== '')
      ) {
        // Breadcrumb (P0 thread-durability): a payload-bearing message that
        // lands here lost its card html AND escaped the payload rebuild —
        // exactly the silent-vanish class. Once per id per page session; the
        // id-less fallback key carries the thread, the position, the source
        // tag and the timestamp (review 2026-09-02: a bare `idx:7` silenced
        // every later id-less drop at position 7, in this thread and every
        // thread the user switched to — in the one place that exists to stop
        // a silent drop). The set is also cleared on thread switch.
        const dropKey = typeof message.id === 'string' && message.id
          ? message.id
          : [
            'idx',
            threadStore.activeThreadId || '',
            messageIndex,
            message.sourceTag || '',
            message.timestamp instanceof Date ? message.timestamp.getTime() : '',
          ].join(':');
        if (!renderDropWarnedRef.current.has(dropKey)) {
          renderDropWarnedRef.current.add(dropKey);
          // eslint-disable-next-line no-console
          console.warn('[GlobalChatSystem] render-drop: contentless assistant message', {
            id: message.id,
            sourceTag: message.sourceTag,
            payloadKind: message.payload?.kind,
            cardHtmlDropped: message.cardHtmlDroppedForFirestore === true,
            position: messageIndex,
            total: messages.length,
          });
        }
        return;
      }

      // Speaker-aware rhythm: stamp the FIRST rendered row of each turn so CSS
      // opens the wider between-speakers gap (see styles.TranscriptContent).
      // A message can emit several rows (routine banner + bubble); only the
      // first carries the mark, so a banner never gets separated from the
      // message it belongs to.
      const turnKey = messageTurnKey(message, currentUid);
      let turnMarkUnused = turnKey !== lastRenderedTurnKey;
      lastRenderedTurnKey = turnKey;
      const turnStartAttr = (): 'true' | undefined => {
        if (!turnMarkUnused) return undefined;
        turnMarkUnused = false;
        return 'true';
      };

      // Render the confirmation bubble before messages that came after it
      if (
        !bubbleRendered &&
        confirmationTs &&
        waitlistController.success &&
        config.showWaitlistBubble &&
        new Date(message.timestamp).getTime() > confirmationTs
      ) {
        targetArray.push(
          <ConstructionWaitlistBubble key="confirmation-bubble" controller={waitlistController} />
        );
        bubbleRendered = true;
      }

      const hasFileAttachments = message.attachments && message.attachments.length > 0;
      const hasSerializedOnly = !hasFileAttachments && message.attachmentsSerialized && message.attachmentsSerialized.length > 0;

      // Detect if this is a pending widget message (should render without bubble)
      // Global: Any message with the pendingSpin animation renders without the message box
      // Also detect stopped widgets (cancelled operations) which should render standalone
      const isPendingWidget =
        (message.html?.includes('pendingSpin') && !message.attachments?.length) || // Active pending widget
        message.html?.includes('stopped-widget-wrapper'); // Stopped/cancelled widget

      // Detect if this is a special message tile (has action buttons, explicit messageType, or workflow buttons)
      // ANSWER-FIRST (2026-08-20): pure answer cards (declined offer + zero
      // questions) carry NO buttons, so the workflowActionButtonsHtml gate
      // alone would drop them into a plain bubble around the card chrome.
      // The two DR card sourceTags are tile-owned shapes regardless of
      // button presence.
      // Shared predicate (turnAttribution.ts) so the one-ⓘ-per-turn rule and
      // this dispatch can never disagree about what a "card" is.
      // (2026-08-22: translation results used to earn the tile via their
      // attached buttons; the buttons are gone, the tile look stays.)
      const isSpecialTile = isSpecialTileMessage(message);

      // CSS variable for stagger animation (used by AnimatedRevealContainer)
      const revealStyle = isOlderMessage ? { '--reveal-index': elementIndex } as React.CSSProperties : undefined;

      if (isPendingWidget) {
        // Render pending widget without message bubble styling
        targetArray.push(
          <div
            key={rowKey}
            data-turn-start={turnStartAttr()}
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
              marginBottom: '0.75rem',
              ...revealStyle,
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: ensureLinksOpenInNewTab(normalizeChatHtml(message.html || '')),
              }}
            />
          </div>
        );
      } else if (isSpecialTile && !message.isUser) {
        // Render as SpecialMessageTile for messages with action buttons
        targetArray.push(
          <MessageContainer key={rowKey} isUser={false} style={revealStyle} data-msg-nav={messageIndex} data-turn-start={turnStartAttr()}>
            <div style={{ maxWidth: '80%', width: '100%' }} data-message-id={message.id}>
              <SpecialMessageTile
                text={message.text}
                html={message.html}
                messageId={message.id}
                messageType={message.messageType || getMessageTypeFromSourceTag(message.sourceTag)}
                priority={message.priority}
                title={message.title}
                actionButtons={message.actionButtons}
                timestamp={message.timestamp}
                showTimestamp={false}
                attachments={message.attachments}
                attachmentsSerialized={message.attachmentsSerialized}
                isStreaming={message.sourceTag?.includes('streaming')}
                tokenUsage={message.tokenUsage}
                tokenUsageSnapshot={message.tokenUsageSnapshot}
                tokenUsages={message.tokenUsages}
                tokenUsageSnapshots={message.tokenUsageSnapshots}
                workflowActionButtonsHtml={message.workflowActionButtonsHtml}
                sourceTag={message.sourceTag}
                onCopyMessage={() => copyMessageToClipboard(message)}
                tags={message.tags}
                selectedActionButtonIndex={message.selectedActionButtonIndex}
                ctaLive={isWorkflowCtaLive({
                  messageId: message.id,
                  messages,
                  threadProcessing: ctaThreadProcessing,
                })}
                /* ONE ⓘ PER TURN (owner directive 2026-09-01): a card whose
                   usage is an inherited copy of this turn's series defers its
                   ⓘ to the answer bubble that owns it; a card-only turn keeps
                   its own. Pure rule in turnAttribution.ts. */
                suppressAttribution={cardAttributionOwnedByTurnAnswer(messages, message, currentUid)}
              />
            </div>
          </MessageContainer>
        );
      } else {
        // Normal message with bubble styling.
        // SENDER IDENTITY (owner directive 2026-07-24): every message says who
        // sent it — Prest0n included — in the BOTTOM meta row next to the other
        // message details, with a pop-out identity card. Alignment follows the
        // VIEWER: their own messages right, everyone else's (humans AND Prest0n)
        // left, because a business thread has several humans in it. Resolution is
        // pure and ungated: a message with no senderId (solo threads, legacy
        // bubbles) is the viewer's own, so those threads keep rendering as
        // before, now labelled explicitly.
        const senderIdentity = resolveMessageSender({
          message,
          currentUid,
          profiles: senderProfiles,
          selfLabel: selfDisplayLabel,
        });
        const displayAsUser = senderIdentity.side === 'right';
        const senderCardKey = message.id || `idx:${messageIndex}`;
        const senderChip = (
          <MessageSenderChip
            identity={senderIdentity}
            isActive={activeSenderCardId === senderCardKey}
            onToggle={() => {
              setActiveSenderCardId(activeSenderCardId === senderCardKey ? null : senderCardKey);
              setActiveTokenInfoId(null);
            }}
          />
        );
        // Routine Threads (2026-07-31): an assistant message stamped with
        // `routineRun` (a routine's scheduled execution) gets a small banner
        // chip ABOVE its bubble — glyph + "Routine · {name} · {when}" — so a
        // run is recognizable at a glance while scrolling the thread.
        if (!message.isUser && message.routineRun) {
          targetArray.push(
            <div
              key={`${rowKey}-routine-run`}
              data-turn-start={turnStartAttr()}
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                marginBottom: '2px',
                ...revealStyle,
              }}
            >
              <RoutineRunBannerChip data-routine-run-banner="">
                <RoutineGlyph size={11} />
                {/* Round 2 (2026-08-02): the words live in their own inline
                    box (RoutineRunBannerText) because text-overflow cannot
                    ellipsize a flex container's anonymous text item — long
                    routine names hard-clipped mid-glyph at docked widths. */}
                <RoutineRunBannerText>
                  {['Routine', message.routineRun.name, routinePastTimeLabel(message.routineRun.scheduledForIso)]
                    .filter(Boolean).join(' · ')}
                </RoutineRunBannerText>
              </RoutineRunBannerChip>
            </div>
          );
        }
        targetArray.push(
          <MessageContainer key={rowKey} isUser={displayAsUser} style={revealStyle} data-msg-nav={messageIndex} data-turn-start={turnStartAttr()}>
            <Message isUser={displayAsUser} $animate={animateEntry}>
              {message.html ? (
                <div
                  dangerouslySetInnerHTML={{
                    // USER bubbles run the render-time heal for stored pre-fix
                    // pasted markup (giant empty off-screen bubbles, invisible
                    // white-on-gold text — owner report 2026-08-27), tested
                    // against this bubble's ACTUAL ground. Assistant html is
                    // untouched: its classes/widget styles are legitimate.
                    __html: ensureLinksOpenInNewTab(normalizeChatHtml(
                      message.html,
                      message.isUser
                        ? { userBubble: { background: displayAsUser ? 'var(--brand-gold)' : 'var(--brand-canvas)' } }
                        : undefined,
                    )),
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    // WS-0 0.7 (review F4): ASSISTANT messages without stored
                    // html (server durable writes no longer author it) render
                    // through the SAME markdown converter as the live stream,
                    // so a reload never downgrades formatting and external
                    // citation links keep their hrefs (autoLinkText's guest
                    // policy strips external markdown links to label-only).
                    // User bubbles stay on autoLinkText: plain text + links.
                    __html: renderMessageBodyHtml(
                      message.text || '',
                      displayAsUser,
                      displayAsUser ? autoLinkText : markdownToHtml,
                    ),
                  }}
                />
              )}

              {/*
                Point 6 (mid-stream hard-refresh data-loss fix): an in-flight
                turn that was streaming when the tab was hard-refreshed is
                marked `interrupted` on reload (see interruptedTurn.ts). The run
                lived in the now-destroyed tab and cannot resume itself, so we
                surface a one-click Regenerate that re-sends the user's original
                (preserved) prompt through the normal send path. Strictly
                additive and gated on `interrupted` — no other bubble changes.
              */}
              {message.interrupted && message.regeneratePrompt && (
                <button
                  type="button"
                  aria-label={message.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG
                    ? 'Retry sending this message'
                    : 'Regenerate interrupted answer'}
                  title={message.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG
                    ? 'Retry this send'
                    : 'Regenerate this answer'}
                  onClick={() => {
                    const prompt = (message.regeneratePrompt || '').trim();
                    if (!prompt) return;
                    // Round J: send-failure bubbles resend WITHOUT re-appending
                    // the user bubble (it already exists in the thread);
                    // interrupted-turn regenerates keep appending a fresh one.
                    void sendProgrammaticMessage(
                      prompt,
                      message.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG
                        ? { skipUserBubble: true }
                        : undefined,
                    );
                  }}
                  style={{
                    marginTop: '10px',
                    padding: '6px 14px',
                    border: '1px solid rgba(198,167,94,0.45)',
                    borderRadius: '999px',
                    background: '#fff',
                    color: '#8a6d2f',
                    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {message.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG ? 'Retry' : 'Regenerate'}
                </button>
              )}

              {/* Unified attachment rendering with variant-based styling.
                  P3d: follow the bubble's DISPLAY side so another participant's
                  attachment renders received (left), not as the viewer's own. */}
              {(hasFileAttachments || hasSerializedOnly) && (
                <AttachmentsSection
                  sourceTag={message.sourceTag}
                  attachments={message.attachments}
                  attachmentsSerialized={message.attachmentsSerialized}
                  isUser={displayAsUser}
                  messageId={message.id}
                  messageText={message.text}
                  /* Origin thread (docked-companion round, 2026-08-24): the
                     transcript on screen IS the active thread, so the file's
                     conversation is known here for free — no message→thread
                     lookup exists (or is needed). Rides with the preview so
                     "open chat" can return to this exact conversation later,
                     from a floating window on another page days after. */
                  threadId={threadStore.activeThreadId ?? undefined}
                  threadTitle={activeThreadRecord?.title}
                />
              )}

              {/*
                Footer row policy for synthetic bubbles:
                - system:* / system bubbles keep the fully-suppressed footer
                  (not real turns; timestamp/actions are pure noise).
                - 'welcome' bubbles render a MINIMAL footer — ONLY the ⓘ
                  (owner directive, Miles 2026-07-13: the intro message
                  always carries the info icon, on every page/view/account —
                  especially when AI composed it). No timestamp, no copy
                  button: the no-noise rationale stands; the ⓘ is mandatory.
                  Usage-less static welcomes get the popup's "no extensive
                  compute" fallback.
                Real user/assistant turns keep the full footer (timestamp +
                processing time + actions).
              */}
              {(() => {
                const tag = message.sourceTag || '';
                const isSystemBubble =
                  tag.startsWith('system:') ||
                  tag === 'system';
                if (isSystemBubble) return null;
                if (tag === 'welcome') {
                  return (
                    <MessageTime>
                      {senderChip}
                      <TokenInfoAction
                        message={message}
                        index={messageIndex}
                        isActive={activeTokenInfoId === (message.id || `idx:${messageIndex}`)}
                        onToggle={() => {
                          const key = message.id || `idx:${messageIndex}`;
                          setActiveTokenInfoId(activeTokenInfoId === key ? null : key);
                          setActiveSenderCardId(null);
                        }}
                      />
                    </MessageTime>
                  );
                }
                return (
              <MessageTime>
                {senderChip}
                {/* Docked meta density (owner feedback, Miles 2026-08-01):
                    the row is nowrap (MessageTime/styles.ts), so at docked
                    width the timestamp compacts — time-only for today,
                    "MM/DD · time" otherwise — with the FULL timestamp as the
                    title tooltip. Fullscreen/floating keep the full label +
                    the generic "time sent" tooltip. Rule pinned in
                    dockedDensity.formatMessageMetaTimestamp. */}
                {(() => {
                  const metaTs = formatMessageMetaTimestamp({
                    docked: dockedPanelDensity,
                    timestamp: message.timestamp,
                  });
                  return (
                    <span title={metaTs.title} data-message-meta-timestamp="">
                      {metaTs.label}
                    </span>
                  );
                })()}
                {/* Display AI model processing time when available */}
                {(() => {
                  const responseTimeMs =
                    message.tokenUsage?.responseTimeMs ??
                    message.tokenUsageSnapshot?.responseTimeMs;
                  const formattedTime = formatProcessingTime(responseTimeMs);
                  if (!message.isUser && formattedTime) {
                    return (
                      <ProcessingTime title="compute time">
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>{formattedTime}</span>
                      </ProcessingTime>
                    );
                  }
                  return null;
                })()}
                <CopyIconButton
                  aria-label="copy message"
                  title="copy message"
                  onClick={() => copyMessageToClipboard(message)}
                >
                  <svg viewBox="0 0 24 24">
                    <rect x="9" y="9" width="11" height="11" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </CopyIconButton>
                <TokenInfoAction
                  message={message}
                  index={messageIndex}
                  isActive={activeTokenInfoId === (message.id || `idx:${messageIndex}`)}
                  onToggle={() => {
                    const key = message.id || `idx:${messageIndex}`;
                    setActiveTokenInfoId(activeTokenInfoId === key ? null : key);
                    setActiveSenderCardId(null);
                  }}
                />
                {/* Show test tag badge to the right of info icon */}
                {message.tags?.includes('test example') && (
                  <span
                    style={{
                      marginLeft: '6px',
                      padding: '2px 6px',
                      fontSize: '0.65rem',
                      fontWeight: 500,
                      color: '#f59e0b',
                      background: 'rgba(245, 158, 11, 0.1)',
                      borderRadius: '4px',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                    }}
                    title="Tagged as test data"
                  >
                    test example
                  </span>
                )}
              </MessageTime>
                );
              })()}
            </Message>
          </MessageContainer>
        );
      }
    });

    // If bubble wasn't rendered yet (no messages after it, or no messages at all), render at end
    // Add to recentElements since it should always be visible
    if (!bubbleRendered && waitlistController.visible && config.showWaitlistBubble) {
      recentElements.push(
        <ConstructionWaitlistBubble key="confirmation-bubble" controller={waitlistController} />
      );
    }

    // Hide pending overlay if a newer message exists (so it disappears when next message arrives)
    // Exclude certain message types that shouldn't trigger hiding:
    // - Other pending overlays (they're transient states themselves)
    // - Status messages (informational, not content)
    // - Completed placeholders (empty after finalization)
    if (pendingOverlay) {
      const overlayMsg = pendingOverlay as ChatMessage;
      const pendingTs =
        overlayMsg.timestamp instanceof Date
          ? overlayMsg.timestamp.getTime()
          : new Date(overlayMsg.timestamp as unknown as string).getTime();

      // Tag families + invisibility rules live in pendingOverlayGate.ts
      // (2026-07-14: invisible context-only messages and the persisted
      // greeting could null the overlay while the widget stayed extracted
      // from the flow — a blank pane with work still running).
      const hasNewer = messages.some((m) => {
        if (!messageCanHidePendingOverlay(m)) return false;
        const ts =
          m.timestamp instanceof Date ? m.timestamp.getTime() : new Date(m.timestamp as unknown as string).getTime();
        return ts > pendingTs;
      });
      // Owner report 2026-08-20 (RingCentral repro): a card delivered to the
      // thread's store while the pane was away is NEWER than the live
      // widget's mint time — suppressing here hid a STILL-RUNNING turn's
      // only indicator (the row is already extracted from the flow, so it
      // rendered nowhere). The thread's processing flag is the liveness
      // truth: while it is up, the indicator stays.
      const threadStillWorking = Boolean(
        state.processingByThread?.[threadStore.activeThreadId || ''],
      );
      if (hasNewer && !threadStillWorking) {
        pendingOverlay = null;
      }
    }

    return { olderElements, recentElements, pendingOverlay };
  };

  // ============================================================================
  // Render Prompt Bar (Inside Chat)
  // ============================================================================
  // Attachment preview surface lives inline as <AttachmentPreviewOverlay />
  // rendered inside <ChatFooter /> directly above the composer prompt bar
  // (see below). The previous floating popup anchored to the attach button
  // was retired 2026-05-11 because its anchor math + portaled positioning
  // produced an awkward floating panel that hovered over message history
  // instead of the user-expected strip just above the composer.

  const renderPromptBarInChat = () => (
    <>
      {/* Test Mode Indicator — shown when dev/preview auto-detect or admin
          toggle is active. NEVER in an exempt organization (task #76). */}
      {isTestModeActive && !trialChrome.trialExempt && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '5px 12px',
          marginBottom: '6px',
          background: '#F0FDF4',
          border: '1px solid #BBF7D0',
          borderRadius: '8px',
          fontSize: '0.75rem',
          color: '#166534',
        }}>
          <span>🧪</span>
          <span>Test Mode — wallet charges disabled</span>
        </div>
      )}
      {/* Balance chrome (2026-08-14): NOTHING renders while the balance is
          healthy; one light nudge when low (<= auto top-up threshold); the
          out-of-work-units explanation when the wallet is empty. */}
      <TestModeBanner
        balanceUsd={lowBalanceState.balanceUsd}
        isCritical={chatOutOfWorkUnits}
        isTestingMode={lowBalanceState.isTestingMode}
        shouldShow={chatOutOfWorkUnits || chatLowBalanceNudge}
        hasPaymentMethod={paymentMethodStatus.hasPaymentMethod}
        onNavigate={() => handleTopUpRequest(
          paymentMethodStatus.hasPaymentMethod ? 'add-tokens' : 'add-payment'
        )}
        interactionKey={`${isChatOpen}-${messages.length}`}
      />
      <PromptBarContainer
        ref={promptBarRef}
        data-testid="chat-prompt-bar"
        style={{
          borderRadius: 0,
          boxShadow: 'none',
          position: 'relative',
          bottom: 'auto',
          left: 'auto',
          transform: 'none',
          width: '100%',
          maxWidth: '100%',
          zIndex: 1151,
          opacity: 1,
          visibility: 'visible',
          animation: 'none',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DragOverlay $isDragging={isDragging}>
        <AttachmentSvgIcon />
        <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>Drop files here to attach</div>
      </DragOverlay>

      {/* One-keystroke mention affordance (owner directives 2026-08-11 →
          2026-08-12 placement round): at the LEFT of the draft — where the
          mention will land — in mention-gated group threads. Shows who one
          click (or one Tab press) will mention: Prest0n after a Prest0n turn,
          else the human who spoke last. Hidden whenever the draft already
          addresses the target or the thread doesn't gate. */}
      {mentionAffordanceTarget && (
        <MentionTabChip
          type="button"
          data-testid="mention-tab-chip"
          title={`press Tab to mention ${mentionAffordanceTarget.display}`}
          aria-label={`mention ${mentionAffordanceTarget.display} — or press Tab`}
          onClick={insertAffordanceMention}
        >
          {mentionAffordanceTarget.display}
          <MentionTabKeycap aria-hidden="true">⇥</MentionTabKeycap>
        </MentionTabChip>
      )}

      {/* Wrapper div to capture clicks on blocked prompt */}
      <div
        onClick={(lowBalanceState.shouldBlock || trialExpiredBlock) ? handleBlockedPromptClick : undefined}
        style={{
          display: 'contents',
          ...((lowBalanceState.shouldBlock || trialExpiredBlock) && { cursor: 'pointer' }),
        }}
      >
        {/* Composer auto-grow: PromptInputWrapper's hidden ::after replica
            (content: attr(data-replicated-value), styles.ts) shares the grid
            cell with the contentEditable; the wrapper is as tall as the
            TALLER of the two. attr() renders as LITERAL TEXT under pre-wrap,
            so this must be the PLAIN TEXT of the prompt — feeding it
            promptHtml (raw markup source) made the invisible replica wrap to
            far more lines than the visible content after rich pastes, pinning
            a caret-unreachable blank band under the pasted text (owner
            report 2026-07-14). */}
        <PromptInputWrapper data-replicated-value={prompt || ''}>
          <PromptInput
            ref={promptInputRef}
            contentEditable={!lowBalanceState.shouldBlock && !trialExpiredBlock}
            suppressContentEditableWarning
            role="textbox"
            aria-label="Message input"
            onFocus={handlePromptFocus}
            onBlur={handlePromptBlur}
            onInput={(e) => {
              handleInput(e);
              // Read-only re-detect of an in-progress "@mention" from the caret;
              // never mutates the composer, no-op when the mentions flag is off.
              mentionAutocomplete.handleInput();
            }}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              // The autocomplete gets first refusal on nav/select/escape keys while
              // its dropdown is open (so Enter picks a member instead of sending);
              // otherwise the key falls straight through to the composer's own
              // send/keymap handler. Inert (returns false) when the flag is off.
              if (mentionAutocomplete.handleKeyDown(e)) return;
              // Bare Tab with the mention affordance visible inserts the
              // mention (2026-08-11) — the chip's keycap discloses the remap.
              if (handleAffordanceKeyDown(e)) return;
              handleKeyDown(e, 'main');
            }}
            dir="ltr"
            data-placeholder={lowBalanceState.shouldBlock ? "you're out of PRO Work Units — that's why sending is unavailable. top up or redeem a code to resume instantly." : (summonMentionPlaceholder ?? config.placeholder)}
            style={{
              ...(lowBalanceState.shouldBlock && {
                opacity: 0.5,
                cursor: 'not-allowed',
                pointerEvents: 'none',
              }),
            }}
          />
        </PromptInputWrapper>
      </div>

      {/* @-mention autocomplete dropdown (P2b follow-up). Self-portals to
          document.body and positions itself just above the composer via the
          input's rect; renders nothing unless the mentions flag is on AND an
          "@query" is active, so it is inert in production. */}
      <ThreadMentionAutocompleteDropdown
        enabled={mentionAutocomplete.enabled}
        open={mentionAutocomplete.open}
        candidates={mentionAutocomplete.candidates}
        activeIndex={mentionAutocomplete.activeIndex}
        anchorRect={mentionAutocomplete.anchorRect}
        onSelect={mentionAutocomplete.onSelect}
        onHover={mentionAutocomplete.onHover}
      />

      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={config.acceptedFileTypes}
        aria-label="File attachment"
      />

      {config.allowAttachments && (
        <div ref={attachAnchorRef} style={{ position: 'relative' }}>
          {/*
            Fix 2 (revised 2026-05-11 per Miles): the floating popup
            previously anchored to the attach button has been replaced
            with an inline horizontal preview strip rendered directly
            above the composer (see <AttachmentPreviewOverlay /> inside
            <ChatFooter />). The strip is in normal document flow, sits
            visually "in front of" the threads band, and shows real
            filename + size + thumbnail/icon + remove (×) per chip.
            The badge on this attach button still reflects the live
            attachments.length count.
          */}
          <ActionButton
            title={lowBalanceState.shouldBlock ? "Top up pr0 to attach files" : "Attach file"}
            onClick={lowBalanceState.shouldBlock ? undefined : handleAttachmentButtonClick}
            aria-label="Attach file"
            disabled={lowBalanceState.shouldBlock}
            style={{
              position: 'relative',
              background: attachments.length > 0 ? '#f0f9ff' : 'white',
              borderColor: attachments.length > 0 ? '#90cdf4' : '#eee',
              color: attachments.length > 0 ? '#3182ce' : '#666',
              ...(lowBalanceState.shouldBlock && {
                opacity: 0.4,
                cursor: 'not-allowed',
              }),
            }}
          >
            <AttachmentSvgIcon />
            {attachments.length > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#3182ce',
                  color: 'white',
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {attachments.length}
              </span>
            )}
          </ActionButton>
        </div>
      )}

      {/* Wrapper to capture clicks on disabled send button */}
      <div
        onClick={() => handleProcessingBlockedClick('main')}
        style={{ display: 'contents' }}
      >
        <SendButton
          ref={sendButtonRef}
          title={lowBalanceState.shouldBlock ? "Top up pr0 to send messages" : (isSystemProcessing() ? "Processing in progress" : "Send message")}
          onClick={() => { void handleSendWithDraftHydration(); }}
          aria-label="Send message"
          disabled={(!getCurrentPlainText() && !state.attachments.length && placeholderAttachments.length === 0) || isSystemProcessing() || lowBalanceState.shouldBlock}
          style={{
            opacity: ((!getCurrentPlainText() && !state.attachments.length && placeholderAttachments.length === 0) || isSystemProcessing() || lowBalanceState.shouldBlock) ? 0.5 : 1,
            cursor: (isSystemProcessing() || lowBalanceState.shouldBlock) ? 'not-allowed' : 'pointer',
          }}
        >
          <SendSvgIcon />
        </SendButton>
      </div>
      </PromptBarContainer>
    </>
  );

  // ============================================================================
  // Render Floating Prompt Bar (When Chat is Closed)
  // ============================================================================

  const renderFloatingPromptBar = () => {
    const isDocked = isChatDockedRight && (!isMobileViewport || config.forceDockButtonOnMobile) && !config.disableDocking;
    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      bottom: CHAT_LAYOUT.BOTTOM_OFFSET,
      borderRadius: 16,
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      zIndex: 1100,
      opacity: 1,
      visibility: 'visible',
      cursor: 'pointer',
      transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease',
      flexWrap: 'nowrap',
      /* On mobile the stylesheet's phone tiers (<=720px / <=360px) own
         gap/width — inline values here would override them and defeat the
         fold-cover tier (review 2026-08-14). Desktop keeps the inline
         geometry unchanged. */
      gap: isMobileViewport ? undefined : '0.5rem',
      width: isDocked ? CHAT_LAYOUT.DOCKED_WIDTH : (isMobileViewport ? undefined : CHAT_LAYOUT.WIDTH),
      maxWidth: isDocked ? '92vw' : (isMobileViewport ? undefined : CHAT_LAYOUT.WIDTH),
    };

    const positionStyle: React.CSSProperties = isDocked
      ? {
          // Same 24px inset as the open docked pane (styles.ts `right: 24px`)
          // so open ↔ closed never shifts horizontally.
          right: '24px',
          left: 'auto',
          transform: 'translateY(0)',
        }
      : {
          left: '50%',
          transform: 'translateX(-50%)',
        };

    const handleMouseEnter = (el: HTMLDivElement) => {
      el.style.transform = isDocked ? 'translateY(-2px)' : 'translateX(-50%) translateY(-2px)';
      el.style.boxShadow = '0 12px 40px rgba(0,0,0,0.16)';
    };

    const handleMouseLeave = (el: HTMLDivElement) => {
      el.style.transform = isDocked ? 'translateY(0)' : 'translateX(-50%) translateY(0)';
      el.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
    };

    const activeThreadLabel = (() => {
      if (!threadStore.activeThreadId) return 'thread';
      const active = threadStore.threads.find((t) => t.id === threadStore.activeThreadId);
      return active?.title || 'thread';
    })();

    return (
      <PromptBarContainer
        ref={floatingPromptBarRef}
        $alignRight={isDocked}
        onClick={handleOpen}
        data-testid="floating-prompt-bar"
        style={{ ...baseStyle, ...positionStyle }}
        onMouseEnter={(e) => handleMouseEnter(e.currentTarget)}
        onMouseLeave={(e) => handleMouseLeave(e.currentTarget)}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DragOverlay $isDragging={isDragging}>
        <AttachmentSvgIcon />
        <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>Drop files here to attach</div>
      </DragOverlay>

      {threadSwitcherEnabled && threadStore.ready && (
        isDocked ? (
          /*
            Docked minimized pill (density round, Miles 2026-08-01): ONE
            compact ellipsized thread-name label + input + send. The label is
            non-interactive (pointer-events: none) — the WHOLE pill is the
            open target, so nothing overlaps and every hit target stays
            comfortable. The full-size chip + "+" pair crowded the ~420px
            pill; new-thread stays one click away inside the reopened panel
            (header ⋯ menu / chip strip).
          */
          <MinimizedThreadLabel
            data-testid="minimized-thread-label"
            aria-label={`active thread ${activeThreadLabel}`}
          >
            {activeThreadLabel}
          </MinimizedThreadLabel>
        ) : (
        <div
          data-testid="floating-thread-affordance"
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            flex: '0 0 auto',
            /* Mobile: the typing area outranks the thread hint (owner
               directive 2026-08-14) — the strip inside the opened chat is
               the real thread surface; this label is only a closed-state
               hint. */
            maxWidth: isMobileViewport ? '45%' : '48%',
            minWidth: 0,
            /* Belt-and-suspenders with the chip/label caps below: nothing
               inside this hint may ever paint over the input beside it. */
            overflow: 'hidden',
          }}
        >
          <ThreadChipButton
            type="button"
            $active
            data-testid="floating-thread-active-chip"
            aria-label={`active thread ${activeThreadLabel}`}
            title={activeThreadLabel}
            /* The chip's own 220px max-width EXCEEDED this container's cap,
               so it overflowed the box and painted straight over the input
               placeholder (owner device screenshot 2026-08-14). Chip and
               label both yield to the container; the label ellipsizes. */
            style={{ maxWidth: '100%', minWidth: 0, flex: '0 1 auto' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
          >
            <ThreadChipLabel style={{ maxWidth: '100%', minWidth: 0 }}>{activeThreadLabel}</ThreadChipLabel>
          </ThreadChipButton>
          <ThreadNewButton
            type="button"
            data-testid="floating-thread-new-button"
            aria-label="start new thread"
            title="new thread"
            onClick={(e) => {
              e.stopPropagation();
              flushCurrentDraftNow(threadStore.activeThreadId);
              threadStore.newThread();
              handleOpen();
            }}
          >
            +
          </ThreadNewButton>
        </div>
        )
      )}

      {/* One-keystroke mention affordance — same left-of-the-draft chip as the
          docked composer (2026-08-12 placement round), so the gesture is
          identical wherever the user types. */}
      {mentionAffordanceTarget && (
        <MentionTabChip
          type="button"
          data-testid="mention-tab-chip-floating"
          title={`press Tab to mention ${mentionAffordanceTarget.display}`}
          aria-label={`mention ${mentionAffordanceTarget.display} — or press Tab`}
          onClick={insertAffordanceMention}
        >
          {mentionAffordanceTarget.display}
          <MentionTabKeycap aria-hidden="true">⇥</MentionTabKeycap>
        </MentionTabChip>
      )}

      {/* Wrapper div to capture clicks on blocked prompt */}
      <div
        onClick={(lowBalanceState.shouldBlock || trialExpiredBlock) ? handleBlockedPromptClick : undefined}
        style={{
          display: 'contents',
          ...((lowBalanceState.shouldBlock || trialExpiredBlock) && { cursor: 'pointer' }),
        }}
      >
        {/* Plain text only — the ::after replica renders attr() literally;
            markup source would inflate the wrapper (see the main composer). */}
        <PromptInputWrapper
          $collapsed
          data-role="prompt-input"
          data-replicated-value={prompt || ''}
          style={{ flex: '1 1 auto', minWidth: 0 }}
        >
          <PromptInput
            ref={promptInputRef}
            contentEditable={!lowBalanceState.shouldBlock && !trialExpiredBlock}
            suppressContentEditableWarning
            role="textbox"
            aria-label="Message input"
            onFocus={handlePromptFocus}
            onBlur={handlePromptBlur}
            onInput={handleInput}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              // Same Tab-mention affordance as the docked composer (2026-08-11).
              if (handleAffordanceKeyDown(e)) return;
              handleKeyDown(e, 'floating');
            }}
            dir="ltr"
            data-placeholder={lowBalanceState.shouldBlock ? "you're out of PRO Work Units — that's why sending is unavailable. top up or redeem a code to resume instantly." : (summonMentionPlaceholder ?? config.placeholder)}
            style={{
              ...(lowBalanceState.shouldBlock && {
                opacity: 0.5,
                cursor: 'not-allowed',
                pointerEvents: 'none',
              }),
            }}
          />
        </PromptInputWrapper>
      </div>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={config.acceptedFileTypes}
        aria-label="File attachment"
      />

      {/* Attachment panel is only shown in the in-chat prompt bar (renderPromptBarInChat)
          where it's anchored to the attachment button. The floating bar has no attachment
          button, so showing the panel here would leave it orphaned and mispositioned. */}

      {/* Wrapper to capture clicks on disabled send button */}
      <div
        onClick={() => handleProcessingBlockedClick('floating')}
        style={{ display: 'contents' }}
      >
        <SendButton
          ref={floatingSendButtonRef}
          title={lowBalanceState.shouldBlock ? "Top up pr0 to send messages" : (isSystemProcessing() ? "Processing in progress" : "Send message")}
          onClick={() => { void handleSendWithDraftHydration(); }}
          aria-label="Send message"
          disabled={(!getCurrentPlainText() && !state.attachments.length && placeholderAttachments.length === 0) || isSystemProcessing() || lowBalanceState.shouldBlock}
          style={{
            opacity: ((!getCurrentPlainText() && !state.attachments.length && placeholderAttachments.length === 0) || isSystemProcessing() || lowBalanceState.shouldBlock) ? 0.5 : 1,
            cursor: (isSystemProcessing() || lowBalanceState.shouldBlock) ? 'not-allowed' : 'pointer',
          }}
        >
          <SendSvgIcon />
        </SendButton>
      </div>
      </PromptBarContainer>
    );
  };

  // ============================================================================
  // Memoized Message Rendering (for performance)
  // ============================================================================

  const renderedContent = React.useMemo(() => {
    return renderMessages();
    // dockedPanelDensity is a dep (2026-08-01): the meta-row timestamp is
    // mode-aware (formatMessageMetaTimestamp), so entering/leaving the
    // docked panel must re-render the footer labels.
  }, [messages, showAllMessages, waitlistController.confirmationTimestamp, activeTokenInfoId, dockedPanelDensity]);

  // Message-step bubbles (2026-08-12 scrolling round): show whenever the
  // thread has at least two visible messages to walk between — in EVERY
  // mode (floating / docked / fullscreen), not only after scrolling.
  const threadNavVisible = React.useMemo(
    () => messages.filter((m: ChatMessage) => !m.isContextOnly).length >= 2,
    [messages],
  );

  // ── Touch stepper wake/sleep (2026-08 reading-comfort round) ──
  // The phone cluster is invisible and click-through until the reader shows
  // intent (a scroll, or a step of their own), then fades back out. That is
  // what keeps it from repeating the 2026-08-14 complaint of controls parked
  // permanently over the message text.
  //
  // Deliberately cheap in state terms: `awakeRef` mirrors the flag so the
  // scroll handler sets state only on the false→true EDGE, never per scroll
  // event. Per-scroll setState is precisely the update-churn class that
  // PROD-185 (the Regenerate red-screen) was about, and this listener runs on
  // the same element as the scroll controller's own.
  const [navClusterAwake, setNavClusterAwake] = React.useState(false);
  // The "12 / 40" readout wakes ONLY on an actual step. Scrolling wakes the
  // buttons (you may want to reach for them) but must not flash a position
  // counter at someone who is just reading.
  const [stepReadoutOn, setStepReadoutOn] = React.useState(false);
  const navAwakeRef = React.useRef(false);
  const stepReadoutRef = React.useRef(false);
  const navSleepTimerRef = React.useRef<number | null>(null);

  const wakeNavCluster = React.useCallback((fromStep = false) => {
    if (!navAwakeRef.current) {
      navAwakeRef.current = true;
      setNavClusterAwake(true);
    }
    if (fromStep && !stepReadoutRef.current) {
      stepReadoutRef.current = true;
      setStepReadoutOn(true);
    }
    if (navSleepTimerRef.current !== null) window.clearTimeout(navSleepTimerRef.current);
    navSleepTimerRef.current = window.setTimeout(() => {
      navAwakeRef.current = false;
      setNavClusterAwake(false);
      if (stepReadoutRef.current) {
        stepReadoutRef.current = false;
        setStepReadoutOn(false);
      }
    }, NAV_CLUSTER_SLEEP_MS);
  }, []);

  React.useEffect(() => () => {
    if (navSleepTimerRef.current !== null) window.clearTimeout(navSleepTimerRef.current);
  }, []);

  React.useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el || !state.isChatOpen) return undefined;
    const onScroll = () => wakeNavCluster();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [state.isChatOpen, chatMessagesRef, wakeNavCluster]);

  // A thread with both edges satisfied at once isn't scrollable — there is
  // nothing to step through, so the cluster stays out of the way entirely.
  const threadIsScrollable = !(isAtThreadTop && isAtThreadBottom);
  const navClusterAwakeEffective = navClusterAwake && threadNavVisible && threadIsScrollable;
  // The readout rides the same wake timer as the controls, and only after a
  // step has actually happened — it is an aid for that gesture, not chrome.
  const stepReadoutVisible = stepReadoutOn && !!stepPosition && threadNavVisible;

  // ── Keyboard stepping (desktop) ──
  // Alt+↑/↓ works anywhere in the chat, including while composing — stepping
  // back to re-read something mid-draft is a real move. Bare J/K only outside
  // an editable, so typing the letters never navigates.
  React.useEffect(() => {
    if (!state.isChatOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      const editable = !!target && (
        target.isContentEditable
        || target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
      );
      const withinChat = !!target && !!chatContainerRef.current?.contains(target);
      if (!withinChat) return;

      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        wakeNavCluster(true);
        if (e.key === 'ArrowUp') stepToPreviousMessage();
        else stepToNextMessage();
        return;
      }
      if (editable || e.altKey || e.shiftKey) return;
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        wakeNavCluster(true);
        stepToPreviousMessage();
      } else if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        wakeNavCluster(true);
        stepToNextMessage();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state.isChatOpen, chatContainerRef, stepToPreviousMessage, stepToNextMessage, wakeNavCluster]);

  // ── Pane-owns-active clock signal: REMOVED (owner override, Miles
  // 2026-08-01) ─────────────────────────────────────────────────────────────
  // History: the 2026-07-15 rule derived `activePaneOverlayVisible`
  // (`paneOverlayVisible && pendingOverlay`) here and handed it to the chip
  // strip + fullscreen sidebar so the ACTIVE thread's summary suppressed its
  // own elapsed clock while the pane overlay owned it ("one clock per
  // thread"). The 2026-08-01 override reverses that: the summary row shows
  // the working spinner + elapsed clock in ALL views/modes INCLUDING the
  // active thread while the pane overlay is visible — the row is the
  // at-a-glance status surface regardless of where the user is, and two
  // clocks (pane + row) are intentional. Both surfaces ignore the (still
  // accepted, deprecated) prop, so nothing derives or passes the signal
  // anymore.

  // ── Uniform in-summary activity indicator (owner directive, Miles
  // 2026-07-14 — supersedes the 2026-07-12 "pane-owns-active" rail rule) ──
  // The chip strip and the fullscreen sidebar receive the per-thread
  // processing map plus the per-thread turn-start epochs; every processing
  // thread's chip/row — active included — renders one spinner + ticking
  // elapsed clock. Elapsed-clock epoch: the STABLE per-thread turn-start
  // (`turnStartedAtByThread`, stamped once on each turn's rising edge), NOT
  // `processingStartedAtByThread` — the latter is re-stamped mid-turn to keep
  // the safety-timeout floor fresh, which reset the in-chip clock to ~0 every
  // time a new streaming bubble started ("working · 0:00" stuck / freeze
  // after ~4-5s). See threadIsolation.ts.

  // ============================================================================
  // Main Render
  // ============================================================================

  // P3 (persistent mount): hidden mode renders nothing but every hook above
  // stays mounted — conversation, drafts, thread store, and in-flight streams
  // survive excluded routes (/auth, /onboarding) instead of being reset.
  if (config.hidden) {
    return null;
  }

  return (
    <>
      {/* Inject streaming animation CSS */}
      <style dangerouslySetInnerHTML={{ __html: STREAMING_PULSE_CSS }} />

      {/* Inject processing tooltip animation CSS */}
      <style dangerouslySetInnerHTML={{ __html: PROCESSING_TOOLTIP_CSS }} />
      
      {/* Inject pending widget animation CSS (for researching/formatting placeholders) */}
      <PendingWidgetGlobalStyles />

      {/* Chat Container (Expanded View) */}
      <ChatContainer
        $isOpen={isChatOpen}
        $isDocked={isChatDockedRight}
        $isFullScreen={isChatFullScreen}
        $zIndex={config.zIndex}
        ref={chatContainerRef}
        className={className}
        data-testid="global-chat-container"
      >
        {/* T3 (thread-workflow UX): fullscreen thread sidebar — same store,
            same orderThreads, same handlers as the chip strip. Hidden <=820px
            (CSS); the chip strip serves compact surfaces and mobile.
            Returning-tab reliability (2026-09-01): while the store hydrates
            (!ready) the rail renders a same-geometry skeleton instead of
            NOTHING — a blank rail on a returning tab read as "my threads are
            gone". */}
        {isChatFullScreen && threadSwitcherEnabled && !threadStore.ready && (
          <ThreadSidebarSkeleton aria-busy="true" aria-label="getting your threads back" data-testid="thread-rail-skeleton">
            <SidebarSkeletonLabel>getting your threads back…</SidebarSkeletonLabel>
            <SidebarSkeletonRow />
            <SidebarSkeletonRow />
            <SidebarSkeletonRow />
            <SidebarSkeletonRow />
            <SidebarSkeletonRow />
          </ThreadSidebarSkeleton>
        )}
        {isChatFullScreen && threadSwitcherEnabled && threadStore.ready && (
          <ThreadSidebar
            threads={visibleThreads}
            activeThreadId={threadStore.activeThreadId}
            activeWorkspaceId={prest0nSessionAccountId}
            /* Extended view (owner directive 2026-08-19 — supersedes #909's
               removal): unfiltered list for the org-grouped extended view;
               foreign rows run the sealed switch, never an in-place open. */
            allThreads={threadStore.allWorkspaceThreads}
            onOpenForeignThread={openForeignThread}
            /* Round 3 (2026-08-01): the SAME membership-derived participant
               counts the header's "Just you"/"N people" line reads — the
               sidebar's "group threads" section is derived from this ONE
               server-synced source, so the two can never disagree. */
            participantsByThread={sharedThreadParticipants}
            processingByThread={state.processingByThread}
            turnStartedAtByThread={state.turnStartedAtByThread}
            activeThreadMessageCount={activeThreadUserMessageCount}
            onSwitch={(threadId, options) => {
              flushCurrentDraftNow(threadStore.activeThreadId);
              // options (routine list parity, 2026-08-02): server-only
              // routine rows pass { fallbackTitle } for navigation hydration.
              threadStore.switchThread(threadId, options);
            }}
            onNew={() => {
              flushCurrentDraftNow(threadStore.activeThreadId);
              threadStore.newThread();
            }}
            onRequestDelete={handleRequestDeleteThread}
            onDeleteEmptyThread={handleDeleteEmptyThread}
            onTogglePin={threadStore.togglePinThread}
            /* Footer (fullscreen exit + account menu, Miles 2026-07-13):
               identity from AuthContext; wallet + settings routes both exist
               in src/App.tsx; sign out is the AuthContext logout the Header
               uses. onExitFullScreen is handleCycleMode — the SAME handler
               as the header's mode-cycle button, which in fullscreen (the
               only mode this sidebar renders in) exits to the default view. */
            account={sidebarAccount}
            onNavigateWallet={() => navigate('/wallet')}
            onNavigateSettings={() => navigate('/settings')}
            onSignOut={maybeAuth?.logout}
            onExitFullScreen={handleCycleMode}
            /* Routine drift fix (2026-08-01): the manager panel patches the
               LOCAL routine mirror through the store so pause/resume and the
               authoritative list snapshot flip the rail + header too. */
            onRoutineMirrorPatch={(threadId, patch) =>
              threadStore.patchThreadRoutineMirror?.(threadId, patch)}
            /* Adversarial review 2026-08-03 — finding 5: scope the shared
               account-routines snapshot cache to the signed-in account, so a
               fullscreen toggle remount reuses it (no flicker, no extra Cloud
               Function call) and switching accounts can never reuse it. */
            accountUid={currentUid}
            /* Finding 4: the routine union must not surface a routine whose
               thread this device just archived — the durable tombstone map is
               the same source writeThreadToServer and the shared-thread
               surfacing path already fail closed on. */
            isThreadTombstoned={(threadId) =>
              isThreadTombstonedInStorage(config.instanceId, currentUid, threadId)}
          />
        )}
        {config.showHeader && (
          <ChatHeader>
            {/* Header priority rule (docked-mode UX, Miles 2026-07-13): the
                action buttons are invariant; the TITLE is what shrinks. The
                styled wrappers carry the flex/min-width:0 contract — see
                HeaderTitleGroup/HeaderTitleColumn/HeaderActions in styles.ts. */}
            {(() => {
              // T2 (thread-workflow UX): the subtitle slot always answers
              // "which thread am I in?" when threads are on — in every view.
              // Falls back to the configured subtitle otherwise.
              const activeRecord = threadSwitcherEnabled && threadStore.ready
                ? threadStore.threads.find((t) => t.id === threadStore.activeThreadId) || null
                : null;
              // THREAD-FIRST HEADER (owner directive 2026-07-24): a thread is a
              // room several people talk in, so the header must not be branded
              // as Prest0n — that reads as "Prest0n is always the one
              // messaging". With a thread open we show the THREAD (renameable
              // title + how many people are in it) and drop the logo/wordmark
              // entirely; per-message identity now says who is speaking. With no
              // thread (config-driven / embedded surfaces, e.g. the guest
              // widget, which may be white-labelled) the configured header is
              // untouched.
              if (activeRecord) {
                const participantCount = sharedThreadParticipants[activeRecord.id] ?? 1;
                // Routine Threads (2026-07-31): a routine thread's header
                // carries ONE subtle meta line — glyph + "Routine ·
                // {scheduleSummary} · {next-run relative}" (paused →
                // "Paused") — plus, since 2026-08-01, the single inline
                // pause/resume chip (RoutineHeaderControls; production
                // feedback: a paused routine offered NO way to resume).
                // Non-routine threads are untouched. The label prefers the
                // routine's NAME over the thread title (naming coherence —
                // routineThreadDisplayName; title-only for normal threads).
                const routinePaused = activeRecord.routineStatus === 'paused';
                // Docked two-line contract (density round, 2026-08-01):
                // line 1 = title (+ the 3-button rail), line 2 = compact
                // routine meta OR "N people" — never both, and never "Just
                // you" (noise when solo). Fullscreen/floating keep the full
                // stack. Pure rule: shouldShowParticipantsLine.
                const participantsLineVisible = shouldShowParticipantsLine({
                  docked: dockedPanelDensity,
                  participantCount,
                  isRoutineThread: !!activeRecord.routineId,
                });
                return (
                  <HeaderTitleGroup>
                    <HeaderTitleColumn>
                      <ThreadHeaderLabel
                        title={routineThreadDisplayName(activeRecord)}
                        onRename={(next) => renameThreadWithCanonicalSync(activeRecord.id, next)}
                      />
                      {/* Workflow tags for the active shared thread. Edit
                          affordances appear only when the participant registry
                          says this user may write here (a `viewer` gets the
                          same chips, read-only) — the server re-checks anyway. */}
                      <ThreadTagChips
                        enabled={threadTags.enabled}
                        tags={threadTags.activeTags}
                        ownerLabel={(userId) => threadMemberLabels[userId] || userId}
                        onCycleStatus={threadTags.canEditActive ? threadTags.onCycleStatus : undefined}
                        onCyclePriority={threadTags.canEditActive ? threadTags.onCyclePriority : undefined}
                        onCycleOwner={threadTags.canEditActive ? threadTags.onCycleOwner : undefined}
                        onRemoveLabel={threadTags.canEditActive ? threadTags.onRemoveLabel : undefined}
                        onAddLabel={threadTags.canEditActive ? threadTags.onAddLabel : undefined}
                      />
                      {activeRecord.routineId && (
                        <RoutineHeaderControls
                          threadId={activeRecord.id}
                          routineId={activeRecord.routineId}
                          routineCount={activeRecord.routineCount}
                          paused={routinePaused}
                          scheduleSummary={activeRecord.routineScheduleSummary}
                          nextRunAt={activeRecord.routineNextRunAt}
                          compact={dockedPanelDensity}
                          onRoutineMirrorPatch={(threadId, patch) =>
                            threadStore.patchThreadRoutineMirror?.(threadId, patch)}
                        />
                      )}
                      {(participantsLineVisible || threadSummonSetting.gatesThisThread) && (
                        <HeaderSubtitleRow>
                          {participantsLineVisible && (
                            <HeaderSubtitle>{participantCountLabel(participantCount)}</HeaderSubtitle>
                          )}
                          {/*
                            Summon-mode pill (owner feedback 2026-08-07): the @
                            toggle alone could not tell the user which mode the
                            thread is in — an icon-only control has no ambient
                            state. This pill names the mode in words, in BOTH
                            states, right under the title; it shares its
                            visibility rule (gatesThisThread) and its vocabulary
                            with the toggle tooltips, so the two can never
                            disagree. It is also the visible home for a rejected
                            setting write (`notice`), which previously surfaced
                            only in a hover tooltip.
                          */}
                          {threadSummonSetting.gatesThisThread && (
                            <HeaderSummonPill
                              data-summon-pill={
                                threadSummonSetting.notice
                                  ? 'error'
                                  : threadSummonSetting.requireMention !== false
                                    ? 'required'
                                    : 'open'
                              }
                            >
                              {threadSummonSetting.notice
                                ? threadSummonSetting.notice
                                : threadSummonSetting.requireMention !== false
                                  ? (getUserUiLanguage() === 'es'
                                      ? 'Prest0n solo responde cuando lo mencionan'
                                      : 'Prest0n replies only when mentioned')
                                  : (getUserUiLanguage() === 'es'
                                      ? 'Prest0n responde a todos los mensajes'
                                      : 'Prest0n replies to every message')}
                            </HeaderSummonPill>
                          )}
                        </HeaderSubtitleRow>
                      )}
                    </HeaderTitleColumn>
                  </HeaderTitleGroup>
                );
              }
              return (
                <HeaderTitleGroup>
                  <ChatHeaderLogoImage
                    src={config.headerLogoUrl}
                    alt={`${config.headerTitle || 'Prest0n'} logo`}
                  />
                  <HeaderTitleColumn>
                    <ChatTitle $accentColor={config.accentColor || undefined}>
                      {config.headerTitleElement || (
                        <>Prest<img src={config.headerLogoUrl} alt="0" style={{ display: 'inline-block', height: '1.1em', verticalAlign: 'baseline', objectFit: 'contain' as const, marginLeft: '-0.08em', marginRight: '-0.08em', filter: 'drop-shadow(0 2px 6px rgba(198,167,94,0.15))' }} />n</>
                      )}
                    </ChatTitle>
                    {config.headerSubtitle ? <HeaderSubtitle>{config.headerSubtitle}</HeaderSubtitle> : null}
                  </HeaderTitleColumn>
                </HeaderTitleGroup>
              );
            })()}
            <HeaderActions>
              {/*
                Docked density round (owner feedback, Miles 2026-08-01): at
                docked width the rail is THREE invariant buttons max — the ⋯
                overflow below + mode-cycle (expand) + hide (minimize). The
                secondary actions (pin, archive, add-people, new-thread,
                clear) fold into the body-portaled ThreadHeaderOverflowMenu,
                wired to the SAME handlers as the wide rail so the two can
                never diverge. Fullscreen/floating keep the full rail.
              */}
              {(() => {
                const activeRecord = threadSwitcherEnabled && threadStore.ready
                  ? threadStore.threads.find((t) => t.id === threadStore.activeThreadId) || null
                  : null;
                if (headerRailDensity) {
                  return (
                    <ThreadHeaderOverflowMenu
                      threadId={activeRecord ? activeRecord.id : null}
                      threadTitle={activeRecord ? routineThreadDisplayName(activeRecord) : ''}
                      pinned={activeRecord?.pinned === true}
                      onTogglePin={threadStore.togglePinThread}
                      /* ARCHIVE-ALWAYS (owner directive 2026-08-20,
                         supersedes the 2026-08-19 blank-slate protection):
                         every thread is archivable, consistently — archiving
                         the last one boots a fresh greeted draft (see
                         removeThreadAndReturnFallback's empty-fallback). */
                      canArchive={!!activeRecord}
                      onRequestArchive={handleHeaderArchiveActiveThread}
                      addPeopleEnabled={threadAddPeople.enabled}
                      summonToggleEnabled={threadSummonSetting.gatesThisThread}
                      summonRequireMention={threadSummonSetting.requireMention}
                      onToggleSummonRequirement={threadSummonSetting.setRequireMention}
                      onAddPeople={threadAddPeople.open}
                      /* New-thread lives here in docked mode because the
                         chip strip hides itself under 2 threads
                         (dockedDensity.shouldShowThreadStrip) — creation
                         must stay reachable at every docked state. */
                      onNewThread={threadSwitcherEnabled && threadStore.ready
                        ? () => {
                            flushCurrentDraftNow(threadStore.activeThreadId);
                            threadStore.newThread();
                          }
                        : undefined}
                      showClear={showTopLevelClearButton}
                      clearLabel={clearChatTitle}
                      clearAriaLabel={clearChatAriaLabel}
                      onClear={handleClearChat}
                    />
                  );
                }
                return (
                  <>
                    {/*
                      Round 3 (2026-08-01): pin + archive for the ACTIVE
                      thread — first in the rail, same invariant
                      HeaderIconButton skin as the siblings. Pin calls the
                      SAME threadStore.togglePinThread the row/chip pins use;
                      archive routes through the SAME confirm/empty-delete
                      flow as the row × (handleHeaderArchiveActiveThread),
                      and the archive button follows the row ×'s last-thread
                      rule (canArchive).
                    */}
                    {activeRecord && (
                      <ThreadHeaderActionButtons
                        threadId={activeRecord.id}
                        threadTitle={routineThreadDisplayName(activeRecord)}
                        pinned={activeRecord.pinned === true}
                        onTogglePin={threadStore.togglePinThread}
                        canArchive={true}
                        onRequestArchive={handleHeaderArchiveActiveThread}
                        notifyMode={activeThreadNotifyMode ?? undefined}
                        onCycleNotifyMode={handleCycleThreadNotifyMode}
                      />
                    )}
                    {/*
                      Add-people trigger (group threading, flag-gated
                      testing-only). Lives in the header rail (2026-07-19
                      feedback: the chat footer had too many bubbles — thread
                      chips, working indicator — so the membership affordance
                      moved up here), first in the rail so it sits left of
                      the dock/hide controls. Same invariant HeaderIconButton
                      as its siblings; the modal it opens is a fixed overlay,
                      unaffected by the trigger's position.
                    */}
                    {threadAddPeople.enabled && (
                      <HeaderIconButton
                        onClick={threadAddPeople.open}
                        data-add-people-trigger=""
                        aria-label="add people to this thread"
                        title="add people"
                      >
                        <AddPeopleSvgIcon />
                      </HeaderIconButton>
                    )}
                    {/*
                      "@Prest0n mention required" toggle (W3b, 2026-08-06).

                      Shipped in the docked ⋯ overflow only, which meant the
                      control did not exist in fullscreen or floating — the
                      two modes this rail serves. Reported from live QA
                      2026-08-07: the thread header offered no way to see or
                      change the setting at all.

                      Same handler and same visibility rule as the overflow
                      copy (gatesThisThread — on for this account AND ≥2
                      humans in the thread), so the two headers cannot
                      disagree about whether the control exists or what it
                      currently reads.
                    */}
                    {threadSummonSetting.gatesThisThread && (
                      <HeaderIconButton
                        onClick={() =>
                          threadSummonSetting.setRequireMention(
                            !(threadSummonSetting.requireMention !== false),
                          )
                        }
                        data-header-summon-toggle={
                          threadSummonSetting.requireMention !== false ? 'required' : 'always'
                        }
                        aria-pressed={threadSummonSetting.requireMention !== false}
                        aria-label="require an @Prest0n mention for Prest0n to reply in this thread"
                        title={
                          /**
                           * The failure notice rides the tooltip rather than a
                           * new surface. The hook has always produced `notice`
                           * on a rejected write and NOTHING rendered it, so a
                           * setting that silently refused to save looked
                           * identical to one that saved — which is how the
                           * server-side persistence bug survived QA (live QA
                           * 2026-08-07: the control "turned gray again from
                           * orange" with no explanation).
                           */
                          threadSummonSetting.notice
                            ? threadSummonSetting.notice
                            : threadSummonSetting.requireMention !== false
                              ? 'Prest0n replies only when mentioned'
                              : 'Prest0n replies to every message'
                        }
                        data-header-summon-error={threadSummonSetting.notice ? '' : undefined}
                      >
                        {/* Distinct glyph per state (owner feedback 2026-08-07):
                            plain @ = mention required; slashed @ = mention no
                            longer required (replies to everything). Mirrors the
                            pin's filled/outline pattern — state lives in the
                            ICON, with the accent styling on
                            [data-header-summon-toggle='required'] on top. */}
                        {threadSummonSetting.requireMention !== false
                          ? <MentionSvgIcon />
                          : <MentionOffSvgIcon />}
                      </HeaderIconButton>
                    )}
                    {/*
                      Top-level "clear chat history" trash button.

                      History (Miles 2026-05-09):
                        - PR #68 removed this button entirely because
                          chip-level × on each ThreadSwitcherBar chip covered
                          most users and the global wipe was the #1 source of
                          accidental data loss.
                        - Follow-up rule: when there is only ONE thread, the
                          chip-level × archives the user's only thread
                          instead of giving them a clean "reset and start
                          fresh" path. So we re-introduce this button — but
                          ONLY when `threads.length <= 1` (covers cold-boot 0
                          + the single-thread steady state). With 2+ threads
                          it stays hidden, preserving PR #68's
                          accidental-wipe fix.
                        - aria-label / title localized EN/ES per PR #68's
                          bilingual fresh-state pattern.
                    */}
                    {showTopLevelClearButton && (
                      <HeaderIconButton
                        onClick={handleClearChat}
                        aria-label={clearChatAriaLabel}
                        title={clearChatTitle}
                        data-testid="global-clear-chat-button"
                      >
                        <TrashSvgIcon />
                      </HeaderIconButton>
                    )}
                  </>
                );
              })()}
              {/* Mode cycle button — desktop cycles default → docked →
                  fullscreen → default. On MOBILE there is no mode to cycle
                  (mobile UX round 2026-08-14): open IS the fullscreen
                  takeover, so the button renders on desktop only and hide
                  below is the one dismissal. */}
              {!isMobileViewport && !config.disableDocking && (
                <HeaderIconButton
                  onClick={handleCycleMode}
                  aria-label={
                    isChatFullScreen ? 'return to default view' :
                    isChatDockedRight ? 'enter full screen' :
                    'dock chat to right'
                  }
                  title={
                    isChatFullScreen ? 'default view' :
                    isChatDockedRight ? 'full screen' :
                    'dock right'
                  }
                >
                  {isChatFullScreen ? <ArrowDownSvgIcon /> :
                   isChatDockedRight ? <ExpandSvgIcon /> :
                   <ArrowRightSvgIcon />}
                </HeaderIconButton>
              )}
              {/* Hide button (always visible). On mobile it renders even if
                  a config turns it off: with the fullscreen takeover it is
                  the ONLY exit, and a takeover with no exit is a trap. */}
              {(config.showHideButton || isMobileViewport) && (
                <HeaderIconButton
                  onClick={handleHide}
                  aria-label="hide chat"
                  title="hide"
                >
                  <HideSvgIcon />
                </HeaderIconButton>
              )}
            </HeaderActions>
          </ChatHeader>
        )}

        <ThreadTagFilterBar
          enabled={threadTags.enabled}
          value={threadTags.filter}
          onChange={threadTags.setFilter}
          ownerOptions={Object.entries(threadMemberLabels).map(([id, label]) => ({ id, label }))}
        />

        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Floating button bar for "Show older" and "Latest" buttons - side by side */}
          <FloatingButtonBar>
            {/* Show Previous Messages Button - only visible when scrolled near top AND messages are hidden */}
            <FloatingPillButton
              $visible={hiddenCount > 0 && isNearTop}
              $variant="secondary"
              onClick={() => {
                // Capture scroll state BEFORE React re-render
                // useLayoutEffect will handle scroll position BEFORE paint
                // This prevents the "flash" of oldest messages before animation
                const el = chatMessagesRef.current;
                if (!el) return;

                pendingRevealRef.current = { scrollHeightBefore: el.scrollHeight };
                setIsRevealing(true);
                setShowAllMessages(true);
              }}
              aria-label="Show previous messages"
            >
              <ChevronUpSvgIcon />
              previous
            </FloatingPillButton>

            {/* Message-step bubbles (2026-08-12 scrolling round): walk the
                thread one message at a time in every mode. ↑ = previous
                message (repeat to keep walking back), ↓ = next message;
                stepping past the last message lands on latest. Edge bubbles
                dim instead of vanishing so the cluster stays put. */}
            <FloatingNavPair>
              <ThreadNavBubble
                $visible={threadNavVisible}
                disabled={isAtThreadTop}
                onClick={() => { wakeNavCluster(true); stepToPreviousMessage(); }}
                aria-label="go to previous message"
                title="previous message (Alt+↑)"
                data-testid="thread-nav-prev"
              >
                <ChevronUpSvgIcon />
              </ThreadNavBubble>
              <ThreadNavBubble
                $visible={threadNavVisible}
                disabled={isAtThreadBottom}
                onClick={() => { wakeNavCluster(true); stepToNextMessage(); }}
                aria-label="go to next message"
                title="next message (Alt+↓)"
                data-testid="thread-nav-next"
              >
                <ChevronDownSvgIcon />
              </ThreadNavBubble>
              {/* Orientation readout — appears while stepping, fades with the
                  controls. Aria-hidden: the buttons already announce
                  themselves, and a live counter would be chatter. */}
              <StepPositionBadge
                $visible={stepReadoutVisible}
                aria-hidden="true"
                data-testid="thread-step-position"
              >
                {stepPosition ? `${stepPosition.index} / ${stepPosition.total}` : ''}
              </StepPositionBadge>
            </FloatingNavPair>

            {/* Jump to Latest Button */}
            <FloatingPillButton
              $visible={showScrollToBottom}
              $variant={newMessageCount > 0 || hasUnseenActivity ? 'primary' : 'secondary'}
              onClick={() => scrollToBottom('smooth')}
              aria-label={
                newMessageCount > 0
                  ? `${newMessageCount} new message${newMessageCount > 1 ? 's' : ''}`
                  : hasUnseenActivity
                    ? 'new activity below'
                    : 'jump to latest messages'
              }
            >
              {newMessageCount > 0 ? (
                `${newMessageCount} new`
              ) : hasUnseenActivity ? (
                'new'
              ) : (
                'latest'
              )}
              <ChevronDownSvgIcon />
            </FloatingPillButton>
          </FloatingButtonBar>

          {/* Touch-only message stepper (2026-08 reading-comfort round). Same
              two actions as the desktop pair, moved to the thumb side and
              asleep until you scroll, so phones get message-by-message
              navigation without anything parked over the text. Only mounts
              once the thread is genuinely scrollable — on a short thread
              there is nothing to step through. */}
          <ThreadNavCluster $awake={navClusterAwakeEffective} data-testid="thread-nav-cluster">
            <StepPositionBadge
              $visible={stepReadoutVisible}
              aria-hidden="true"
              data-testid="thread-step-position-touch"
            >
              {stepPosition ? `${stepPosition.index} / ${stepPosition.total}` : ''}
            </StepPositionBadge>
            <ThreadNavBubble
              $visible={threadNavVisible}
              disabled={isAtThreadTop}
              onClick={() => { wakeNavCluster(true); stepToPreviousMessage(); }}
              aria-label="go to previous message"
              data-testid="thread-nav-prev-touch"
            >
              <ChevronUpSvgIcon />
            </ThreadNavBubble>
            <ThreadNavBubble
              $visible={threadNavVisible}
              disabled={isAtThreadBottom}
              onClick={() => { wakeNavCluster(true); stepToNextMessage(); }}
              aria-label="go to next message"
              data-testid="thread-nav-next-touch"
            >
              <ChevronDownSvgIcon />
            </ThreadNavBubble>
          </ThreadNavCluster>
          {(() => {
            const { olderElements, recentElements, pendingOverlay: extractedPendingOverlay } = renderedContent;
            // Exactly-one-indicator invariant (2026-08-12, turnIndicator.ts):
            // when the rail flag says this thread's turn is in flight but no
            // pending-shaped message survived into the overlay (a handler
            // retired its widget mid-await, or a hydrate/reattach restored
            // the flag without one), render the synthetic fallback widget —
            // never a spinning rail row over an empty pane. Computed OUTSIDE
            // the renderedContent memo on purpose: the memo does not re-run
            // on processing-flag flips.
            const pendingOverlay = extractedPendingOverlay
              ?? resolveSyntheticTurnIndicator({
                processing: !!threadStore.activeThreadId
                  && Boolean(state.processingByThread?.[threadStore.activeThreadId]),
                messages,
              });
            return (
              <>
                {/* The top reserve is keyed off THREAD SHAPE only (hidden history, or a
                    thread long enough to step through) — deliberately NOT off
                    showScrollToBottom. That pill flips the moment you scroll up to
                    read, and folding it in here re-ran padding-top 1.5rem→3.5rem,
                    shifting every message down ~32px under the reader's eyes at the
                    exact instant they started reading. Reserving on thread shape is
                    stable across scrolling, so the transcript never moves on its own
                    (2026-08 reading-comfort round). */}
                <ChatMessages
                  ref={chatMessagesRef}
                  $hasFloatingButtons={hiddenCount > 0 || threadNavVisible}
                  onCopy={handleTranscriptCopy}
                >
                <TranscriptContent ref={transcriptContentRef}>
                  {/* Returning-tab reliability (2026-09-01): an empty ACTIVE
                      pane during the server hydrate gets visible state —
                      shimmer while loading, an inline retry row on error —
                      instead of the old silent blank (the failure read as
                      "my conversation is gone"). Gated on an EMPTY pane only:
                      'loading' can flash on local-full switches, and a pane
                      with content needs neither (useChatThreads hydrate-status
                      contract). Inline UI, never a banner band. */}
                  {threadSwitcherEnabled
                    && messages.length === 0
                    && threadStore.activeThreadHydrateStatus === 'loading' && (
                      <TranscriptSkeleton aria-busy="true" data-testid="transcript-skeleton">
                        <TranscriptSkeletonBubble />
                        <TranscriptSkeletonBubble $user />
                        <TranscriptSkeletonBubble />
                        <TranscriptSkeletonNotice>picking up right where you left off…</TranscriptSkeletonNotice>
                      </TranscriptSkeleton>
                    )}
                  {threadSwitcherEnabled
                    && messages.length === 0
                    && threadStore.activeThreadHydrateStatus === 'error' && (
                      <TranscriptRetryRow data-testid="transcript-retry">
                        <span>couldn&apos;t load this conversation</span>
                        <TranscriptRetryButton
                          type="button"
                          onClick={() => threadStore.retryActiveThreadHydrate()}
                        >
                          try again
                        </TranscriptRetryButton>
                      </TranscriptRetryRow>
                    )}
                  {/* I4: "Load earlier messages" — pinned at the TRUE top of the
                      transcript when the server holds pages older than the
                      loaded window. ALWAYS reachable while local history is
                      collapsed (P0 thread-durability, 2026-09-02 — see
                      collapsedHistory.ts): the gate is a function of the
                      server paging state alone. A click from the collapsed
                      state expands local history first, then fetches, so the
                      user still walks local → server history in order. */}
                  {loadEarlierControlVisible({
                    threadSwitcherEnabled,
                    earlierMessagesAvailable: threadStore.earlierMessagesAvailable,
                  }) && (() => {
                      const copy = loadEarlierMessagesCopy(getUserUiLanguage());
                      return (
                        <LoadEarlierMessagesButton
                          type="button"
                          onClick={handleLoadEarlierMessages}
                          disabled={threadStore.isLoadingEarlierMessages}
                          $loading={threadStore.isLoadingEarlierMessages}
                          aria-label={copy.idle}
                          aria-busy={threadStore.isLoadingEarlierMessages}
                          data-testid="load-earlier-messages"
                        >
                          {threadStore.isLoadingEarlierMessages ? copy.loading : copy.idle}
                        </LoadEarlierMessagesButton>
                      );
                    })()}
                  {/* Older messages - revealed instantly, scroll animation provides motion */}
                  {olderElements.length > 0 && (
                    <AnimatedRevealContainer
                      ref={revealContainerRef}
                      $expanded={showAllMessages}
                      $isAnimating={isRevealing}
                    >
                      <div className="reveal-inner">
                        {olderElements}
                      </div>
                    </AnimatedRevealContainer>
                  )}

                  {/* Divider between older and recent messages */}
                  {olderElements.length > 0 && showAllMessages && (
                    <RevealDivider $visible={showAllMessages}>
                      <span>earlier messages</span>
                    </RevealDivider>
                  )}

                  {/* Recent messages (always visible) */}
                  {recentElements}
                  {/* F4-A: one-click starter chips under the empty-thread welcome
                      bubble. Hidden once the thread has a real user message
                      (same empty-state lifecycle as the welcome — F4-B).
                      Multi-tab parity (2026-08-09): the PERSISTED greeting's own
                      starters win over this tab's config — every tab shows the
                      same chips for the same thread the moment it shows the
                      same greeting, independent of its own config-load timing. */}
                  {(() => {
                    const welcomeMsg = state.messages.find(
                      (m) => m?.id === 'welcome' || m?.sourceTag === 'welcome',
                    );
                    // 2026-08-09 (owner directive): the account's PERSONALIZED
                    // suggestions — memory/document-grounded, refreshed by
                    // refreshGreetingSuggestions — are the chips whenever they
                    // exist. They previously rendered only as dead <li> bullets
                    // inside the greeting bubble while the clickable chips
                    // showed the static onboarding set, so the tailored
                    // suggestions were the ones the user could NOT click.
                    // Persisted-with-greeting starters stay the next rung
                    // (multi-tab parity), then the configured/in-code set.
                    //
                    // Mention prefix: in a thread that gates on @Prest0n,
                    // sending a suggestion verbatim posts to the group and gets
                    // NO reply — the chip must say (and send) what actually
                    // reaches Prest0n. Applied here, against the live gate, so
                    // the same suggestion stays clean in a 1:1.
                    // Capability grounding BEFORE the mention prefix (owner
                    // directive 2026-08-11): whichever source won, a chip that
                    // needs an unconnected app is rewritten into the
                    // "Connect my <App> — …" form (which fires the in-chat
                    // one-click connect card) or dropped — the product never
                    // recommends a message Prest0n can't act on. Unknown
                    // connection state (null) grounds nothing — fail open.
                    const groundOpts = { allowConnectChips };
                    const effectiveStarters = resolveSuggestionChips({
                      // The live set leads. `config.suggestedTasks` (the daily
                      // greeting-sweep pool) stays as the next rung down — it
                      // is the same class of content, just staler, and on this
                      // fleet it has never been populated at all.
                      personalized: groundSuggestionChips(
                        dynamicStarters.length > 0 ? dynamicStarters : config.suggestedTasks,
                        connectedAppSlugs,
                        groundOpts,
                      ),
                      persisted: groundSuggestionChips(welcomeMsg?.starters, connectedAppSlugs, groundOpts),
                      configured: groundSuggestionChips(config.welcomeStarters, connectedAppSlugs, groundOpts),
                      mentionRequired: suggestionsNeedMention({
                        gatesThisThread: threadSummonSetting.gatesThisThread,
                        requireMention: threadSummonSetting.requireMention,
                      }),
                    });
                    // Per-thread processing, not the global flag: state.isProcessing
                    // stays true while ANY thread has a turn in flight, so a
                    // background turn in another thread used to blank the chips
                    // on the empty thread the user was actually looking at
                    // (owner screenshot 2026-08-09 — a sibling thread was mid-turn).
                    const activeThreadBusy = threadStore.activeThreadId
                      ? Boolean(state.processingByThread?.[threadStore.activeThreadId])
                      : state.isProcessing;
                    return shouldShowStarterChips({
                      messages: state.messages,
                      starters: effectiveStarters,
                      isProcessing: activeThreadBusy,
                    }) ? (
                      <WelcomeStarterChips
                        starters={effectiveStarters}
                        disabled={activeThreadBusy}
                        onPick={handleSuggestionPick}
                      />
                    ) : null;
                  })()}
                  {/* Follow-up chips (owner directives 2026-08-11 + 2026-08-19 +
                      2026-08-23): the row shows after every landed message that
                      is not the viewer's own — Prest0n's turns AND teammates'
                      messages. The hook's gate (followupVisibility) suppresses
                      only empty threads (starter chips own those),
                      viewer-spoke-last moments, and error state. Fetch economy
                      (2026-08-24): the hook anchors its FETCH tail at the last
                      assistant turn, so a teammate message re-shows the held
                      chips without minting a new billable generation. Chips
                      stay CLICKABLE while a turn runs — a pick routes through
                      the send gate, which queues it visibly behind the
                      in-flight turn. Surviving suggestions get the same
                      render-time treatment as starters: capability grounding,
                      then the live @Prest0n prefix when this thread gates. */}
                  {(() => {
                    // Error state first (owner directive 2026-08-12): after an
                    // interrupted/failed turn the recommendation IS the retry —
                    // one deterministic chip that re-sends the user's original
                    // request (same action as the card's Regenerate button),
                    // never freshly generated topic chips off a broken tail.
                    const retryFollowup = followupRecsEnabled && !isInternalPrest0n && !activeThreadBusyForFollowups
                      ? resolveRetryFollowup(state.messages)
                      : null;
                    if (retryFollowup) {
                      return (
                        <ThreadFollowupChips
                          suggestions={['Try that again']}
                          disabled={activeThreadBusyForFollowups}
                          onPick={() => {
                            // Parity with the card's own Retry button: a
                            // send-failure bubble's user message already
                            // exists in the thread — never append a second
                            // copy (2026-08-12).
                            void sendProgrammaticMessage(
                              retryFollowup.regeneratePrompt,
                              retryFollowup.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG
                                ? { skipUserBubble: true }
                                : undefined,
                            );
                          }}
                        />
                      );
                    }
                    const followupChips = applyAssistantMentionToAll(
                      groundSuggestionChips(threadFollowups.suggestions, connectedAppSlugs, { allowConnectChips }),
                      suggestionsNeedMention({
                        gatesThisThread: threadSummonSetting.gatesThisThread,
                        requireMention: threadSummonSetting.requireMention,
                      }),
                    );
                    return followupChips.length > 0 ? (
                      <ThreadFollowupChips
                        suggestions={followupChips}
                        onPick={handleSuggestionPick}
                      />
                    ) : null;
                  })()}
                  {children}
                </TranscriptContent>
                </ChatMessages>

                {/*
                  Tool-owned pending widget — its OWN full-width row between
                  the messages area and the thread rail (phantom-chip fix,
                  Miles 2026-07-13). It previously rendered inside the action
                  rail as a flex sibling of the thread chips, where its pill
                  shape read as a phantom third thread.

                  The overlay is gated on the ACTIVE thread's own per-thread
                  flag (never the global bit — a background thread's turn
                  must not animate the active pane). The stop button behavior
                  is unchanged.

                  Uniform in-summary indicator (owner directive, Miles
                  2026-07-14 — supersedes the 2026-07-12 "pane-owns-active"
                  rail rule): the chip / sidebar row for EVERY processing
                  thread — active included — shows its own spinner + elapsed
                  clock, driven purely by `processingByThread`. This pane
                  overlay is content, not rail state; the two coexist by
                  design. The old rail-level fallback "working…" pill
                  (2026-05-07 fix, refined by MT-QA-BUG-009 awaiting-input
                  suppression) stays GONE.
                */}
                {paneOverlayVisible && pendingOverlay && (
                  <PendingOverlayRow>
                    <PendingOverlayContainer
                      aria-live="polite"
                      aria-busy="true"
                      role="status"
                      data-source-tag={pendingOverlay.sourceTag || 'pending'}
                      onClick={(e: React.MouseEvent) => {
                        const target = e.target as HTMLElement;
                        const button = target.closest('button');
                        if (button?.id === 'pending-widget-stop-btn') {
                          e.preventDefault();
                          e.stopPropagation();
                          // Flush queue + cancel in-flight (PR feat/same-thread-queue-while-streaming).
                          cancelActiveThread();
                        }
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: ensureLinksOpenInNewTab(
                            normalizeChatHtml(pendingOverlay.html || pendingOverlay.text || '')
                          ),
                        }}
                      />
                    </PendingOverlayContainer>
                    {/*
                      2026-08-14 owner directive (rev 2, screenshot repro —
                      supersedes the same-day "always show spend" note): a
                      HEALTHY balance renders NO balance figure in chat, so
                      the working row shows the spinner/timer alone. The live
                      wallet-balance chip appears ONLY while the balance is
                      LOW (0 < balance <= the auto top-up threshold — the
                      same chatLowBalanceNudge gate as the composer banner)
                      or OUT of PRO Work Units. The chip still self-gates
                      (signed-out / org-usage-restricted viewers render
                      nothing) and stays a React sibling so the pill's
                      HTML-string contract (pendingShimmer shape detection,
                      stop-button id) is untouched.
                    */}
                    {(chatLowBalanceNudge || chatOutOfWorkUnits) && <SpendChip />}
                  </PendingOverlayRow>
                )}

                {/*
                  Bottom action rail — thread tabs ONLY (2026-07-13): no
                  status pill ever shares this row with the chips again, so
                  nothing here can read as a phantom thread.
                */}
                {(() => {
                  // Docked density (owner feedback, 2026-08-01): under 2
                  // threads there is NOTHING to switch, so the docked strip
                  // hides entirely and the room goes back to the
                  // conversation (new-thread stays reachable via the header
                  // ⋯ menu). Pure rule: dockedDensity.shouldShowThreadStrip;
                  // every other mode keeps the strip exactly as before.
                  const stripAllowed = threadSwitcherEnabled && threadStore.ready
                    && (!isChatFullScreen || isMobileViewport)
                    && shouldShowThreadStrip({
                      docked: dockedPanelDensity,
                      threadCount: threadStore.threads.length,
                    });
                  // The archive toast must survive strip suppression — an
                  // archive in docked mode can be the very act that drops
                  // the count under 2, and its confirmation must still show.
                  if (dockedPanelDensity && !stripAllowed && !archiveToast) return null;
                  return (
                <ThreadSwitcherContainer $docked={dockedPanelDensity}>
                  <ThreadSwitcherActionRail>
                    {archiveToast && (
                      <ArchiveToastPill role="status" data-archive-toast="">
                        {archiveToast}
                      </ArchiveToastPill>
                    )}
                    {/* T3: the chip strip serves compact views; in desktop
                        fullscreen the sidebar replaces it (isMobileViewport
                        tracks the sidebar's own 820px breakpoint). */}
                    {stripAllowed && (
                      <ThreadSwitcherBar
                        threads={visibleThreads}
                        activeThreadId={threadStore.activeThreadId}
                        isActiveProcessing={state.isProcessing}
                        processingByThread={state.processingByThread}
                        turnStartedAtByThread={state.turnStartedAtByThread}
                        activeThreadMessageCount={activeThreadUserMessageCount}
                        onSwitch={(threadId) => {
                          flushCurrentDraftNow(threadStore.activeThreadId);
                          threadStore.switchThread(threadId);
                        }}
                        onNew={() => {
                          flushCurrentDraftNow(threadStore.activeThreadId);
                          return threadStore.newThread();
                        }}
                        onRequestDelete={handleRequestDeleteThread}
                        onDeleteEmptyThread={handleDeleteEmptyThread}
                        onTogglePin={threadStore.togglePinThread}
                        /* Round 3 (2026-08-01): group chips wear the
                           two-people glyph, from the same membership-derived
                           counts as the sidebar sections + header line. */
                        participantsByThread={sharedThreadParticipants}
                        /* Docked thread-list round (2026-08-24): the strip
                           reports its measured "more chips than fit" bit so
                           the docked ☰ trigger below appears exactly when
                           threads are hidden off the rail. */
                        onOverflowChange={setThreadStripOverflowing}
                      />
                    )}
                    {/* Thread-list trigger: host-side sibling —
                        ThreadSwitcherBar's own pinned DOM stays untouched.
                        MOBILE (2026-08-14): ≥2 VISIBLE threads — the same
                        tag-filtered list the sheet renders, so the trigger
                        can never open an empty sheet while a tag filter is
                        active (review 2026-08-14). DOCKED (2026-08-24): only
                        while the strip actually overflows — when every chip
                        fits there is nothing the vertical list adds. */}
                    {stripAllowed
                      && (isMobileViewport
                        ? visibleThreads.length >= 2
                        : (dockedPanelDensity && threadStripOverflowing)) && (
                      <ThreadSheetTrigger
                        type="button"
                        data-thread-sheet-trigger=""
                        aria-haspopup="dialog"
                        aria-expanded={threadSheetOpen}
                        aria-label="show all threads"
                        title="all threads"
                        onClick={(e) => {
                          // Docked panel form anchors on the trigger; the
                          // mobile sheet ignores the measurement.
                          const rect = e.currentTarget.getBoundingClientRect();
                          setThreadSheetAnchor({
                            right: Math.max(8, Math.round(window.innerWidth - rect.right)),
                            bottom: Math.max(8, Math.round(window.innerHeight - rect.top + 8)),
                          });
                          setThreadSheetOpen(true);
                        }}
                      >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                          <line x1="5" y1="4" x2="14" y2="4" />
                          <line x1="5" y1="8" x2="14" y2="8" />
                          <line x1="5" y1="12" x2="14" y2="12" />
                          <circle cx="2.4" cy="4" r="0.9" fill="currentColor" stroke="none" />
                          <circle cx="2.4" cy="8" r="0.9" fill="currentColor" stroke="none" />
                          <circle cx="2.4" cy="12" r="0.9" fill="currentColor" stroke="none" />
                        </svg>
                      </ThreadSheetTrigger>
                    )}
                  </ThreadSwitcherActionRail>
                </ThreadSwitcherContainer>
                  );
                })()}
                {/* The list itself — same data, same handlers as the strip
                    above (three renderers, one source of truth). Mobile
                    wears it as the bottom sheet; the docked pane wears it
                    as the anchored drop-up panel (2026-08-24). */}
                {(isMobileViewport || dockedPanelDensity) && threadSwitcherEnabled && threadStore.ready && (
                  <ThreadListSheet
                    variant={isMobileViewport ? 'sheet' : 'panel'}
                    anchor={threadSheetAnchor}
                    open={threadSheetOpen}
                    onClose={() => setThreadSheetOpen(false)}
                    threads={visibleThreads}
                    activeThreadId={threadStore.activeThreadId}
                    processingByThread={state.processingByThread}
                    turnStartedAtByThread={state.turnStartedAtByThread}
                    activeThreadMessageCount={activeThreadUserMessageCount}
                    participantsByThread={sharedThreadParticipants}
                    onSwitch={(threadId) => {
                      flushCurrentDraftNow(threadStore.activeThreadId);
                      threadStore.switchThread(threadId);
                    }}
                    onNew={() => {
                      flushCurrentDraftNow(threadStore.activeThreadId);
                      threadStore.newThread();
                    }}
                    onRequestDelete={handleRequestDeleteThread}
                    onDeleteEmptyThread={handleDeleteEmptyThread}
                    onTogglePin={threadStore.togglePinThread}
                  />
                )}
              </>
            );
          })()}
        </div>

        {/* Prompt bar inside chat footer */}
        {isChatOpen && (
          <ChatFooter>
            {/*
              Inline attachment preview strip (Fix 2, revised
              2026-05-11). Renders directly above the composer prompt
              bar as a horizontal chip rail showing real filename +
              size + image thumbnail (or mime icon) + remove (×) per
              attachment. Sits in normal document flow so it can never
              be hidden behind ancestor overflow:hidden, and visually
              overlays the threads band when one is present (z-index
              raised above the band but below modals). Renders
              live attachments AND re-attach placeholders so a user
              who refreshes never loses sight of their pending files.
            */}
            {(attachments.length > 0 || placeholderAttachments.length > 0) && (
              <AttachmentPreviewOverlay
                files={attachments}
                placeholders={placeholderAttachments}
                onRemoveFile={handleRemoveAttachment}
                onRemovePlaceholder={handleDismissPlaceholder}
                onPlaceholderClick={handleReattachPlaceholder}
              />
            )}
            {/*
              The "Choose a thread" multi-choice panel was REMOVED from this slot
              (owner directive 2026-07-31): thread navigation and disambiguation
              live exclusively in the left ThreadSidebar. Do not remount an
              ambient chooser in the conversation area. Merge note (2026-07-31):
              main independently softened the panel to an on-request "Choose
              reply thread" button in this same slot — that row is removed too,
              per the same directive; the duplicate-title signal lives on the
              sidebar rows ("similar name" hint) instead.
            */}
            {/*
              Add-people MODAL mount (P2c, flag-gated testing-only). Its trigger
              moved to the ChatHeader rail (2026-07-19 feedback); the panel is a
              fixed overlay so this tree position is cosmetic-safe.
            */}
            <ThreadAddPeoplePanel
              enabled={threadAddPeople.enabled}
              isOpen={threadAddPeople.isOpen}
              loading={threadAddPeople.loading}
              model={threadAddPeople.model}
              busyUserId={threadAddPeople.busyUserId}
              notice={threadAddPeople.notice}
              loadError={threadAddPeople.loadError}
              onRetry={threadAddPeople.retry}
              onInvite={() => {
                // In-place invite (owner directive 2026-08-13): open the Team
                // & Roles invite modal right here instead of navigating the
                // user away to settings mid-conversation.
                threadAddPeople.close();
                setInviteModal({ open: true });
              }}
              addRole={threadAddPeople.addRole}
              onRoleChange={threadAddPeople.setAddRole}
              onAdd={threadAddPeople.addPerson}
              onClose={threadAddPeople.close}
            />
            {renderPromptBarInChat()}
          </ChatFooter>
        )}
      </ChatContainer>

      {/* Floating Prompt Bar (Shown when chat is closed) */}
      {!isChatOpen && renderFloatingPromptBar()}

      {/* Processing tooltip - shown when user clicks send while processing */}
      <ProcessingTooltip
        visible={showProcessingTooltip}
        anchorRef={activeTooltipAnchorRef}
        onDismiss={dismissProcessingTooltip}
      />

      {/* In-chat member invite — full Team & Roles parity (owner directive
          2026-08-13). Opened by the add-people panel's "invite someone new"
          and by the invite-member global tool ("invite jane@acme.com").
          onSent refreshes the add-people roster so the new pending invite
          shows the moment the panel is next opened. */}
      <InviteModal
        isOpen={inviteModal.open}
        initialEmail={inviteModal.email}
        onClose={() => setInviteModal({ open: false })}
        onSent={() => {
          try { threadAddPeople.retry(); } catch { /* roster refresh is best-effort */ }
        }}
      />

      {/* Clear chat confirmation modal. On threading surfaces the header
          trash can is an ARCHIVE action (Miles 2026-07-12): conversations
          are archived — Prest0n can still reference them — never deleted,
          and a fresh thread starts immediately. Non-threading surfaces keep
          the legacy clear-history semantics/copy. */}
      <ConfirmationModal
        isOpen={showClearConfirmModal}
        onConfirm={confirmClearChatWithReset}
        onCancel={hideClearConfirmation}
        title={threadSwitcherEnabled ? 'Archive All Conversations' : 'Clear Chat History'}
        message={threadSwitcherEnabled
          ? 'This archives all your conversations and starts you fresh. Prest0n can still reference them if needed, but they will no longer appear in your threads.'
          : 'This will permanently delete all your chat messages. This action cannot be undone.'}
        confirmText={threadSwitcherEnabled ? 'Archive All' : 'Clear History'}
        cancelText="Cancel"
        isDangerous={!threadSwitcherEnabled}
      />

      {/*
        Thread-archive confirmation modal.

        Triggered by the × affordance inside a chip on the ThreadSwitcherBar.
        The × is hidden until hover/focus and never deletes directly — the
        user always lands here first. The thread's messages are preserved in
        `prest0_thread_archive__<instanceId>` so nothing is lost from general
        memory (future UI can restore archived threads).
      */}
      <ConfirmationModal
        isOpen={!!pendingDeleteThread}
        onConfirm={handleConfirmDeleteThread}
        onCancel={handleCancelDeleteThread}
        title="Archive this conversation?"
        message={
          pendingDeleteThread
            ? (
              // Shared threads archive FOR EVERYONE (owner directive
              // 2026-08-26) — the confirm must say so before the click, not
              // let the other members discover it.
              (sharedThreadParticipants[pendingDeleteThread.id] ?? 0) >= 2
                || pendingDeleteThread.sharedThread === true
                ? `“${pendingDeleteThread.title}” is a group thread — archiving closes it for everyone in it, on all their devices, and they'll see a note that you archived it. Prest0n keeps the conversation in memory and can still reference it, but nobody will be able to reopen or continue it.`
                : `“${pendingDeleteThread.title}” will leave your threads on all your devices. Prest0n keeps it in memory and can still reference it when helpful, but the conversation itself is closed — you won't be able to reopen or continue it.`
            )
            : ''
        }
        confirmText="Archive"
        cancelText="Keep"
        isDangerous={false}
        isInfo={true}
      />

      {/* Remote shared-archive announcement (owner directive 2026-08-26):
          the full-screen 5s hold when someone archives the thread this user
          is INSIDE. Passing notices (thread archived while elsewhere) ride
          the archive toast via the subscription effect above. */}
      <ThreadArchiveNoticeOverlay />

      {/* Critical Balance Popup - appears on interaction when the wallet is empty (<= $0) in production mode */}
      <CriticalBalancePopup
        isOpen={showCriticalBalancePopup}
        onClose={() => setShowCriticalBalancePopup(false)}
        hasPaymentMethod={paymentMethodStatus.hasPaymentMethod}
        isLoadingPaymentStatus={paymentMethodStatus.isLoading}
        onNavigate={handleTopUpRequest}
        currentBalanceUsd={lowBalanceState.balanceUsd}
      />
    </>
  );
};

export default GlobalChatSystem;
